import {subscribeTaskRefresh} from '../tasks/taskRefresh';
import {preserveTaskTimeOnDateChange,taskDateNZ} from '../tasks/taskDueDate';
import {JobTaskCompletion,oneOffJob} from '../tasks/jobTaskCompletion';
import {jobTaskRepository,jobTaskFeatureEnabled} from '../tasks/jobTaskRepository';
import {JobTaskFeedback} from '../tasks/JobTaskCheckbox';
import { mergeClientViews, compareClientLastViewed, formatClientViewNZ } from '../clientQuoteViews';
import {phaseStaffOnDay} from '../phases/scheduling';
import {phaseStaffingEnabled} from '../phases/phaseApi';
import { MediaVault } from '../components/MediaVault';
import { createMarketingVaultBackend } from '../marketingVaultSupabase';
import {equipmentConfirmationQueue} from '../equipmentConfirmationQueue';
import {EquipmentConfirmationList} from '../components/EquipmentConfirmationList';
import { PrivateImage } from '../components/PrivateAsset';
import { generateEquipment } from '../equipmentOperations';
import { calculationPatch, isAcceptedQuote, quoteItems } from '../quoteCalculations';
import { useAdminSession } from '../hooks/useAdminSession';
import { SessionRecoveryPanel } from '../components/SessionRecoveryPanel';
import { requestEditorNavigation } from '../editorDraft';
import { createQuoteRevision } from '../quoteRevision';
import { supersedeQuote } from '../quoteSuperseded';
import React, { useState, useEffect, useRef, useMemo, useCallback, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { normalizeStaffRole, STAFF_ROLE_OPTIONS } from '../staffRoles';
import { Lock, User, LayoutDashboard, CalendarIcon, Users, Menu, DollarSign, Settings, CheckCircle, XCircle, Clock, MapPin, FileText, Calendar, ChevronRight, Circle, Plus, Package, Edit3, Trash2, X, Trello, ChevronLeft, Briefcase, Truck, Eye, AlertCircle, ArrowUpRight, Image as ImageIcon, Mail, Phone, Building2, ShieldAlert, List as ListIcon, LayoutGrid, FolderOpen, Database, Search, ChevronDown, Loader2, Filter, UserPlus, Star, Repeat, Handshake } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { QuoteBuilder } from '../components/QuoteBuilder';
import { QuoteDraftRecovery } from '../components/QuoteDraftRecovery';
import { QuotePreview } from '../components/QuotePreview';
import { ProductModal } from '../components/ProductModal';
import { DashboardOverview } from '../components/DashboardOverview';
import { InquiryDetailsModal } from '../components/InquiryDetailsModal';
import { SubmissionTypeBadge } from '../components/SubmissionTypeBadge';
import { Quote, QuoteStatus, Product, PipelineItem } from '../types';
import { supabase } from '../supabaseClient';
import Companies from './Companies';
import { PreferredPartnerAdmin } from '../components/PreferredPartnerAdmin';
import { StaffReferralProgramme } from '../components/StaffReferralProgramme';
import { TaskControlScope, TaskControlWorkspace, isCancelledTask } from '../components/TaskControlWorkspace';
import { TaskDetailsModalShell } from '../components/TaskDetailsModalShell';
import { NotificationSettings } from '../components/NotificationSettings';

// ⚡ THE MASTER PIPELINE STAGES
const groupedVault = createMarketingVaultBackend(supabase);
const loadGroupedVault = () => groupedVault.loadEvents();

const UNIFIED_STAGES: Array<{id: string, label: string, type: string}> = [
  { id: 'INQUIRY', label: 'Inquiry', type: 'SALES' },
  { id: 'DISCOVERY', label: 'Discovery', type: 'SALES' },
  { id: 'PROPOSAL', label: 'Proposal', type: 'SALES' },
  { id: 'PRE_PRODUCTION', label: 'Pre-Production', type: 'PRODUCTION' },
  { id: 'STAGING', label: 'Staging', type: 'PRODUCTION' },
  { id: 'LIVE_EVENT', label: 'Live Event', type: 'PRODUCTION' },
  { id: 'POST_EVENT', label: 'Post-Event', type: 'PRODUCTION' },
  { id: 'COMPLETED', label: 'Completed', type: 'PRODUCTION' },
  { id: 'LOST', label: 'Lost', type: 'SALES' }
];

export const LicenseeLogin = () => {
    const session = useAdminSession();
    // A verified identity change must never reuse another Admin's in-memory editor.
    return <AdminWorkspace key={session.verifiedAuthId || 'unverified'} session={session} />;
};

const AdminWorkspace = ({ session }: { session: ReturnType<typeof useAdminSession> }) => {
    const { currentUser, verifiedAuthId, checking: checkingSession, recovery, accessDenied, retry, logout } = session;
    const isAuthenticated = !!currentUser && !checkingSession;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [activeTab, setActiveTab] = useState<string>('dashboard');
    const [isLoading, setIsLoading] = useState(false);
    const [viewState, setViewState] = useState('DASHBOARD');
    const [dbSearch, setDbSearch] = useState('');
    const [quoteSearch, setQuoteSearch] = useState('');
    const [quoteStatusFilter, setQuoteStatusFilter] = useState('ACTIVE');
    const [quoteSortOrder, setQuoteSortOrder] = useState('EVENT_DATE_ASC');
    const quoteReadSequence = useRef(0);
    const [clientViewHistoryUnavailable, setClientViewHistoryUnavailable] = useState(false);
    const [inventoryViewMode, setInventoryViewMode] = useState<'grid' | 'list'>('grid');
    const [quotes, setQuotes] = useState<any[]>([]);
    const [activeQuote, setActiveQuote] = useState<any | null>(null);
    const [crmItems, setCrmItems] = useState<PipelineItem[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [editingContact, setEditingContact] = useState<any>(null);
    const [dbFilter, setDbFilter] = useState('ALL');
    const [casualStaffList, setCasualStaffList] = useState<any[]>([]);
    const [isCasualEditorOpen, setIsCasualEditorOpen] = useState(false);
    const [newCasualForm, setNewCasualForm] = useState({ name: '', phone: '' });
    const [products, setProducts] = useState<any[]>([]);
    const pipelineScrollContainer = useRef<HTMLDivElement>(null);
    const [calendarView, setCalendarView] = useState<'month' | 'week' | 'day'>('month');
    const [calendarTaskMode, setCalendarTaskMode] = useState<'summary' | 'detailed'>('summary'); // ⚡ NEW: Defaults to clean summary mode!
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [isAddingClient, setIsAddingClient] = useState(false);
    const [newClient, setNewClient] = useState({ firstName: '', lastName: '', clientEmail: '', clientPhone: '', companyName: '', companyId: '' });
    const [isProductEditorOpen, setIsProductEditorOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<any>(null);
    const [isStaffEditorOpen, setIsStaffEditorOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState<any>(null);
    
    // ⚡ ADMIN CALENDAR STATE (For viewing staff availability)
    const [adminAvailabilities, setAdminAvailabilities] = useState<Record<string, any>>({});
    const [adminCalendarMonth, setAdminCalendarMonth] = useState(new Date());
    
    // ⚡ NEW: Admin Override State
    const [adminSelectedDate, setAdminSelectedDate] = useState<string | null>(null);
    const [adminAvailForm, setAdminAvailForm] = useState({ status: 'AVAILABLE', startTime: '12:00', endTime: '23:00' });

    // ⚡ NEW: Admin Override Save Engine
    const handleSaveAdminAvailability = async () => {
        if (!editingStaff || !adminSelectedDate) return;

        const payload: any = {
            staff_id: editingStaff.id,
            date: adminSelectedDate,
            status: adminAvailForm.status,
            start_time: adminAvailForm.status === 'PARTIAL' ? adminAvailForm.startTime || null : null,
            end_time: adminAvailForm.status === 'PARTIAL' ? adminAvailForm.endTime || null : null
        };

        try {
            const existing = adminAvailabilities[adminSelectedDate];
            let savedRecord = { ...payload };

            if (existing && existing.id) {
                const { error } = await supabase.from('staff_availability').update(payload).eq('id', existing.id);
                if (error) throw error;
                savedRecord.id = existing.id;
            } else {
                const { data, error } = await supabase.from('staff_availability').insert([payload]).select().single();
                if (error) throw error;
                if (data) savedRecord.id = data.id;
            }
            
            setAdminAvailabilities(prev => ({ ...prev, [adminSelectedDate]: savedRecord }));
            setAdminSelectedDate(null);
        } catch (err: any) {
            alert("Failed to override schedule: " + err.message);
        }
    };

    const fetchAdminAvailability = async (staffId: string) => {
        const { data, error } = await supabase.from('staff_availability').select('*').eq('staff_id', staffId);
        if (!error && data) {
            const availMap: Record<string, any> = {};
            data.forEach(item => {
                const cleanDate = item.date.split('T')[0];
                availMap[cleanDate] = item;
            });
            setAdminAvailabilities(availMap);
        }
    };
    const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
    const [equipmentFocusShiftId, setEquipmentFocusShiftId] = useState<string | undefined>();
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [assigningJob, setAssigningJob] = useState<PipelineItem | null>(null);
    const [tempAssignedIds, setTempAssignedIds] = useState<string[]>([]);
    const [leadFilter, setLeadFilter] = useState('ACTIVE');
    const [quoteListFilter, setQuoteListFilter] = useState<'ALL' | 'ACTION'>('ALL');
    const [editingTask, setEditingTask] = useState<any>(null);
    const [isGrouped, setIsGrouped] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [taskFilter, setTaskFilter] = useState<'ALL' | 'MINE'>('ALL');
    const [taskAssigneeFilter, setTaskAssigneeFilter] = useState<string>('ALL'); // ⚡ NEW: Specific staff filter
    const [showPipelineGraveyard, setShowPipelineGraveyard] = useState(false);
    const [isProcessingTask, setIsProcessingTask] = useState<string | null>(null); // ⚡ ADD THIS HERE
    
    // ⚡ NEW PROJECT ROUTER: Remembers which internal project's tasks we are isolating
    const [selectedProjectIdFilter, setSelectedProjectIdFilter] = useState<string>('ALL');

    // ⚡ AUTO-FILL SEARCH STATE
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    
    // Creates a clean, duplicate-free list of clients to search through
    const uniqueClientsForSearch = useMemo(() => {
        const map = new Map();
        crmItems.forEach((item: any) => {
            const key = `${item.firstName}-${item.lastName}-${item.email || item.clientEmail}`.toLowerCase();
            if (!map.has(key)) map.set(key, item);
        });
        return Array.from(map.values());
    }, [crmItems]);

    // ⚡ MANUAL INQUIRY ENTRY STATE (Upgraded with Company Memory)
    const [isAddingInquiry, setIsAddingInquiry] = useState(false);
    const [isCreatingCompany, setIsCreatingCompany] = useState(false); // Remembers if we are typing a new company
    const [customServiceInput, setCustomServiceInput] = useState(''); // ⚡ NEW: Holds custom typed services
    const [newInquiry, setNewInquiry] = useState({
        firstName: '', lastName: '', clientEmail: '', clientPhone: '', 
        eventName: '', eventDate: '', venue: '', location: '', notes: '',
        company_id: '', newCompanyName: '', // ⚡ NEW COMPANY FIELDS
        services: [] as string[]
    });

    // ⚡ SAVE MANUAL INQUIRY FUNCTION (Upgraded to fix the Company Sync Bug)
    const handleSaveManualInquiry = async () => {
      if (!newInquiry.firstName || !newInquiry.eventName || !newInquiry.clientEmail) {
          alert("Please provide at least a First Name, an Email, and an Event/Job Name.");
          return;
      }

      let finalCompanyId = newInquiry.company_id;
      let finalCompanyName = null; // ⚡ Added to track the name explicitly

      // 1. If they typed a new company, create it in the database first!
      if (isCreatingCompany && newInquiry.newCompanyName.trim() !== '') {
          const { data: newCompData, error: compError } = await supabase
              .from('companies')
              .insert([{ name: newInquiry.newCompanyName }])
              .select()
              .single();

          if (compError) {
              alert("Error creating new company: " + compError.message);
              return;
          }
          finalCompanyId = newCompData.id; 
          finalCompanyName = newCompData.name;
      } else if (newInquiry.company_id) {
          // Find the name of the existing company they selected
          const existingComp = companies.find(c => c.id === newInquiry.company_id);
          if (existingComp) finalCompanyName = existingComp.name;
      }

      // 2. Format services into a clean string
      const servicesString = newInquiry.services.length > 0 ? newInquiry.services.join(', ') : null;

      // 3. Save the Lead securely
      const { error } = await supabase.from('event_leads').insert([{
          first_name: newInquiry.firstName,
          last_name: newInquiry.lastName,
          email: newInquiry.clientEmail,
          phone: newInquiry.clientPhone,
          project_ref: newInquiry.eventName,
          event_date: newInquiry.eventDate || null,
          venue_location: newInquiry.venue,
          additional_details: newInquiry.notes,
          services_interested: servicesString,
          company_id: finalCompanyId || null, 
          company_name: finalCompanyName || null, // ⚡ Explicitly save the name so Quote Builder sees it!
          status: 'INQUIRY', // ⚡ Standardize initial stage
          lead_source: 'Manual Entry'
      }]);

      if (!error) {
          setIsAddingInquiry(false);
          setIsCreatingCompany(false);
          setNewInquiry({ firstName: '', lastName: '', clientEmail: '', clientPhone: '', eventName: '', eventDate: '', venue: '', location: '', notes: '', company_id: '', newCompanyName: '', services: [] });
          setCustomServiceInput(''); // ⚡ CLEARS THE CUSTOM INPUT BOX
          
          // ⚡ THE FIX: Fetch BOTH leads and companies so the state syncs perfectly!
          await fetchCompanies();
          if (typeof fetchLeads === 'function') await fetchLeads(); 
      } else {
          alert("Error saving inquiry: " + error.message);
      }
  };

    // ⚡ RESPONSIVE MANUAL INQUIRY MODAL
    const renderManualInquiryModal = () => {
      if (!isAddingInquiry) return null;

      return (
          <div className="fixed inset-0 z-[130] flex items-center justify-center overflow-hidden bg-slate-950/90 p-3 backdrop-blur-3xl md:p-4">
              <GlassCard className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden !p-0 border-brand-neonBlue/30 shadow-neon rounded-[32px] md:max-h-[calc(100dvh-2rem)] md:rounded-[40px] animate-fade-in-up">
                  <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/5 px-5 py-4 md:px-8 md:py-6">
                      <h3 className="type-heading flex min-w-0 items-center gap-3 text-xl text-white md:gap-4 md:text-3xl">
                          <FileText className="text-brand-neonBlue" size={28} /> Log Phone Inquiry
                      </h3>
                      <button aria-label="Close Log Phone Inquiry" onClick={() => { setIsAddingInquiry(false); setClientSearch(''); }} className="shrink-0 text-slate-500 hover:text-white transition-all"><X size={28}/></button>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 custom-scrollbar md:px-8 md:py-6">
                    <div className="space-y-6">
                      
                      {/* ⚡ NEW: AUTO-FILL FROM EXISTING CLIENTS */}
                      <div className="space-y-2 mb-6 pb-6 border-b border-white/5 relative">
                          <label className="type-label ml-1 flex items-center gap-2 text-brand-neonBlue">
                              <Search size={12} /> Auto-Fill from Database
                          </label>
                          <input 
                              className="w-full bg-brand-neonBlue/5 border border-brand-neonBlue/30 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all placeholder:text-slate-500 shadow-inner"
                              placeholder="Search past clients by name or email..."
                              value={clientSearch}
                              onChange={(e) => {
                                  setClientSearch(e.target.value);
                                  setShowClientDropdown(true);
                              }}
                              onFocus={() => setShowClientDropdown(true)}
                              onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                          />
                          {showClientDropdown && clientSearch && (
                              <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-brand-neonBlue/30 rounded-2xl shadow-2xl z-50 max-h-48 overflow-y-auto custom-scrollbar">
                                  {uniqueClientsForSearch
                                      .filter((c: any) => 
                                          `${c.firstName} ${c.lastName} ${c.email || c.clientEmail} ${c.companyName}`.toLowerCase().includes(clientSearch.toLowerCase())
                                      )
                                      .map((c: any, i: number) => (
                                          <div 
                                              key={i} 
                                              className="p-4 border-b border-white/5 last:border-0 hover:bg-brand-neonBlue hover:text-slate-900 cursor-pointer transition-colors group flex justify-between items-center"
                                              onClick={() => {
                                                  setNewInquiry(prev => ({
                                                      ...prev,
                                                      firstName: c.firstName || '',
                                                      lastName: c.lastName || '',
                                                      clientEmail: c.email || c.clientEmail || '',
                                                      clientPhone: c.phone || c.clientPhone || '',
                                                      company_id: c.company_id || c.companyId || ''
                                                  }));
                                                  setIsCreatingCompany(false);
                                                  setClientSearch('');
                                                  setShowClientDropdown(false);
                                              }}
                                          >
                                              <div>
                                                  <div className="font-bold group-hover:text-slate-900">{c.firstName} {c.lastName}</div>
                                                  <div className="text-xs text-slate-400 group-hover:text-slate-800 flex gap-2 mt-1">
                                                      <span>{c.email || c.clientEmail}</span>
                                                  </div>
                                              </div>
                                              {c.companyName && c.companyName !== 'Private Account' && (
                                                  <span className="type-label rounded bg-black/20 px-2 py-1 group-hover:bg-black/10">
                                                      {c.companyName}
                                                  </span>
                                              )}
                                          </div>
                                      ))
                                  }
                              </div>
                          )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-brand-neonBlue">First Name *</label>
                              <input className="w-full bg-slate-900/50 border border-brand-neonBlue/30 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.firstName} onChange={e => setNewInquiry({...newInquiry, firstName: e.target.value})} placeholder="e.g. Jane" autoFocus />
                          </div>
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-slate-500">Last Name</label>
                              <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.lastName} onChange={e => setNewInquiry({...newInquiry, lastName: e.target.value})} placeholder="e.g. Smith" />
                          </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-slate-500">Email</label>
                              <input type="email" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.clientEmail} onChange={e => setNewInquiry({...newInquiry, clientEmail: e.target.value})} placeholder="jane@example.com" />
                          </div>
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-slate-500">Phone</label>
                              <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.clientPhone} onChange={e => setNewInquiry({...newInquiry, clientPhone: e.target.value})} placeholder="021 555 1234" />
                          </div>
                      </div>

                      {/* COMPANY SELECTION / CREATION */}
                      <div className="space-y-2 pt-4 border-t border-white/5">
                            <div className="flex justify-between items-end">
                                <label className="type-label ml-1 text-slate-500">Company / Organization</label>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsCreatingCompany(!isCreatingCompany);
                                        // Clear the fields when swapping to avoid confusion
                                        setNewInquiry(prev => ({ ...prev, company_id: '', newCompanyName: '' }));
                                    }}
                                    className="type-button rounded-md bg-brand-neonBlue/10 px-2 py-2 text-xs text-brand-neonBlue transition-all hover:text-white"
                                >
                                    {isCreatingCompany ? "Choose Existing" : "+ Add New Company"}
                                </button>
                            </div>

                            {!isCreatingCompany ? (
                                <select 
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all appearance-none cursor-pointer"
                                    value={newInquiry.company_id}
                                    onChange={e => setNewInquiry({...newInquiry, company_id: e.target.value})}
                                >
                                    <option value="">-- No Company Attached --</option>
                                    {companies?.map((company: any) => (
                                        <option key={company.id} value={company.id}>{company.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input 
                                    className="w-full bg-brand-neonBlue/5 border border-brand-neonBlue/30 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all shadow-[0_0_15px_rgba(0,243,255,0.1)]"
                                    value={newInquiry.newCompanyName}
                                    onChange={e => setNewInquiry({...newInquiry, newCompanyName: e.target.value})}
                                    placeholder="Type new company name here..."
                                    autoFocus
                                />
                            )}
                        </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/5">
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-brand-neonBlue">Event/Job Name *</label>
                              <input className="w-full bg-slate-900/50 border border-brand-neonBlue/30 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.eventName} onChange={e => setNewInquiry({...newInquiry, eventName: e.target.value})} placeholder="e.g. Smith Wedding" />
                          </div>
                          <div className="space-y-2">
                              <label className="type-label ml-1 text-slate-500">Event Date</label>
                              <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all [color-scheme:dark]" value={newInquiry.eventDate} onChange={e => setNewInquiry({...newInquiry, eventDate: e.target.value})} />
                          </div>
                      </div>

                      {/* ⚡ UPGRADED: DYNAMIC INVENTORY & CUSTOM SERVICES */}
                      <div className="space-y-3 pt-2">
                          <label className="type-label ml-1 text-slate-500">Interested Services (Optional)</label>
                          
                          {/* 1. Custom Text Input */}
                          <div className="flex gap-2">
                              <input 
                                  type="text" 
                                  placeholder="Type custom item & press Enter..." 
                                  value={customServiceInput}
                                  onChange={(e) => setCustomServiceInput(e.target.value)}
                                  onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                          e.preventDefault();
                                          if (customServiceInput.trim()) {
                                              setNewInquiry(prev => ({ ...prev, services: [...prev.services, customServiceInput.trim()] }));
                                              setCustomServiceInput('');
                                          }
                                      }
                                  }}
                                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-brand-neonBlue transition-colors"
                              />
                              <Button 
                                  onClick={(e) => {
                                      e.preventDefault();
                                      if (customServiceInput.trim()) {
                                          setNewInquiry(prev => ({ ...prev, services: [...prev.services, customServiceInput.trim()] }));
                                          setCustomServiceInput('');
                                      }
                                  }}
                                  className="bg-white/10 hover:bg-brand-neonBlue hover:text-slate-900 text-white rounded-xl px-4 transition-colors border-none"
                              >
                                  <Plus size={16} />
                              </Button>
                          </div>

                          {/* 2. Scrollable Selection Area */}
                          <div className="flex flex-wrap gap-2 max-h-[160px] overflow-y-auto custom-scrollbar p-1">
                              
                              {/* Render Actual Products */}
                              {products.map(product => {
                                  const isSelected = newInquiry.services.includes(product.id);
                                  return (
                                      <button
                                          key={product.id}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              setNewInquiry(prev => ({
                                                  ...prev,
                                                  services: isSelected 
                                                      ? prev.services.filter(s => s !== product.id)
                                                      : [...prev.services, product.id]
                                              }))
                                          }}
                                          className={`type-button min-h-11 rounded-full border px-4 py-2 text-xs transition-all duration-200 ${
                                              isSelected 
                                                  ? 'bg-brand-neonBlue text-slate-950 border-brand-neonBlue shadow-[0_0_15px_rgba(0,243,255,0.4)] hover:shadow-[0_0_25px_rgba(0,243,255,0.6)]' 
                                                  : 'bg-slate-900 border-white/10 text-slate-400 hover:text-white hover:border-brand-neonBlue/50'
                                          }`}
                                      >
                                          {product.name}
                                      </button>
                                  )
                              })}

                              {/* Render Custom Added Services */}
                              {newInquiry.services.filter(s => !products.some(p => p.id === s)).map(customService => (
                                   <button
                                      key={customService}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          setNewInquiry(prev => ({
                                              ...prev,
                                              services: prev.services.filter(s => s !== customService)
                                          }))
                                      }}
                                      className="type-button group flex min-h-11 items-center gap-2 rounded-full border border-brand-neonBlue bg-brand-neonBlue px-4 py-2 text-xs text-slate-950 shadow-[0_0_15px_rgba(0,243,255,0.4)] transition-all duration-200 hover:shadow-[0_0_25px_rgba(0,243,255,0.6)]"
                                      title="Click to remove"
                                   >
                                      {customService} <X size={12} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                                   </button>
                              ))}
                          </div>
                      </div>

                      <div className="space-y-2">
                          <label className="type-label ml-1 text-slate-500">Venue / Location</label>
                          <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all" value={newInquiry.venue} onChange={e => setNewInquiry({...newInquiry, venue: e.target.value})} placeholder="Where is this happening?" />
                      </div>

                      <div className="space-y-2">
                          <label className="type-label ml-1 text-slate-500">Initial Notes / Requirements</label>
                          <textarea className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:border-brand-neonBlue outline-none transition-all min-h-[80px]" value={newInquiry.notes} onChange={e => setNewInquiry({...newInquiry, notes: e.target.value})} placeholder="Any extra details..." />
                      </div>

                    </div>
                  </div>

                      <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-white/10 bg-slate-950/80 px-5 py-4 md:flex-row md:gap-4 md:px-8 md:py-5">
                          <Button variant="outline" className="w-full md:flex-1 py-5 rounded-2xl" onClick={(e) => { e.preventDefault(); setIsAddingInquiry(false); setClientSearch(''); }}>Cancel</Button>
                          <Button className="w-full rounded-2xl border-none bg-brand-neonBlue py-5 text-slate-950 shadow-[0_0_20px_rgba(0,243,255,0.3)] hover:shadow-[0_0_30px_rgba(0,243,255,0.5)] md:flex-1" onClick={(e) => { e.preventDefault(); handleSaveManualInquiry(); setClientSearch(''); }}>Log Inquiry</Button>
                      </div>
              </GlassCard>
          </div>
      );
    };

    const toggleGroup = (groupName: string) => {
      setExpandedGroups(prev => prev.includes(groupName) 
          ? prev.filter(g => g !== groupName) 
          : [...prev, groupName]
      );
  };
    
    // ⚡ NEW: Shift Data State for the Action Required Filter
    const [allShifts, setAllShifts] = useState<any[]>([]);
    // ⚡ NEW: Global Tasks State
    const [globalTasks, setGlobalTasks] = useState<any[]>([]);
    const taskCompletion = useMemo(()=>new JobTaskCompletion(jobTaskRepository,row=>setGlobalTasks(old=>old.map(t=>t.id===row.id?{...t,...row,...(t.assignee_id!==row.assignee_id?{assignee:undefined}:{})}:t)),jobTaskFeatureEnabled()),[]);
    useSyncExternalStore(taskCompletion.subscribe,taskCompletion.snapshot);
    useEffect(()=>{taskCompletion.clear();},[currentUser?.id,taskCompletion]);
    const taskEditSnapshot=useRef<{task:any;revision:number|undefined}|null>(null);
    const openTaskEditor=(task:any)=>{taskEditSnapshot.current={task:{...task},revision:taskCompletion.meta(task.id)?.revision};setEditingTask({...task});};

    const [managementTasksLoading,setManagementTasksLoading]=useState(true);
    const [managementTasksError,setManagementTasksError]=useState('');
    const [isAddingGlobalTask, setIsAddingGlobalTask] = useState(false);

    // ⚡ ADMIN ALARM ENGINE STATE
    const [activeAdminAlarm, setActiveAdminAlarm] = useState<any>(null);

    // ⚡ ADMIN BACKGROUND ALARM SCANNER
    useEffect(() => {
        if (!isAuthenticated || !currentUser) return;
        
        const timer = setInterval(() => {
            const now = new Date();
            const triggeredTask = globalTasks.find(t => 
                t.status !== 'COMPLETED' && 
                t.is_urgent && 
                t.due_date && 
                new Date(t.due_date) <= now &&
                (t.assignee_id === currentUser.id || !t.assignee_id) // ⚡ FIX: Only triggers for MY tasks or Unassigned tasks!
            );
            
            if (triggeredTask && (!activeAdminAlarm || activeAdminAlarm.id !== triggeredTask.id)) {
                setActiveAdminAlarm(triggeredTask);
            }
        }, 10000); // Scans the clock every 10 seconds

        return () => clearInterval(timer);
    }, [globalTasks, activeAdminAlarm, isAuthenticated, currentUser]);

    // ⚡ NEW: INTERNAL PROJECTS STATE
    const [internalProjects, setInternalProjects] = useState<any[]>([]);
    const [isAddingProject, setIsAddingProject] = useState(false);
    const [newProject, setNewProject] = useState({ title: '', description: '', deadline: '', status: 'ACTIVE' });

    const fetchLeads = async () => {
      try {
        // ⚡ FIX: Grab the products dictionary directly from the DB so we can translate IDs immediately!
        let dictionary = products;
        if (dictionary.length === 0) {
            const { data: pData } = await supabase.from('products').select('id, name');
            if (pData) dictionary = pData;
        }

        const { data, error } = await supabase
          .from('event_leads')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
  
        const mappedItems = (data || []).map(item => {
          const isProfileOnly = !(item.project_ref || item.event_type || item.additional_details || item.venue_location);
  
          return {
            ...item,
            id: item.id,
            eventName: item.event_name || item.project_ref || (isProfileOnly ? 'Personnel Profile' : 'Unnamed Event'),
            firstName: item.first_name || 'Guest',
            lastName: item.last_name || '',
            
            companyName: (() => {
                const linkedCompany = companies.find(c => c.id === item.company_id || c.id === item.companyId);
                if (linkedCompany) return linkedCompany.name;
                return item.company_name || item.company || 'Private Account';
            })(),
            email: item.email || '',
            phone: item.phone || '',
            eventType: item.event_type || 'Corporate Event',
            eventDate: item.event_date || 'TBD',
            eventVenue: item.venue_location || item.event_venue || item.venue || 'TBD',
            duration: item.duration_hrs || item.duration || '',
            
            created_at: item.created_at || new Date().toISOString(),
            createdAt: item.created_at || new Date().toISOString(), 
            date: item.created_at || new Date().toISOString(),      
            
            value: Number(item.value || 0),
            amount: Number(item.amount || item.value || 0),
            revenue: Number(item.value || 0),                      
            
            type: isProfileOnly ? 'CONTACT' : 'SALES', 
            
            ...(() => {
              if (isProfileOnly) {
                return { status: 'PROFILE', salesStage: 'PROFILE', productionStage: 'PROFILE' };
              }
              
              const raw = String(item.status || '').trim().toUpperCase();
              let safeStatus = 'INQUIRY';
              
              if (['INQUIRY'].includes(raw)) safeStatus = 'INQUIRY';
              if (['DISCOVERY'].includes(raw)) safeStatus = 'DISCOVERY';
              if (['PROPOSAL', 'SENT'].includes(raw)) safeStatus = 'PROPOSAL';
              if (['PRE_PRODUCTION', 'APPROVED'].includes(raw)) safeStatus = 'PRE_PRODUCTION';
              if (['STAGING', 'ONBOARDING'].includes(raw)) safeStatus = 'STAGING';
              if (['LIVE', 'LIVE_EVENT'].includes(raw)) safeStatus = 'LIVE_EVENT';
              if (['POST_EVENT'].includes(raw)) safeStatus = 'POST_EVENT';
              if (['COMPLETE', 'COMPLETED', 'WRAP_UP'].includes(raw)) safeStatus = 'COMPLETED';
              if (['LOST', 'REJECTED'].includes(raw)) safeStatus = 'LOST';
              if (['WITHDRAWN', 'ARCHIVED'].includes(raw)) safeStatus = 'WITHDRAWN'; // ⚡ FIX 1: Teaches the app how to read Withdrawn!
  
              return { status: safeStatus, salesStage: safeStatus, productionStage: safeStatus };
            })(),
            
            interestedProducts: (() => {
               const raw = item.services_interested || item.services_interested_in || "";
               if (!raw) return "None selected";
               if (Array.isArray(raw)) {
                 return raw.map(val => dictionary.find(p => p.id === String(val).trim())?.name || String(val)).join(', ');
               }
               if (typeof raw === 'string') {
                 return raw.split(',').map(val => dictionary.find(p => p.id === val.trim())?.name || val.trim()).join(', ');
               }
               return String(raw);
            })(),
            
            clientMessage: item.additional_details || item.notes || item.message || 'No additional details provided.'
          };
        });
  
        const today = new Date();
        today.setHours(0, 0, 0, 0); 
        
        const processedLeads = mappedItems.map((lead: any) => {
            if (!lead.eventDate || lead.eventDate === 'TBD' || lead.status === 'COMPLETED' || lead.status === 'LOST') return lead;
            
          let safeDateStr = lead.eventDate;
          if (safeDateStr && safeDateStr.includes('/')) {
              const parts = safeDateStr.split('/');
              if (parts.length === 3) safeDateStr = `${parts[2]}-${parts[1]}-${parts[0]}`; 
          }
          const eDate = new Date(safeDateStr);
          if (isNaN(eDate.getTime())) return lead;
            eDate.setHours(0, 0, 0, 0);
            
            let newStatus = lead.status;
            
            if (eDate.getTime() === today.getTime() && ['STAGING', 'PRE_PRODUCTION'].includes(lead.status)) {
                newStatus = 'LIVE_EVENT';
            } 
            else if (eDate.getTime() < today.getTime() && ['STAGING', 'PRE_PRODUCTION', 'LIVE_EVENT'].includes(lead.status)) {
                newStatus = 'POST_EVENT';
            }
            
            if (newStatus !== lead.status) {
                supabase.from('event_leads').update({ status: newStatus }).eq('id', lead.id).then();
                return { ...lead, status: newStatus, salesStage: newStatus, productionStage: newStatus };
            }
            
            return lead;
        });
  
        setCrmItems(processedLeads);
        setIsLoading(false);
      } catch (err) {
        console.error("Database Fetch Error:", err);
        setIsLoading(false);
      }
    };
  
    const fetchStaff = async () => {
      try {
        const { data, error } = await supabase.from('staff_members').select('*').order('full_name');
        if (error) throw error;
        setStaffList(data);
      } catch (err) { console.error("Error fetching staff:", err); }
    };
    const fetchCasualStaff = async () => {
      try {
        const { data, error } = await supabase.from('casual_staff').select('*').order('name');
        if (error) throw error;
        setCasualStaffList(data);
      } catch (err) { console.error("Error fetching casuals:", err); }
    };
    
    const handleDeleteCasual = async (id: string) => {
      if (window.confirm('Remove this casual from your database? They will no longer appear in your dropdowns.')) {
        try {
          await supabase.from('casual_staff').delete().eq('id', id);
          await fetchCasualStaff();
        } catch (e: any) { alert(`Error: ${e.message}`); }
      }
    };

    const handleSaveCasual = async () => {
      if (!newCasualForm.name.trim()) return alert("Name is required.");
      try {
        const { error } = await supabase.from('casual_staff').insert([{ 
            name: newCasualForm.name.trim(), 
            phone: newCasualForm.phone.trim() 
        }]);
        if (error) throw error;
        
        await fetchCasualStaff(); 
        setIsCasualEditorOpen(false); 
        setNewCasualForm({ name: '', phone: '' }); 
      } catch (err: any) { alert('Failed to add casual: ' + err.message); }
    };
  
    const fetchProducts = async () => {
      try {
        const { data, error } = await supabase.from('products').select('*').order('name');
        if (error) throw error;
        setProducts(data);
      } catch (err) { console.error("Error fetching products:", err); }
    };

    const fetchShifts = useCallback(async () => {
        try {
            const {data,error}=phaseStaffingEnabled()?await supabase.rpc('admin_staffing_shift_index'):await supabase.from('event_shifts').select('*');
            if(error)throw error;
            if(data)setAllShifts(data);
        } catch (err) {if(phaseStaffingEnabled())setAllShifts([]);console.error('Staffing index could not be verified');}
    },[]);


    
    useEffect(()=>{if(!phaseStaffingEnabled()||!isAuthenticated)return;const timer=window.setInterval(fetchShifts,15000);return()=>window.clearInterval(timer);},[isAuthenticated,fetchShifts]);

    // ⚡ FETCH ALL GLOBAL TASKS (Upgraded to pull Internal Project names!)
    const taskRefreshEpoch = useRef(0);
    const fetchGlobalTasks = async () => {
      const epoch=++taskRefreshEpoch.current;
      setManagementTasksLoading(true);
      try {
          const { data,error } = await supabase.from('tasks')
              .select('*, assignee:staff_members(full_name), lead:event_leads(first_name, last_name, project_ref, event_date), project:internal_projects(title)')
              .order('due_date', { ascending: true, nullsFirst: false });
          if(error)throw error;
          const rows=data?await taskCompletion.hydrateRows(data):null;
          if(epoch!==taskRefreshEpoch.current)return;
          if(rows)setGlobalTasks(rows);
          setManagementTasksError('');
      } catch (err) {if(epoch===taskRefreshEpoch.current){setManagementTasksError('Task list unavailable');console.error(err);}}
      finally {if(epoch===taskRefreshEpoch.current)setManagementTasksLoading(false);}
    };

    // Reuse the task reader on entry and browser return; no polling or new delivery channel.
    const refreshTasksRef=useRef(fetchGlobalTasks);
    refreshTasksRef.current=fetchGlobalTasks;
    useEffect(()=>{
      if(!isAuthenticated)return;
      const dispose=subscribeTaskRefresh(()=>void refreshTasksRef.current());
      return()=>{taskRefreshEpoch.current++;dispose();};
    },[isAuthenticated,currentUser?.id]);
    useEffect(()=>{
      if(isAuthenticated&&(activeTab==='tasks'||isDetailsModalOpen))void refreshTasksRef.current();
    },[isAuthenticated,activeTab,isDetailsModalOpen,selectedInquiry?.id]);

    // ⚡ FETCH INTERNAL PROJECTS
    const fetchProjects = async () => {
        try {
            const { data } = await supabase.from('internal_projects').select('*').order('created_at', { ascending: false });
            if (data) setInternalProjects(data);
        } catch (err) { console.error(err); }
    };
  
  const formatQuoteFromDB = (dbQuote: any) => {
    const getNum = (val: any, defaultVal: number) => {
      if (val === null || val === undefined || val === '') return defaultVal;
      const n = Number(val);
      return isNaN(n) ? defaultVal : n;
    };

    return {
      ...dbQuote,
      id: dbQuote.id,
      title: dbQuote.title || 'Untitled Proposal',
      status: dbQuote.status || 'DRAFT',
      linkedCrmItemId: dbQuote.lead_id || null,
      eventDate: dbQuote.event_date || null,
      eventLocation: dbQuote.event_location || dbQuote.venue_location || null,

      eventStartTime: dbQuote.event_start_time || null,
      eventEndTime: dbQuote.event_end_time || null,
      setupTime: dbQuote.setup_time || null,
      packDownTime: dbQuote.pack_down_time || null,

      client: {
        firstName: dbQuote.client_first_name || '',
        lastName: dbQuote.client_last_name || '',
        email: dbQuote.client_email || '',
        phone: dbQuote.client_phone || '',
        company: dbQuote.client_company || '',
        accountsEmail: dbQuote.accounts_email || '' // ⚡ Added this line!
      },

      // ⚡ STRICT TRANSLATOR: Prioritize flat database columns, and pull the new Roadshow text!
      logistics: (() => {
        let parsed: any = dbQuote.logistics || {};
        if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch (e) { parsed = {}; }
        }
        
        // Trust the flat columns FIRST, fallback to JSON only if necessary
        const getVal = (dbVal: any, jsonVal: any) => {
            if (dbVal !== undefined && dbVal !== null && dbVal !== '') return Number(dbVal);
            if (jsonVal !== undefined && jsonVal !== null && jsonVal !== '') return Number(jsonVal);
            return 0;
        };

        return {
            quoteStaffing: parsed.quoteStaffing,
            hours: 0,
            hourlyRate: 0,
            travelDistanceKm: getVal(dbQuote.travel_distance_km, parsed.travelDistanceKm),
            travelRatePerKm: getVal(dbQuote.travel_rate_per_km, parsed.travelRatePerKm),
            setupFee: getVal(dbQuote.setup_fee, parsed.setupFee),
            // ⚡ NEW: Tell the reader to extract the saved roadshow schedule from the database!
            roadshowSchedule: parsed.roadshowSchedule || ''
        };
      })(),

      subtotal: Number(dbQuote.subtotal) || 0,
      tax: Number(dbQuote.gst_amount) || 0,
      total: Number(dbQuote.total_amount) || Number(dbQuote.grand_total) || 0,
      viewCount: dbQuote.view_count || 0,
      lastViewedAt: dbQuote.last_viewed_at || null,
      items: quoteItems(dbQuote.items),
      presentationSlideContent: dbQuote.presentation_slide_content?.text || (typeof dbQuote.presentation_slide_content === 'string' ? dbQuote.presentation_slide_content : ''),
      packingList: dbQuote.packing_list || [],
      custom_slides: dbQuote.custom_slides || [],
      customSlides: dbQuote.custom_slides || []
    };
  };
  const fetchQuotes = async () => {
    const sequence = ++quoteReadSequence.current;
    try {
      const { data, error } = await supabase.from('quotes').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      const { data: viewStats, error: viewError } = await supabase.rpc('admin_client_quote_view_stats');
      if (sequence === quoteReadSequence.current) {
        setClientViewHistoryUnavailable(!!viewError);
        setQuotes(mergeClientViews((data || []).map(formatQuoteFromDB), viewError ? [] : viewStats || []));
      }
    } catch (err) { 
      console.error("Error fetching quotes:", err); 
    }
  };
  
    useEffect(() => {
    if (!isAuthenticated || activeTab !== 'quotes') return;
    const refresh = () => { if (document.visibilityState === 'visible') void fetchQuotes(); };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  // fetchQuotes uses the latest-request guard; these are read-only refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  const fetchCompanies = async () => {
      try {
        const { data, error } = await supabase.from('companies').select('*').order('name');
        if (error) throw error;
        setCompanies(data);
      } catch (err) { console.error("Error fetching companies:", err); }
    };
  
    useEffect(() => {
      if (!isAuthenticated) return;
      fetchLeads();
      fetchCompanies();
      fetchQuotes();
      fetchStaff();
      fetchProducts();
      fetchCasualStaff();
      fetchShifts();
      fetchGlobalTasks();
      fetchProjects();

  
      const channel = supabase
        .channel('schema-db-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'event_leads' }, () => {
          fetchLeads();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'quotes' }, () => {
          fetchQuotes();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'event_shifts' }, () => {
          fetchShifts();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
          fetchGlobalTasks();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'internal_projects' }, () => {
          fetchProjects();
        })
        // ⚡ NEW LISTENERS BELOW THIS LINE ⚡
        .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
          fetchCompanies();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
          fetchProducts();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_members' }, () => {
          fetchStaff();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'casual_staff' }, () => {
          fetchCasualStaff();
        })
        .subscribe((status) => {
          console.log("🔌 Realtime Connection Status:", status);
        });
  
      return () => {
        supabase.removeChannel(channel);
      };
    }, [isAuthenticated, fetchShifts]);

      const handleSaveManualClient = async () => {
        if (!newClient.firstName || !newClient.clientEmail) { 
            alert("First Name and Email are required."); 
            return; 
        }

        try {
          let finalCompanyId = newClient.companyId;
          let finalCompanyName = newClient.companyName;

          if (newClient.companyId === 'NEW' && newClient.companyName) {
            const { data: companyData, error: companyError } = await supabase
              .from('companies')
              .insert([{ name: newClient.companyName }])
              .select()
              .single();

            if (companyError) throw new Error(`Company Rego Failed: ${companyError.message}`);
            
            finalCompanyId = companyData.id;
            finalCompanyName = companyData.name;
            
            await fetchCompanies();
          }

          const payload = { 
            first_name: newClient.firstName, 
            last_name: newClient.lastName, 
            email: newClient.clientEmail, 
            phone: newClient.clientPhone, 
            company_name: finalCompanyName,
            company_id: finalCompanyId !== 'NEW' ? finalCompanyId : null,
            project_ref: `New Inquiry: ${newClient.firstName} ${newClient.lastName}`
        };

          const { error: leadError } = await supabase.from('event_leads').insert([payload]);
          if (leadError) throw leadError;

          await fetchLeads();
          setIsAddingClient(false);
          setNewClient({ firstName: '', lastName: '', clientEmail: '', clientPhone: '', companyName: '', companyId: '' });
          
          alert("Record and Company initialized successfully!");
        } catch (e: any) { 
          alert(`Error: ${e.message}`); 
        }
      };
    
      const handleUpdateQuoteStatus = async (quoteId: string, leadId: string | undefined, newStatus: QuoteStatus) => {
        try {
          if (newStatus === 'SUPERSEDED') {
            await supersedeQuote(quoteId);
            await fetchQuotes();
            return;
          }
          let updatePayload: any = { status: newStatus };

          // ⚡ NEW: THE BLUEPRINT ENGINE FOR QUICK PROFILE APPROVALS
          if (newStatus === 'APPROVED') {
              const { data: quote, error: readError } = await supabase.from('quotes').select('*').eq('id', quoteId).single();
              if (readError || !quote) throw new Error('Unable to verify the saved Quote before approval.');
              if (isAcceptedQuote(quote)) { await Promise.all([fetchQuotes(), fetchLeads()]); return; }
              Object.assign(updatePayload, calculationPatch(quote));
              
              if (quote) {
                  const tier1Logistics = [
                    { id: 'contact', label: 'On-the-Night Contact Person', type: 'text', required: true, helpText: 'Who is our team\'s main point of contact during the event? (Name & Mobile Number)', section: 'Event Logistics' },
                    { id: 'access', label: 'Venue Access & Pack-in Time', type: 'text', required: true, helpText: 'What is the earliest time our crew can access the room to begin setting up?', section: 'Event Logistics' },
                    { id: 'runsheet', label: 'Event Run Sheet', type: 'upload', required: false, helpText: 'Please upload your official run sheet or timeline so our team is synced with your schedule.', section: 'Event Logistics' },
                    { id: 'floorplan', label: 'Venue Floor Plan / Placement', type: 'upload', required: false, helpText: 'Upload a floor plan or briefly describe exactly where you want the installation located in the room.', section: 'Event Logistics' },
                    { id: 'parking', label: 'Loading Dock & Parking Instructions', type: 'textarea', required: false, helpText: 'Are there specific loading bays, height restrictions, or parking passes our crew needs to know about?', section: 'Event Logistics' },
                    { id: 'power', label: 'Power & Wi-Fi Availability', type: 'select', options: 'Dedicated Power & Wi-Fi available, Power only (Bring 4G/5G internet), Neither (Fully off-grid needed)', required: true, section: 'Event Logistics' },
                    { id: 'wifi_details', label: 'Wi-Fi Network & Password', type: 'text', required: false, helpText: 'If dedicated venue Wi-Fi is available, please provide the login details here.', section: 'Event Logistics' },
                    { id: 'meals', label: 'Contractor Meals', type: 'select', options: 'Yes - Meals provided, No - Crew to sort own meals', required: true, helpText: 'For events spanning over 4 hours (including setup/packdown), it is industry standard to provide a vendor meal.', section: 'Event Logistics' },
                    { id: 'notes', label: 'Additional Production Notes', type: 'textarea', required: false, helpText: 'Anything else our crew should know before arriving?', section: 'Event Logistics' }
                  ];

                  const tier2Sharing = [
                    { id: 'sharing_method', label: 'Preferred Sharing Method', type: 'select', options: 'Email & SMS, Email Only, SMS Only, QR Code Only, AirDrop Only (Apple devices)', required: true, section: 'Digital Sharing' },
                    { id: 'email_from_name', label: 'Email From Name', type: 'text', required: false, helpText: 'e.g., ABC Company or ASB Gala', section: 'Digital Sharing' },
                    { id: 'email_reply_to', label: 'Reply Email Address', type: 'text', required: false, helpText: 'Where should replies go? e.g., events@abccompany.com', section: 'Digital Sharing' },
                    { id: 'email_subject', label: 'Email Subject Line', type: 'text', required: false, helpText: 'Keep it punchy! (e.g., "Your incredible photos from the ASB Gala!")', section: 'Digital Sharing' },
                    { id: 'email_body', label: 'Email Body Copy', type: 'textarea', required: false, helpText: 'The message inside the email. Great place for a thank you note or sponsor links.', section: 'Digital Sharing' },
                    { id: 'sms_copy', label: 'SMS / Text Message Copy', type: 'textarea', required: false, helpText: 'Keep it under 140 characters. Example: "Thanks for celebrating with us! View your Quikpix gallery here: [Link]"', section: 'Digital Sharing' },
                    { id: 'hashtag', label: 'Event Hashtag', type: 'text', required: false, helpText: 'e.g., #ASBGala2026. We will pre-populate this when guests share to social media.', section: 'Digital Sharing' }
                  ];

                  let needsDigitalSharing = false;
              let tier3Products: any[] = [];
              const qTitle = (quote.title || quote.eventName || '').toLowerCase();

              quoteItems(quote.items).forEach((item: any) => {
                // ⚡ FIX: We removed the 'isOptional' skip so Silent Approve scans ALL items!
                const iName = (item.name || '').toLowerCase();
                const iDesc = (item.description || '').toLowerCase();
                const sharingKeywords = ['booth', '360', 'ai', 'roamer', 'roaming', 'glamour', 'sharing', 'digital', 'kiosk', 'photo'];
                
                // ⚡ Added qTitle to scan the actual Event Name too!
                if (sharingKeywords.some(keyword => iName.includes(keyword) || iDesc.includes(keyword) || qTitle.includes(keyword))) {
                    needsDigitalSharing = true;
                }

                    const product = products.find(p => String(p.id) === String(item.productId || item.product_id));
                    if (product) {
                        const fields = (product as any).onboarding_fields || (product as any).onboardingFields || [];
                        if (fields.length > 0) {
                            fields.forEach((f: any) => {
                                const fLabel = (f.label || '').toLowerCase();
                                if (!fLabel.includes('email') && !fLabel.includes('sms') && !fLabel.includes('subject line') && !fLabel.includes('sharing') && !fLabel.includes('wi-fi') && !fLabel.includes('wifi')) {
                                    tier3Products.push({ ...f, section: `${product.name} Specs` });
                                }
                            });
                        }
                    }
                  });

                  updatePayload.portal_blueprint = [
                      ...tier1Logistics,
                      ...(needsDigitalSharing ? tier2Sharing : []),
                      ...tier3Products
                  ];
              }
          }

          const { error: quoteError } = await supabase.from('quotes').update(updatePayload).eq('id', quoteId);
          if (quoteError) throw quoteError;
          
          if (leadId) {
            let pipelineStatus = 'PROPOSAL';
            if (newStatus === 'APPROVED') pipelineStatus = 'PRE_PRODUCTION';
            if (newStatus === 'REJECTED') pipelineStatus = 'LOST';
            if (newStatus === 'WITHDRAWN' as any) pipelineStatus = 'WITHDRAWN';
            
            await supabase.from('event_leads').update({ status: pipelineStatus }).eq('id', leadId);
          }
          await Promise.all([fetchQuotes(), fetchLeads()]);
        } catch (err: any) { alert(`Failed: ${err.message}`); }
      };
    
      const handleLinkLeadToCompany = async (leadId: string, companyId: string) => {
        const selectedCompany = companies.find(c => c.id === companyId);
        try {
          const payload = { 
            company_id: companyId ? companyId : null,  
            company_name: selectedCompany?.name || 'Private Account' 
          };
    
          const { error } = await supabase
            .from('event_leads')
            .update(payload)
            .eq('id', leadId);
    
          if (error) throw error;
    
          await fetchLeads(); 
          
          setSelectedInquiry((prev: any) => 
            prev ? { ...prev, company_id: payload.company_id, companyId: payload.company_id, companyName: payload.company_name } : null
          );
        } catch (err: any) { 
          console.error("Linking Error:", err); 
          alert(`Failed to update company link: ${err.message}`); 
        }
      };
    
      const handleQuoteAction = async (item?: any) => {
        const originTab = activeTab; 
        
        setActiveTab('quotes');
        setIsDetailsModalOpen(false);
    
        if (item) {
          let leadIdForQuote = item.id;
          
          const isContactProfile = item.type === 'CONTACT' || item.eventName === 'Personnel Profile';
          
          const shouldSpawnNewInquiry = isContactProfile || originTab === 'database' || originTab === 'companies';
          
          let quoteTitle = shouldSpawnNewInquiry 
            ? `Direct Proposal - ${item.firstName} ${item.lastName}` 
            : (item.eventName || `Proposal - ${item.firstName}`);
    
          if (shouldSpawnNewInquiry) {
             try {
                const validCompanyId = (item.companyId || item.company_id);
                
                const newEventPayload = {
                   first_name: item.firstName || '',
                   last_name: item.lastName || '',
                   email: item.email || item.clientEmail || '',
                   phone: item.phone || item.clientPhone || '',
                   company_id: validCompanyId ? validCompanyId : null,
                   company_name: item.companyName !== 'Private Account' ? item.companyName : null,
                   project_ref: quoteTitle,
                   event_type: 'Direct Quote',
                   status: 'PROPOSAL' 
                };
                
                const { data, error } = await supabase
                  .from('event_leads')
                  .insert([newEventPayload])
                  .select()
                  .single();
                
                if (error) {
                   alert(`Database rejected the new Inquiry: ${error.message}`);
                } else if (data) {
                   leadIdForQuote = data.id; 
                   await fetchLeads(); 
                }
             } catch (e: any) {
                alert(`System error while spawning Inquiry: ${e.message}`);
             }
          }
    
          const newQuoteDraft: any = {
            id: crypto.randomUUID(), // ⚡ Generates a real, permanent ID immediately
            title: quoteTitle || `Draft Proposal: ${item.firstName}`, 
            status: 'DRAFT',
            createdAt: new Date().toISOString(),
            linkedCrmItemId: leadIdForQuote, 
            
            eventDate: item.eventDate || item.event_date || '',
            eventLocation: item.eventVenue || item.venue_location || item.venue || '',
            
            client: {
              firstName: item.firstName || item.first_name || '',
              lastName: item.lastName || item.last_name || '',
              email: item.clientEmail || item.email || '',
              phone: item.clientPhone || item.phone || '',
              company: (item.companyName === 'Private Account' || item.company_name === 'Private Account') ? '' : (item.companyName || item.company_name || '')
            },
            items: [],
            // ⚡ STRIKE 2: Erase the hardcoded 200 from the template forever!
            logistics: { 
              hours: 0, 
              hourlyRate: 0, 
              travelDistanceKm: 0, 
              travelRatePerKm: 0, 
              setupFee: 0 
            }
          };
    
          setActiveQuote(newQuoteDraft);
          setViewState('CREATE_QUOTE');
        } else {
          setActiveQuote(null);
          setViewState('CREATE_QUOTE');
        }
      };

    // ⚡ 1. UPDATE TASK STATUS (The Repeating Engine) ⚡
    const handleUpdateTaskStatus = async (task: any, newStatus: string) => {
      if(taskCompletion.enabled&&oneOffJob(task)){
        try {await taskCompletion.change(task,newStatus);return true;}
        catch(e:any){window.alert(taskCompletion.save(task.id).message||e.message);return false;}
      }

      // Prevent double-clicking!
      if (isProcessingTask === task.id) return;
      setIsProcessingTask(task.id);

      try {
          // Instantly update the UI so it feels fast
          setGlobalTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
          
          // Update the database
          await supabase.from('tasks').update({ status: newStatus }).eq('id', task.id);

          // ⚡ RECURRENCE MAGIC (FIXED!) ⚡
          // We only clone if it is a strictly valid repeating interval.
          const validRecurrences = ['daily', 'weekly', 'fortnightly', 'monthly', 'bimonthly', 'quarterly', 'yearly'];
          
          if (newStatus === 'COMPLETED' && validRecurrences.includes(task.recurrence)) {
              const oldDate = new Date(task.due_date || new Date());
              let nextDate = new Date(oldDate);
              
              if (task.recurrence === 'daily') nextDate.setDate(nextDate.getDate() + 1);
              if (task.recurrence === 'weekly') nextDate.setDate(nextDate.getDate() + 7);
              if (task.recurrence === 'fortnightly') nextDate.setDate(nextDate.getDate() + 14);
              if (task.recurrence === 'monthly') nextDate.setMonth(nextDate.getMonth() + 1);
              if (task.recurrence === 'bimonthly') nextDate.setMonth(nextDate.getMonth() + 2);
              if (task.recurrence === 'quarterly') nextDate.setMonth(nextDate.getMonth() + 3);
              if (task.recurrence === 'yearly') nextDate.setFullYear(nextDate.getFullYear() + 1);

              const cloneTask = {
                  title: task.title,
                  assignee_id: task.assignee_id,
                  lead_id: task.lead_id,
                  project_id: task.project_id,
                  task_type: task.task_type || 'MANUAL',
                  status: 'TODO',
                  is_urgent: task.is_urgent,
                  due_date: nextDate.toISOString(),
                  recurrence: task.recurrence
              };

              // Silently insert the next cycle
              await supabase.from('tasks').insert([cloneTask]);
              
              // Refresh the board in the background to show the new task
              fetchGlobalTasks();
          }
      } catch (err) {
          console.error("Task Update Error:", err);
          alert("Failed to update task.");
      } finally {
          setIsProcessingTask(null);
      }
    };

  const handleDeleteGlobalTask = async (id: string) => {
    if (!window.confirm("Permanently delete this task?")) return;
    setGlobalTasks(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) fetchGlobalTasks();
  };

  // ⚡ 2. STATE FOR THE NEW TASK BUILDER (Timezone Corrected)
  const getLocalDateString = () => {
    const d = new Date();
    // Adjust for New Zealand local time offset
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0];
};

const [newTaskData, setNewTaskData] = useState({
      title: '',
      assignee_id: '',
      lead_id: '',
      project_id: '',
      due_date: getLocalDateString(),
      recurrence: 'none',
      description: '', // ⚡ Added description memory
      is_urgent: false // ⚡ NEW: Tracks urgent status
  });

  // ⚡ 3. SAVE MANUAL GLOBAL TASK
  const handleSaveGlobalTask = async () => {
      if (!newTaskData.title.trim()) return alert("Task title is required.");
      
      const payload = {
          title: newTaskData.title,
          assignee_id: newTaskData.assignee_id || null,
          lead_id: newTaskData.lead_id || null,
          project_id: newTaskData.project_id || null,
          due_date: newTaskData.due_date ? new Date(newTaskData.due_date).toISOString() : null,
          status: 'TODO',
          task_type: 'MANUAL',
          recurrence: newTaskData.recurrence,
          description: newTaskData.description || null, // ⚡ Saves the instructions!
          is_urgent: newTaskData.is_urgent || false // ⚡ NEW: Saves urgent status!
      };

      const { error } = await supabase.from('tasks').insert([payload]);
      if (error) {
          alert(error.message);
      } else {
          setIsAddingGlobalTask(false);
          setNewTaskData({ title: '', assignee_id: '', lead_id: '', project_id: '', due_date: getLocalDateString(), recurrence: 'none', description: '', is_urgent: false });
          fetchGlobalTasks();
      }
  };

      const handleViewInquiry = (item: PipelineItem, shiftId?: string) => { setEquipmentFocusShiftId(shiftId); setSelectedInquiry(item); setIsDetailsModalOpen(true); };
      
      const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault(); 
        if (!email || !password) return;

        setIsLoading(true);
        try {
          const { error: authError } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password: password
          });

          if (authError) throw new Error("Invalid email or password.");

          setPassword('');
          // The auth listener validates the session and trusted Admin identity.
        } catch (err: any) {
          alert(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      
      const handleProductSaved = async () => { await fetchProducts(); };
      const handleDuplicateQuote = (old: Quote) => {
          // Keep unsaved revisions out of the persisted list. Otherwise the
          // missing-record safeguard incorrectly blocks their first save.
          setActiveQuote(createQuoteRevision(old));
          setViewState('CREATE_QUOTE');
      };
      
      const handleSaveQuote = async (quote: any, shouldPreview: boolean = false, onPersisted?: (quote: Quote) => void) => {
        try {
          const previous = quotes.find(q => q.id === quote.id);
          if (isAcceptedQuote(previous)) throw new Error('Accepted Quote totals are preserved. Duplicate to a draft revision before changing calculations.');
          const calculated = calculationPatch({ ...quote, status: 'DRAFT' });
          quote = { ...quote, ...calculated, grandTotal: calculated.grand_total, staffCountRequired: calculated.staff_count_required };
          const isDraft = String(quote.id).startsWith('draft-');
          
          let leadId = quote.linkedCrmItemId || quote.lead_id || activeQuote?.linkedCrmItemId || activeQuote?.lead_id || null;
          if (leadId === '') leadId = null;
          
          let safeDate = quote.eventDate || quote.event_date || null;
          if (typeof safeDate === 'string') {
              if (safeDate.trim() === '' || safeDate === 'Invalid Date') {
                  safeDate = null;
              } else if (safeDate.includes('/')) {
                  const parts = safeDate.split('/');
                  if (parts.length === 3) safeDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
              }
          } else if (safeDate instanceof Date) {
              safeDate = isNaN(safeDate.getTime()) ? null : safeDate.toISOString().split('T')[0];
          }
          
          const parseNum = (val: any) => {
              if (!val) return 0;
              if (typeof val === 'number') return val;
              const parsed = parseFloat(String(val).replace(/[^0-9.-]+/g, ''));
              return isNaN(parsed) ? 0 : parsed;
          };
    
          const finalTotal = calculated.grand_total;
          const subtotalNum = calculated.subtotal;
          const taxNum = calculated.gst_amount;
    
          const finalTitle = quote.eventName || quote.title || 'Untitled Proposal';
          const finalVenue = quote.eventLocation || quote.venue_location || null;
    
         // 1. Find the company by name to get its specific details
          const companySearchName = quote.client?.company || quote.companyName || '';
          const linkedCompany = companies.find(c => 
              c.name?.toLowerCase().trim() === companySearchName.toLowerCase().trim()
          );

          // 2. Grab the billing email directly from the company table
          const finalAccountsEmail = quote.accounts_email || linkedCompany?.accounts_payable_email || quote.client?.email || null;

          // 3. Print to the console to prove it found it
          console.log("🏢 FOUND COMPANY:", linkedCompany?.name || "NO MATCH");
          console.log("📧 FOUND BILLING EMAIL:", finalAccountsEmail);

          // 4. Build the payload to send to the database
          const quotePayload = {
            id: quote.id || crypto.randomUUID(),
            title: finalTitle,
            status: quote.status || 'DRAFT',
            staff_count_required: calculated.staff_count_required,
            staffing_tier: calculated.staffing_tier,
            lead_id: leadId,
            event_date: safeDate,
            event_location: finalVenue,
            
            client_first_name: quote.client?.firstName || null,
            client_last_name: quote.client?.lastName || null,
            client_email: quote.client?.email || null,
            client_phone: quote.client?.phone || null,
            client_company: quote.client?.company === 'Private Account' ? null : (quote.client?.company || null),
            accounts_email: finalAccountsEmail,
                
            // ⚡ TRUTH-SERUM FIX: Send zeros as strings so backend falsy-filters don't delete them!
            setup_fee: parseNum(quote.logistics?.setupFee) === 0 ? "0" : parseNum(quote.logistics?.setupFee),
            travel_distance_km: parseNum(quote.logistics?.travelDistanceKm) === 0 ? "0" : parseNum(quote.logistics?.travelDistanceKm),
            travel_rate_per_km: parseNum(quote.logistics?.travelRatePerKm) === 0 ? "0" : parseNum(quote.logistics?.travelRatePerKm),
            logistics_hours: 0,
            logistics_hourly_rate: 0,

            // ⚡ OVERWRITE THE GHOST: Force the JSON bundle to update!
            logistics: typeof quote.logistics === 'object' ? JSON.stringify(quote.logistics) : quote.logistics,
            internal_costs: typeof quote.internal_costs === 'object' ? JSON.stringify(quote.internal_costs) : quote.internal_costs,
    
            subtotal: subtotalNum,
            gst_amount: taxNum,
            grand_total: finalTotal,
            total_amount: finalTotal, 
            items: quote.items || [],
            packing_list: quote.equipmentList || quote.packingList || [], 
            presentation_slide_content: quote.presentationSlideContent ? { text: quote.presentationSlideContent } : null,
            custom_slides: quote.customSlides || quote.custom_slides || [],
          };

          // ⬇️ ⚡ PASTE THE NEW DASHBOARD BLUEPRINT ENGINE HERE ⚡ ⬇️
          if (quote.status === 'APPROVED') {
              const tier1Logistics = [
                { id: 'contact', label: 'On-the-Night Contact Person', type: 'text', required: true, helpText: 'Who is our team\'s main point of contact during the event? (Name & Mobile Number)', section: 'Event Logistics' },
                { id: 'access', label: 'Venue Access & Pack-in Time', type: 'text', required: true, helpText: 'What is the earliest time our crew can access the room to begin setting up?', section: 'Event Logistics' },
                { id: 'runsheet', label: 'Event Run Sheet', type: 'upload', required: false, helpText: 'Please upload your official run sheet or timeline so our team is synced with your schedule.', section: 'Event Logistics' },
                { id: 'floorplan', label: 'Venue Floor Plan / Placement', type: 'upload', required: false, helpText: 'Upload a floor plan or briefly describe exactly where you want the installation located in the room.', section: 'Event Logistics' },
                { id: 'parking', label: 'Loading Dock & Parking Instructions', type: 'textarea', required: false, helpText: 'Are there specific loading bays, height restrictions, or parking passes our crew needs to know about?', section: 'Event Logistics' },
                { id: 'power', label: 'Power & Wi-Fi Availability', type: 'select', options: 'Dedicated Power & Wi-Fi available, Power only (Bring 4G/5G internet), Neither (Fully off-grid needed)', required: true, section: 'Event Logistics' },
                { id: 'wifi_details', label: 'Wi-Fi Network & Password', type: 'text', required: false, helpText: 'If dedicated venue Wi-Fi is available, please provide the login details here.', section: 'Event Logistics' },
                { id: 'meals', label: 'Contractor Meals', type: 'select', options: 'Yes - Meals provided, No - Crew to sort own meals', required: true, helpText: 'For events spanning over 4 hours (including setup/packdown), it is industry standard to provide a vendor meal.', section: 'Event Logistics' },
                { id: 'notes', label: 'Additional Production Notes', type: 'textarea', required: false, helpText: 'Anything else our crew should know before arriving?', section: 'Event Logistics' }
              ];

              const tier2Sharing = [
                { id: 'sharing_method', label: 'Preferred Sharing Method', type: 'select', options: 'Email & SMS, Email Only, SMS Only, QR Code Only, AirDrop Only (Apple devices)', required: true, section: 'Digital Sharing' },
                { id: 'email_from_name', label: 'Email From Name', type: 'text', required: false, helpText: 'e.g., ABC Company or ASB Gala', section: 'Digital Sharing' },
                { id: 'email_reply_to', label: 'Reply Email Address', type: 'text', required: false, helpText: 'Where should replies go? e.g., events@abccompany.com', section: 'Digital Sharing' },
                { id: 'email_subject', label: 'Email Subject Line', type: 'text', required: false, helpText: 'Keep it punchy! (e.g., "Your incredible photos from the ASB Gala!")', section: 'Digital Sharing' },
                { id: 'email_body', label: 'Email Body Copy', type: 'textarea', required: false, helpText: 'The message inside the email. Great place for a thank you note or sponsor links.', section: 'Digital Sharing' },
                { id: 'sms_copy', label: 'SMS / Text Message Copy', type: 'textarea', required: false, helpText: 'Keep it under 140 characters. Example: "Thanks for celebrating with us! View your Quikpix gallery here: [Link]"', section: 'Digital Sharing' },
                { id: 'hashtag', label: 'Event Hashtag', type: 'text', required: false, helpText: 'e.g., #ASBGala2026. We will pre-populate this when guests share to social media.', section: 'Digital Sharing' }
              ];

              let needsDigitalSharing = false;
                  let tier3Products: any[] = [];
                  const qTitle = (quote.title || quote.eventName || '').toLowerCase();

                  (quote.items || []).forEach((item: any) => {
                    // ⚡ FIX: We removed the 'isOptional' skip so Silent Approve scans ALL items!
                    const iName = (item.name || '').toLowerCase();
                    const iDesc = (item.description || '').toLowerCase();
                    const sharingKeywords = ['booth', '360', 'ai', 'roamer', 'roaming', 'glamour', 'sharing', 'digital', 'kiosk', 'photo'];
                    
                    if (sharingKeywords.some(keyword => iName.includes(keyword) || iDesc.includes(keyword) || qTitle.includes(keyword))) {
                        needsDigitalSharing = true;
                    }

                const product = products.find(p => String(p.id) === String(item.productId || item.product_id));
                if (product) {
                    const fields = (product as any).onboarding_fields || (product as any).onboardingFields || [];
                    if (fields.length > 0) {
                        fields.forEach((f: any) => {
                            const fLabel = (f.label || '').toLowerCase();
                            if (!fLabel.includes('email') && !fLabel.includes('sms') && !fLabel.includes('subject line') && !fLabel.includes('sharing') && !fLabel.includes('wi-fi') && !fLabel.includes('wifi')) {
                                tier3Products.push({ ...f, section: `${product.name} Specs` });
                            }
                        });
                    }
                }
              });

              (quotePayload as any).portal_blueprint = [
                  ...tier1Logistics,
                  ...(needsDigitalSharing ? tier2Sharing : []),
                  ...tier3Products
              ];
          }
          // ⬆️ ⚡ END OF BLUEPRINT ENGINE ⚡ ⬆️

          const { data, error } = await supabase
              .from('quotes')
              .upsert([quotePayload]) 
              .select()
              .single();
              
          if (error) throw error;
    
          const cleanQuote = formatQuoteFromDB(data);
    
          setQuotes(prev => {
              const exists = prev.some(q => q.id === quote.id);
              if (exists) return prev.map(q => q.id === quote.id ? cleanQuote : q);
              return [cleanQuote, ...prev.filter(q => q.id !== quote.id)];
          });
          setActiveQuote(cleanQuote);
    
          if (isDraft) {
             setQuotes(prev => [cleanQuote, ...prev.filter(q => q.id !== quote.id)]);
          } else {
             setQuotes(prev => prev.map(q => q.id === quote.id ? cleanQuote : q));
          }
          setActiveQuote(cleanQuote);
    
        if (leadId) {
            // ⚡ UPGRADED REVENUE PROJECTION ENGINE
            // Start with the base subtotal (which safely includes required items like design fees)
            let projectedPipelineValue = subtotalNum;
            
            // Loop through all items and ADD any optional items that are checked for the Profit Simulator!
            if (quote.items && quote.items.length > 0) {
                quote.items.forEach((item: any) => {
                    const isOptional = item.isOptional || item.is_optional;
                    // This catches the most common variable names that checkbox might be using in your Quote Builder
                    const isSimulated = item.simulateProfit || item.simulate_profit || item.isSimulated || item.simulateInProfit || item.simulateInProfitAdvisor;
                    
                    if (isOptional && isSimulated) {
                        const optionPrice = parseNum(item.price || item.unitPrice || item.base_price || 0);
                        const optionQty = parseNum(item.quantity || 1);
                        projectedPipelineValue += (optionPrice * optionQty);
                    }
                });
            }

            const { error: leadError } = await supabase
               .from('event_leads')
               .update({
                  project_ref: finalTitle,             
                  event_date: safeDate,         
                  venue_location: finalVenue, 
                  company_name: quote.client?.company === 'Private Account' ? null : (quote.client?.company || null),
                  value: projectedPipelineValue // ⚡ FED TO THE PIPELINE
               })
               .eq('id', leadId);
               
            if (leadError) {
                console.error("Pipeline Sync Error:", leadError);
                alert(`Pipeline Update Failed: ${leadError.message}`); 
            }

            // ⚡ AUTO-TASK ENGINE: Post-Purchase & Logistics (Upgraded for Gear Sync & Multi-Shift Cleanup!) ⚡
            if (quote.status === 'APPROVED') {
              const { data: existingTasks } = await supabase.from('tasks').select('id, title, task_type, due_date').eq('lead_id', leadId);
              const equipmentManager = staffList.find(s => s.role === 'Equipment Manager');
              const assignedStaffId = equipmentManager?.id || null;
              try {
                  await generateEquipment(leadId, cleanQuote.id);
              } catch (error: any) {
                  alert('Quote saved. Equipment requires attention: ' + error.message);
              }

              // 9. Travel Task Logic
              const travelTitle = `🚗 LOGISTICS: Book Travel & Accommodation for ${finalTitle}`;
              const hasTravelTask = existingTasks?.some(t => t.task_type === 'AUTO_LOGISTICS' && t.title === travelTitle);
              
              if (parseNum(quote.logistics?.travelDistanceKm) > 50 && !hasTravelTask) {
                  await supabase.from('tasks').insert([{
                      title: travelTitle, lead_id: leadId, assignee_id: assignedStaffId, task_type: 'AUTO_LOGISTICS', status: 'TODO', 
                      due_date: safeDate ? new Date(new Date(safeDate).getTime() - (7 * 24 * 60 * 60 * 1000)).toISOString() : new Date().toISOString(), recurrence: 'none'
                  }]);
              }

              if (typeof fetchGlobalTasks === 'function') fetchGlobalTasks(); 
            }
         }
          
         await fetchQuotes(); 
         await fetchLeads(); 
         await fetchCompanies(); 
         // Acknowledge the exact submitted draft before mounting the recovery list.
         onPersisted?.(cleanQuote);
         setViewState(shouldPreview ? 'VIEW_QUOTE' : 'DASHBOARD');
         return true;
             
        } catch (err: any) {
          console.error("Database Save Error:", err);
          // Approval callers must not continue with pipeline/task work after a rejected calculation.
          if (quote.status === 'APPROVED') throw err;
          alert(`Failed to save quote: ${err.message}`);
          return false;
        }
      };

      const handleStatusChange = async (newStatus: QuoteStatus) => {
        if (!activeQuote) return;
        if (newStatus === 'SUPERSEDED') {
          try {
            await supersedeQuote(activeQuote.id);
            await fetchQuotes();
            const { data, error } = await supabase.from('quotes').select('*').eq('id', activeQuote.id).single();
            if (error) throw error;
            setActiveQuote(formatQuoteFromDB(data));
          } catch (error: any) { alert(`Failed to supersede quote: ${error.message}`); }
          return;
        }
        if (newStatus === 'APPROVED') {
          await handleUpdateQuoteStatus(activeQuote.id, activeQuote.linkedCrmItemId || activeQuote.lead_id, newStatus);
          return;
        }
        try {
          const { error: quoteError } = await supabase
            .from('quotes')
            .update({ status: newStatus })
            .eq('id', activeQuote.id);
          if (quoteError) throw quoteError;
    
          const linkedLeadId = activeQuote.linkedCrmItemId || activeQuote.lead_id;
          if (linkedLeadId) {
            let newPipelineStage = null;
            if (newStatus === 'SENT') newPipelineStage = 'PROPOSAL';
            if (newStatus === 'REJECTED') newPipelineStage = 'LOST';
            if ((newStatus as any) === 'WITHDRAWN') newPipelineStage = 'WITHDRAWN'; // ⚡ FIX 2: Connects the preview dropdown to the master lead!
    
            if (newPipelineStage) {
              await supabase.from('event_leads').update({ status: newPipelineStage }).eq('id', linkedLeadId);
            }
          }
    
          await Promise.all([fetchQuotes(), fetchLeads()]);
          
          setActiveQuote((prev: any) => prev ? { ...prev, status: newStatus } : null);
        } catch (err: any) {
          alert(`Failed to update status: ${err.message}`);
        }
      };

  const handleDeleteQuote = async (e: React.MouseEvent, quoteId: string) => {
    e.stopPropagation(); 
    
    if (window.confirm('CRITICAL: Are you sure you want to permanently delete this quote?')) {
        try {
            const { error } = await supabase.from('quotes').delete().eq('id', quoteId);
            if (error) throw error;
            
            alert('Quote deleted successfully.');
            await fetchQuotes(); 
        } catch (err: any) {
            console.error('Error deleting quote:', err);
            alert('Failed to delete quote: ' + err.message);
        }
    }
};

const handleEditQuote = () => setViewState('CREATE_QUOTE');
const handlePreviewUpdate = (quote: Quote) => handleSaveQuote(quote, true);

const handleNewProduct = () => { setEditingProduct(null); setIsProductEditorOpen(true); };
      const handleEditProduct = (p: Product) => { setEditingProduct(p); setIsProductEditorOpen(true); };
      const handleDeleteProduct = async (id: string) => { if (window.confirm('Delete product?')) { await supabase.from('products').delete().eq('id', id); await fetchProducts(); } };
    
  const handleNewStaff = () => {
    setEditingStaff({ 
        id: '', 
        full_name: '', 
        role: 'Event Attendant',
        email: '', 
        phone: '', 
        base_location: 'Auckland'
    });
    setIsStaffEditorOpen(true);
  };

  const handleEditStaff = (staff: any) => {
    setEditingStaff({ 
        ...staff,
        full_name: staff.full_name || staff.name || ''
    });
    setAdminCalendarMonth(new Date()); // Reset calendar to current month
    fetchAdminAvailability(staff.id);  // ⚡ Pull their calendar from the DB!
    setIsStaffEditorOpen(true);
  };

  const handleDeleteStaff = async (id: string) => {
    if (window.confirm('CRITICAL ACTION: Are you sure you want to remove this crew member from the roster?')) {
      try {
        const { error } = await supabase.from('staff_members').delete().eq('id', id);
        if (error) throw error;
        await fetchStaff();
      } catch (e: any) {
        alert(`System Error: ${e.message}`);
      }
    }
  };

  const handleSaveStaff = async () => {
    if (!editingStaff || !(editingStaff.full_name || editingStaff.name)) {
      alert("Name is required.");
      return;
    }

    const payload = {
      full_name: editingStaff.full_name || editingStaff.name,
      role: editingStaff.role,
      email: editingStaff.email,
      phone: editingStaff.phone,
      base_location: editingStaff.base_location
    };

    try {
      if (editingStaff.id) {
        const { error } = await supabase.from('staff_members').update(payload).eq('id', editingStaff.id);
        if (error) throw error;
      } else {
        const { id, ...insertPayload } = payload as any;
        const { error } = await supabase.from('staff_members').insert([insertPayload]);
        if (error) throw error;
      }

      await fetchStaff(); 
      setIsStaffEditorOpen(false);
      setEditingStaff(null);
    } catch (e: any) {
      alert(`Database Error: ${e.message}`);
    }
  };

      if (recovery) return <SessionRecoveryPanel {...recovery} onRetry={retry} onLogout={() => {
        void logout().catch(() => alert('Sign out could not be completed. Please retry.'));
      }} />;
      if (checkingSession) return <div role="status" className="min-h-screen flex items-center justify-center bg-slate-950 text-white">Restoring secure session…</div>;
      if (!isAuthenticated) {
        return (
          <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center bg-slate-950">
            <div className="w-full max-w-md relative z-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-800 text-white mb-4 border border-white/10"><Lock size={20} /></div>
                <h1 className="text-3xl font-display font-bold text-white">Licensee Login</h1>
              </div>
              <GlassCard className="p-8">
              {accessDenied && <p role="alert" className="mb-4 text-rose-300">Admin access is unavailable for this account. Please contact Admin.</p>}
              <form onSubmit={handleLogin} className="space-y-6">
                  <input type="email" required className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-brand-neonBlue outline-none transition-colors" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  <input type="password" required className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-brand-neonBlue outline-none transition-colors" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  <Button type="submit" className="w-full bg-brand-neonBlue text-slate-950 font-bold shadow-neon" disabled={isLoading}>
                      {isLoading ? <Loader2 size={20} className="animate-spin mx-auto"/> : 'Sign In'}
                  </Button>
                </form>
              </GlassCard>
            </div>
          </div>
        );
      }

      const renderQuotesList = () => {
        let filteredQuotes = (quotes || []).filter(quote => {
            // ⚡ HIDE BASE TEMPLATES FROM THE DASHBOARD!
            if (quote.is_base_template) return false;

            // 1. Search Bar Logic
            const search = quoteSearch.toLowerCase();
            const title = (quote.title || quote.eventName || '').toLowerCase();
            const clientFirst = (quote.client?.firstName || '').toLowerCase();
            const clientLast = (quote.client?.lastName || '').toLowerCase();
            const linkedLead = crmItems.find(i => i.id === quote.linkedCrmItemId || i.id === (quote as any).lead_id);
            const company = (quote.companyName || quote.client?.company || linkedLead?.companyName || '').toLowerCase();
            
            const matchesSearch = title.includes(search) || clientFirst.includes(search) || clientLast.includes(search) || company.includes(search);
            if (!matchesSearch) return false;

            // 2. Status Dropdown Logic
            const s = String(quote.status || 'DRAFT').toUpperCase();
            if (quoteStatusFilter === 'ACTIVE') {
                return !['WITHDRAWN', 'ARCHIVED', 'REJECTED', 'SUPERSEDED'].includes(s);
            }
            // ⚡ THE NEW FOLLOW-UP FILTER ENGINE
            if (quoteStatusFilter === 'FOLLOW_UP') {
                const threeDaysAgo = new Date();
                threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
                const d = new Date((quote as any).updated_at || quote.createdAt || (quote as any).created_at || new Date());
                return (s === 'SENT' || s === 'PROPOSAL') && (d < threeDaysAgo);
            }
            if (quoteStatusFilter === 'SENT') return s === 'SENT' || s === 'PROPOSAL';
            if (quoteStatusFilter === 'ALL') return true;
            return s === quoteStatusFilter;
        });

        // 3. Sorting Engine Logic
        filteredQuotes.sort((a, b) => {
            if (quoteSortOrder === 'VALUE_DESC') {
                const valA = Number(a.subtotal || a.amount || a.value || 0);
                const valB = Number(b.subtotal || b.amount || b.value || 0);
                return valB - valA;
            }
            if (quoteSortOrder === 'EVENT_DATE_ASC') {
                const dateA = a.eventDate ? new Date(a.eventDate).getTime() : Infinity;
                const dateB = b.eventDate ? new Date(b.eventDate).getTime() : Infinity;
                return dateA - dateB; // Upcoming events first
            }
            // ⚡ NEW: Sort by Most Views
            if (quoteSortOrder === 'VIEWS_DESC') {
                return (b.viewCount || 0) - (a.viewCount || 0);
            }
            // ⚡ NEW: Sort by Most Recently Viewed
            if (quoteSortOrder === 'LAST_VIEWED_DESC') {
                return compareClientLastViewed(a, b);
            }
            // Default: CREATED_DESC (Newest quotes first)
            const createdA = new Date(a.createdAt || a.created_at || 0).getTime();
            const createdB = new Date(b.createdAt || b.created_at || 0).getTime();
            return createdB - createdA;
        });

        const getQuoteBadge = (status: string) => {
            const s = String(status || '').toUpperCase();
            if (s === 'DRAFT') return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
            if (s === 'SENT' || s === 'PROPOSAL') return 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/20';
            if (s === 'APPROVED' || s === 'PRE_PRODUCTION') return 'bg-green-500/10 text-green-400 border-green-500/20';
            if (s === 'COMPLETED') return 'bg-slate-800 text-slate-400 border-white/10';
            if (s === 'REJECTED' || s === 'LOST') return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
            if (s === 'WITHDRAWN' || s === 'ARCHIVED') return 'bg-slate-800 text-slate-500 border-slate-700 border-dashed';
            return 'bg-slate-800 text-slate-400 border-slate-700';
        };

        return (
            <div className="space-y-6 mt-6">
                {/* ⚡ UPGRADED MOBILE-RESPONSIVE FILTERS */}
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between bg-slate-900/40 p-4 md:p-5 rounded-[24px] md:rounded-[32px] border border-white/5 shadow-inner">
                    <div className="relative w-full lg:max-w-md">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" size={18}/>
                        <input 
                            className="w-full bg-slate-950/50 border border-slate-800 rounded-[16px] py-4 pl-12 pr-6 text-white focus:border-brand-neonBlue outline-none shadow-inner transition-all text-sm placeholder:text-slate-600" 
                            placeholder="Search proposals..."
                            value={quoteSearch}
                            onChange={e => setQuoteSearch(e.target.value)}
                        />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                        {/* Status Dropdown */}
                        <div className="flex items-center bg-slate-950/50 rounded-[16px] border border-slate-800 flex-1 sm:flex-none overflow-hidden hover:border-brand-neonBlue/50 transition-colors">
                            <div className="pl-4 pr-3 bg-slate-900 flex items-center h-full border-r border-slate-800">
                                <Filter size={14} className="text-brand-neonBlue" />
                            </div>
                            <select 
                                value={quoteStatusFilter}
                                onChange={e => setQuoteStatusFilter(e.target.value)}
                                className="bg-transparent text-[10px] md:text-xs font-black uppercase tracking-widest text-white outline-none cursor-pointer py-4 px-4 appearance-none w-full"
                            >
                                <option value="ACTIVE">🟢 Active Pipeline</option>
                                <option value="ALL">📋 All Quotes</option>
                                <option value="DRAFT">🟡 Drafts Only</option>
                                <option value="SENT">🔵 Sent & Pending</option>
                                <option value="FOLLOW_UP">🔥 Action: To Follow-Up</option>
                                <option value="APPROVED">✅ Approved</option>
                                <option value="WITHDRAWN">👻 Withdrawn / Archived</option>
                                <option value="SUPERSEDED">Superseded</option>
                            </select>
                        </div>
                        
                        {/* Sort Dropdown */}
                        <div className="flex items-center bg-slate-950/50 rounded-[16px] border border-slate-800 flex-1 sm:flex-none overflow-hidden hover:border-brand-neonPink/50 transition-colors">
                            <div className="pl-4 pr-3 bg-slate-900 flex items-center h-full border-r border-slate-800">
                                <ListIcon size={14} className="text-brand-neonPink" />
                            </div>
                            <select 
                                value={quoteSortOrder}
                                onChange={e => { setQuoteSortOrder(e.target.value); void fetchQuotes(); }}
                                className="bg-transparent text-[10px] md:text-xs font-black uppercase tracking-widest text-white outline-none cursor-pointer py-4 px-4 appearance-none w-full"
                            >
                                <option value="CREATED_DESC">📅 Newest First</option>
                                <option value="EVENT_DATE_ASC">🚀 Next Event Date</option>
                                <option value="VALUE_DESC">💰 Highest Value</option>
                                {/* ⚡ NEW: Added the view count sorting options */}
                                <option value="VIEWS_DESC">👀 Most Viewed</option>
                                <option value="LAST_VIEWED_DESC">🔥 Recently Viewed — clients, newest first</option>
                            </select>
                        </div>
                        {clientViewHistoryUnavailable && <p role="status" className="text-xs text-amber-300">Recent client views could not be refreshed. Showing historical view records.</p>}
                    </div>
                </div>

                <div className="space-y-4">
                    {filteredQuotes.map((quote) => (
                        <div 
                        key={quote.id}
                        onClick={() => { 
                            const linkedLead = crmItems.find(i => i.id === quote.linkedCrmItemId || i.id === (quote as any).lead_id);
                            if (linkedLead) {
                                handleViewInquiry(linkedLead);
                            } else {
                                setActiveQuote(quote); 
                                setViewState('VIEW_QUOTE'); 
                            }
                        }} 
                        className={`group bg-slate-900/40 border hover:border-brand-neonBlue/40 p-5 md:p-6 rounded-[24px] md:rounded-[32px] cursor-pointer transition-all duration-300 flex flex-col md:flex-row justify-between md:items-center gap-4 relative overflow-hidden shadow-lg ${quote.status === 'WITHDRAWN' ? 'opacity-40 hover:opacity-100 border-white/5' : 'border-white/5'}`}
                    >
                            {quote.status === 'DRAFT' && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]"></div>
                            )}

<div className="flex flex-col gap-2.5 min-w-0 pr-4">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h3 className={`text-lg md:text-xl font-bold transition-colors truncate ${quote.status === 'WITHDRAWN' ? 'text-slate-500 line-through' : 'text-white group-hover:text-brand-neonBlue'}`}>
                                        {quote.title || quote.eventName || 'Unnamed Proposal'}
                                    </h3>
                                    <span className={`type-button text-xs px-3 py-1 rounded-full border shrink-0 ${getQuoteBadge(quote.status)}`}>
                                        {quote.status || 'DRAFT'}
                                    </span>
                                    
                                    {/* ⚡ UPGRADED: VIEW COUNTER BADGE WITH TIMESTAMP */}
                                    {quote.viewCount > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mt-1 sm:mt-0">
                                            <span className="flex items-center w-fit gap-1.5 text-[11px] md:text-sm font-bold text-white bg-slate-800/80 px-3 py-1.5 rounded-lg border border-white/10 shrink-0 shadow-inner">
                                                <Eye size={14} className="text-brand-neonBlue" /> 
                                                {quote.viewCount} {quote.viewCount === 1 ? 'View' : 'Views'}
                                            </span>
                                            {quote.lastViewedAt && (
                                                <span className="text-[11px] md:text-xs text-brand-neonBlue font-medium italic">
                                                    Last seen: {
                                                        formatClientViewNZ(quote.lastViewedAt)
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1 text-[10px] md:text-xs font-medium text-slate-400">
                                    <span className="flex items-center gap-1.5 bg-slate-950/50 px-3 py-1.5 rounded-lg border border-white/5">
                                        <Calendar size={12} className="text-slate-500" />
                                        {quote.eventDate ? new Date(quote.eventDate).toLocaleDateString('en-NZ') : 'No Event Date'}
                                    </span>

                                    <span className="flex items-center gap-1.5 bg-slate-950/50 px-3 py-1.5 rounded-lg border border-white/5">
                                        <MapPin size={12} className="text-slate-500" />
                                        <span className="truncate max-w-[120px] md:max-w-[200px]">{quote.eventLocation || 'No Venue'}</span>
                                    </span>

                                    <span className="flex items-center gap-1.5 bg-slate-950/50 px-3 py-1.5 rounded-lg border border-white/5">
                                        <Building2 size={12} className="text-slate-500" />
                                        <span className="truncate max-w-[120px] md:max-w-[200px]">
                                            {(() => {
                                                const linkedLead = crmItems.find(i => i.id === quote.linkedCrmItemId || i.id === (quote as any).lead_id);
                                                return quote.companyName || (quote.client && quote.client.company) || linkedLead?.companyName || 'Private Account';
                                            })()}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <div className="text-left md:text-right flex flex-col justify-center items-start md:items-end z-10 pt-4 md:pt-0 border-t md:border-t-0 border-white/5 md:border-none shrink-0">
                                <div className="flex items-center w-full md:w-auto justify-between md:justify-end gap-4">
                                    <div className={`text-xl md:text-2xl font-bold tracking-tight ${quote.status === 'WITHDRAWN' ? 'text-slate-500' : 'text-white'}`}>
                                        ${Number(quote.subtotal || quote.amount || quote.value || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                      {/* ⚡ NEW VIEW PROPOSAL BUTTON */}
                                      <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveQuote(quote); 
                                                setViewState('VIEW_QUOTE');
                                            }}
                                            className="px-3 py-1.5 md:px-4 md:py-2 bg-brand-neonBlue/10 hover:bg-brand-neonBlue text-brand-neonBlue hover:text-slate-950 rounded-lg md:rounded-xl transition-all border border-brand-neonBlue/30 font-bold text-[9px] md:text-[10px] uppercase tracking-widest flex items-center gap-1.5"
                                            title="View Proposal Document"
                                        >
                                            <FileText size={14} className="shrink-0" /> <span className="hidden sm:inline">View</span>
                                        </button>

                                      
                                        {/* ⚡ UPGRADED SILENT APPROVE BUTTON */}
                                        {quote.status !== 'APPROVED' && quote.status !== 'COMPLETED' && quote.status !== 'WITHDRAWN' && quote.status !== 'SUPERSEDED' && (
                                            <button 
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('SILENT APPROVE: Push this job into Live Logistics WITHOUT sending emails or creating an invoice?')) {
                                                        try {
                                                            // Route through the master engine to trigger Auto-Tasks
                                                            await handleSaveQuote({ ...quote, status: 'APPROVED' }, false);
                                                            
                                                            // Bump the pipeline stage automatically
                                                            const targetLeadId = quote.linkedCrmItemId || quote.lead_id || quote.leadId;
                                                            if (targetLeadId) {
                                                                await supabase.from('event_leads').update({ status: 'PRE_PRODUCTION' }).eq('id', targetLeadId);
                                                                if (typeof fetchLeads === 'function') await fetchLeads(); // Refresh the pipeline
                                                            }
                                                            
                                                            alert('Success! Job is now Live and Warehouse Prep Tasks have been dispatched.');
                                                        } catch (err: any) {
                                                            alert('Error: ' + err.message);
                                                        }
                                                    }
                                                }}
                                                className="type-button text-xs px-3 py-2 rounded-xl bg-brand-neonBlue/10 text-brand-neonBlue border border-brand-neonBlue/30 hover:bg-brand-neonBlue hover:text-slate-950 transition-all shadow-[0_0_15px_rgba(34,211,238,0.2)] mr-2"
                                            >
                                                Silent Approve
                                            </button>
                                        )}

                                        {/* ⚡ NEW WITHDRAW BUTTON */}
                                        {quote.status !== 'WITHDRAWN' && quote.status !== 'APPROVED' && (
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if(window.confirm('Withdraw this quote? It will be hidden from the active pipeline and won\'t count towards expected revenue.')) {
                                                        handleUpdateQuoteStatus(quote.id, quote.linkedCrmItemId || (quote as any).lead_id, 'WITHDRAWN' as any);
                                                    }
                                                }}
                                                className="p-2.5 bg-slate-950/50 hover:bg-slate-800 text-slate-500 hover:text-white rounded-xl transition-all border border-transparent hover:border-slate-600 flex items-center justify-center"
                                                title="Withdraw Quote"
                                            >
                                                <XCircle size={16} />
                                            </button>
                                        )}
                                        
                                        <button 
                                            onClick={(e) => handleDeleteQuote(e, quote.id)}
                                            className="p-2.5 bg-slate-950/50 hover:bg-rose-500/20 text-slate-600 hover:text-rose-500 rounded-xl transition-all border border-transparent hover:border-rose-500/30 flex items-center justify-center"
                                            title="Delete Quote Permanently"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {filteredQuotes.length === 0 && (
                        <div className="text-center py-20 border border-dashed border-white/5 rounded-[32px] text-slate-500 font-medium">
                            No proposals found matching this criteria.
                        </div>
                    )}
                </div>
            </div>
        );
      };
    
      const renderLeadsList = () => {
        if (leadFilter === 'NEEDS_GEAR') return <EquipmentConfirmationList jobs={crmItems} shifts={allShifts}
          onBack={()=>setLeadFilter('ALL')}
          onOpen={(job,shiftId)=>handleViewInquiry(job, shiftId)} />;
        let leads = (crmItems || []).filter((item: any) => item.type === 'SALES');

        // ⚡ 1. DEFINE HELPER FUNCTIONS FIRST
        const isActionRequired = (item: any) => {
          const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
          if (['INQUIRY', 'DISCOVERY', 'POST_EVENT'].includes(stage)) return true;
          
          // ⚡ FIX: Only check for missing crew or un-prepped gear if the job is actually APPROVED and ACTIVE!
          const activeProductionStages = ['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT'];
          if (!activeProductionStages.includes(stage)) return false;

          const projectShifts = allShifts.filter(s => s.lead_id === item.id);
          for (const shift of projectShifts) {
              const roster = shift.staff_roster;
              if ((!roster || !roster.crew || roster.crew.length===0) && !(shift.staffing_source==='PHASES' && roster?.required_count===0))return true;
              
              const confirmedCount = roster.crew.filter((c: any) => c.status === 'CONFIRMED').length;
              const hasAttentionNeeded = roster.crew.some((c: any) => c.status === 'ACCEPTED' || c.status === 'PENDING');
              
              if (confirmedCount < (shift.staffing_source==='PHASES'?(roster.required_count??0):(roster.required_count||1)) || hasAttentionNeeded) return true;
          }

          // ⚡ NEW: Mark as needing action if prep is pending!
          const prepTasks = globalTasks.filter(t => t.lead_id === item.id && t.title?.includes('Prep Gear'));
          if (prepTasks.length > 0) {
              const allPrepped = prepTasks.every(t => t.status === 'COMPLETED');
              if (!allPrepped) return true; 
          }

          return false;
        };

        const getActionTypes = (item: any) => {
          const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
          const badges: {label: string, color: string}[] = [];

          if (stage === 'INQUIRY') {
              badges.push({ label: 'New Lead', color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' });
              return badges;
          }
          if (stage === 'POST_EVENT') {
              badges.push({ label: 'Awaiting Wrap-Up', color: 'text-indigo-400 border-indigo-500/30 bg-indigo-500/10' });
          }

          // ⚡ FIX: Only show Staffing and Prep warnings if the job is actually Approved/Active!
          const activeProductionStages = ['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT'];
          if (activeProductionStages.includes(stage)) {
              const projectShifts = allShifts.filter(s => s.lead_id === item.id);
              let hasPendingApproval = false;
              let hasMissingCrew = false;
              let hasAwaitingReply = false;
              let hasMissingGear = false; // ⚡ NEW

              for (const shift of projectShifts) {
                  const crew = shift.staff_roster?.crew || [];
                  const required = shift.staffing_source==='PHASES'?(shift.staff_roster?.required_count??0):(shift.staff_roster?.required_count||1);
                  const confirmed = crew.filter((c: any) => c.status === 'CONFIRMED').length;

                  if (crew.some((c: any) => c.status === 'ACCEPTED')) hasPendingApproval = true;
                  if (confirmed < required || (!crew.length && required>0)) hasMissingCrew = true;
                  if (crew.some((c: any) => c.status === 'PENDING')) hasAwaitingReply = true;
                  
                  // ⚡ Check if gear needs confirming
                  if (equipmentConfirmationQueue([item], [shift]).length > 0) {
                      hasMissingGear = true;
                  }
              }

              if (hasMissingGear) badges.push({ label: 'Gear to Confirm', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10 shadow-[0_0_10px_rgba(249,115,22,0.2)]' }); // ⚡ NEW
              if (hasPendingApproval) badges.push({ label: 'To Approve', color: 'text-brand-neonBlue border-brand-neonBlue/30 bg-brand-neonBlue/10' });
              if (hasMissingCrew) badges.push({ label: 'Short Staffed', color: 'text-rose-400 border-rose-500/30 bg-rose-500/10' });
              if (hasAwaitingReply) badges.push({ label: 'Awaiting Reply', color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' });

              const prepTasks = globalTasks.filter(t => t.lead_id === item.id && t.title?.includes('Prep Gear'));
              if (prepTasks.length > 0) {
                  const allPrepped = prepTasks.every(t => t.status === 'COMPLETED');
                  if (allPrepped) {
                      badges.push({ label: 'Gear Ready', color: 'text-green-400 border-green-500/30 bg-green-500/10 shadow-[0_0_10px_rgba(34,197,94,0.2)]' });
                  } else {
                      badges.push({ label: 'Prep Pending', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' });
                  }
              }
          }

          return badges;
        };

        // ⚡ RESTORED: THE MISSING BADGE FUNCTION
        const getStageBadge = (stageRaw: string) => {
            const s = String(stageRaw || '').toUpperCase();
            if (s === 'INQUIRY') return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
            if (s === 'DISCOVERY') return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
            if (s === 'PROPOSAL') return 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/20';
            if (s === 'PRE_PRODUCTION') return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
            if (s === 'STAGING') return 'bg-pink-500/10 text-pink-400 border-pink-500/20';
            if (s === 'LIVE_EVENT' || s === 'LIVE') return 'bg-red-500/10 text-red-400 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.2)]';
            if (s === 'POST_EVENT') return 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20';
            if (s === 'COMPLETED' || s === 'COMPLETE') return 'bg-green-500/10 text-green-400 border-green-500/20';
            if (s === 'LOST') return 'bg-slate-800 text-slate-500 border-slate-700';
            return 'bg-slate-800 text-slate-400 border-slate-700';
        };

        // ⚡ 2. NOW APPLY THE FILTERS (Using the functions above)
        if (leadFilter === 'ACTION') {
          leads = leads.filter(isActionRequired);
      } else if (leadFilter === 'NEEDS_GEAR') {
          // ⚡ NEW: Filter by our new orange gear badge!
          leads = leads.filter((item: any) => getActionTypes(item).some(b => b.label === 'Gear to Confirm'));
      } else if (leadFilter === 'NEEDS_QUOTE') {
          // ⚡ THE NEW ENGINE: Only show new inquiries that don't have a quote attached yet
          leads = leads.filter((item: any) => {
              const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
              if (stage !== 'INQUIRY' && stage !== 'DISCOVERY') return false;
              const hasQuote = quotes.some(q => q.linkedCrmItemId === item.id || (q as any).lead_id === item.id);
              return !hasQuote;
          });
      } else if (leadFilter === 'SHORT_STAFFED') {
          leads = leads.filter((item: any) => getActionTypes(item).some(b => b.label === 'Short Staffed'));
        } else if (leadFilter === 'TO_APPROVE') {
            leads = leads.filter((item: any) => getActionTypes(item).some(b => b.label === 'To Approve'));
          } else if (leadFilter === 'AWAITING_REPLY') {
            leads = leads.filter((item: any) => getActionTypes(item).some(b => b.label === 'Awaiting Reply'));
          } else if (leadFilter === 'ACTIVE') {
            leads = leads.filter((item: any) => {
                const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
                // ⚡ FIX: Now explicitly excludes WITHDRAWN from the active view!
                return !['POST_EVENT', 'COMPLETED', 'COMPLETE', 'LOST', 'REJECTED', 'WITHDRAWN', 'ARCHIVED'].includes(stage);
            });
        } else if (leadFilter === 'WITHDRAWN') {
            // ⚡ FIX: Adds the dedicated logic for the Withdrawn filter
            leads = leads.filter((item: any) => {
                const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
                return ['WITHDRAWN', 'ARCHIVED'].includes(stage);
            });
        } else if (leadFilter === 'WRAP_UP') {
            // ⚡ NEW: Dedicated bucket for jobs that just finished
            leads = leads.filter((item: any) => {
                const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
                return stage === 'POST_EVENT';
            });
        } else if (leadFilter === 'COMPLETED') {
            leads = leads.filter((item: any) => {
                const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
                return ['COMPLETED', 'COMPLETE'].includes(stage);
            });
          } else if (leadFilter === 'LOST') {
            leads = leads.filter((item: any) => {
                const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
                return ['LOST', 'REJECTED'].includes(stage);
            });
        }

        // ⚡ NEW: FORCE SORT ALL LEADS BY EVENT DATE (EARLIEST TO LATEST)
        // Any job with 'TBD' or no date gets safely pushed to the bottom
        leads.sort((a: any, b: any) => {
            const dateA = a.eventDate && a.eventDate !== 'TBD' ? new Date(a.eventDate).getTime() : Infinity;
            const dateB = b.eventDate && b.eventDate !== 'TBD' ? new Date(b.eventDate).getTime() : Infinity;
            return dateA - dateB;
        });

        return (
          <div className="animate-fade-in-up mt-2">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 md:mb-10 gap-4 md:gap-6">
              <div>
                <h1 className="type-page-title text-white">Lead Capture</h1>
                <p className="type-label mt-2 flex items-center gap-2 text-slate-500">
                  <Mail size={12} className="text-brand-neonBlue" /> Website & Direct Inquiries
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  {/* ⚡ NEW: Side-by-Side Mobile Filters */}
                  <div className="grid grid-cols-2 sm:flex gap-2 w-full">
                      {/* Action Required Quick-Toggle */}
                      <div className="flex items-center bg-slate-900/80 rounded-xl md:rounded-[20px] p-1 border border-white/10 backdrop-blur-xl shadow-2xl">
                         <button onClick={() => setLeadFilter(leadFilter === 'ACTION' ? 'ACTIVE' : 'ACTION')} className={`type-button flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs transition-all md:rounded-xl md:px-6 md:py-2.5 ${leadFilter === 'ACTION' ? 'bg-brand-neonPink text-white shadow-neon-pink' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}>
                            <AlertCircle size={12} className={`shrink-0 ${leadFilter === 'ACTION' ? 'text-white' : 'text-brand-neonPink'}`} /> <span className="truncate">Action</span>
                         </button>
                      </div>

                      {/* Status Dropdown */}
                      <div className="flex items-center bg-slate-900/80 rounded-xl md:rounded-[20px] border border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden hover:border-brand-neonBlue/50 transition-colors">
                          <div className="pl-2 md:pl-4 pr-1.5 md:pr-3 bg-slate-900/50 flex items-center h-full border-r border-white/5">
                              <Filter size={12} className="text-brand-neonBlue shrink-0" />
                          </div>
                          <select 
                              value={leadFilter}
                              onChange={e => setLeadFilter(e.target.value)}
                              className="type-button min-h-11 w-full min-w-0 cursor-pointer appearance-none bg-transparent px-2 py-2 text-xs text-white outline-none md:px-4 md:py-3"
                          >
                              <option value="ACTIVE">🟢 Upcoming / Active</option>
                              <option value="NEEDS_GEAR">🟠 Gear to Confirm</option>
                              <option value="NEEDS_QUOTE">📝 Needs Quote</option>
                              <option value="WRAP_UP">🏁 To Wrap-Up</option>
                              <option value="ALL">📋 All</option>
                              <option value="COMPLETED">✅ Done</option>
                              <option value="WITHDRAWN">📁 Withdrawn / Archived</option> 
                              <option value="LOST">👻 Lost</option>
                          </select>
                      </div>
                  </div>
                  
                  {/* ⚡ SLIMMED LOG LEAD BUTTON */}
                  <Button onClick={() => setIsAddingInquiry(true)} className="w-full sm:w-auto bg-brand-neonBlue text-slate-950 font-bold px-4 py-2.5 md:px-6 md:py-3 rounded-xl md:rounded-[20px] hover:scale-105 transition-transform border-none shadow-neon text-xs md:text-sm">
                      <Plus size={16} className="mr-1.5" /> Log Lead
                  </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
            {leads.map((item: any) => {
              const currentStage = String((item as any).stage || item.salesStage || item.status || 'INQUIRY').toUpperCase();
              const actionBadges = getActionTypes(item);
              const needsAction = isActionRequired(item);
              
              return (
                <GlassCard key={item.id} className="p-5 rounded-2xl hover:border-brand-neonBlue/50 cursor-pointer group transition-all relative overflow-hidden shadow-lg" onClick={() => handleViewInquiry(item)}>
                {needsAction && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-neonPink shadow-[0_0_15px_rgba(244,114,182,0.5)] z-10"></div>
                )}
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                        <h4 className="text-lg font-bold text-white group-hover:text-brand-neonBlue transition-colors">
                          {item.eventName || item.project_ref || 'Unnamed Event'}
                        </h4>
                        <span className={`type-label rounded-full border px-3 py-1 ${getStageBadge(currentStage)}`}>
                            {currentStage.replace('_', ' ')}
                        </span>
                        <SubmissionTypeBadge submissionType={item.submission_type} />
                        {/* ⚡ UPGRADED: RENDERS MULTIPLE SPECIFIC ACTION BADGES */}
                        {actionBadges.map((badge, idx) => (
                            <span key={idx} className={`type-label rounded-full border px-3 py-1 ${badge.color}`}>
                                {badge.label}
                            </span>
                        ))}
                    </div>
                    
                    <p className="text-slate-400 font-medium text-xs mt-1">
                      {item.firstName} {item.lastName} <span className="mx-1.5 text-slate-600">•</span> {companies.find(c => c.id === item.company_id || c.id === item.companyId)?.name || item.companyName || 'Private Account'}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-400 md:gap-3">
                      <span className="flex items-center gap-1 bg-slate-950/50 px-2 py-1 md:px-2.5 md:py-1.5 rounded-lg border border-white/5 max-w-[140px] md:max-w-none">
                        <MapPin size={10} className="text-slate-500 shrink-0"/> <span className="truncate">{item.eventVenue || 'No Venue'}</span>
                      </span>
                      <span className="flex items-center gap-1 bg-slate-950/50 px-2 py-1 md:px-2.5 md:py-1.5 rounded-lg border border-white/5 shrink-0">
                        <Calendar size={10} className="text-slate-500 shrink-0"/> {item.eventDate || 'No Date'}
                      </span>
                      <span className="type-numeric ml-auto flex shrink-0 items-center gap-1 text-brand-neonBlue md:ml-2">
                         ${(Number(item.value) || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 shrink-0 mt-3 md:mt-0 w-full md:w-auto" onClick={e => e.stopPropagation()}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = item as any;
                        const displayNotes = `PROJECT: ${data.eventName || 'N/A'}\nVENUE: ${data.eventVenue || 'TBD'}\nDATE: ${data.eventDate || 'TBD'}\n🛠️ PRODUCTS: ${data.interestedProducts || 'None'}\n\nREQS: ${data.notes || ''}`;
                        setSelectedInquiry({ ...data, notes: displayNotes });
                        setIsDetailsModalOpen(true);
                      }} 
                      className="type-button min-h-11 flex-1 rounded-lg border border-white/10 px-2 py-2 text-center text-xs text-slate-300 transition-all hover:bg-white/10 md:flex-none md:rounded-xl md:px-4 md:py-2.5"
                    >
                      Detail Specs
                    </button>
                    <button 
                      onClick={() => handleQuoteAction(item)} 
                      className="type-button min-h-11 flex-1 rounded-lg bg-brand-neonBlue px-2 py-2 text-center text-xs text-slate-950 shadow-neon transition-all hover:bg-brand-neonBlue/90 md:flex-none md:rounded-xl md:px-5 md:py-2.5"
                    >
                      Build Proposal
                    </button>
                  </div>
                </div>
              </GlassCard>
            )})}
            {leads.length === 0 && (
                <div className="text-center py-16 border-2 border-dashed border-white/5 rounded-[40px] text-slate-500 font-medium">
                    No active leads matching this filter.
                </div>
            )}
            </div>
          </div>
        );
      };

      const renderDatabase = () => {
        // ⚡ UPGRADED: Added Dropdown Filtering Logic
        const searched = crmItems.filter((i: any) => {
          // 1. Text Search
          const search = dbSearch.toLowerCase();
          const first = (i.firstName || "").toLowerCase();
          const last = (i.lastName || "").toLowerCase();
          const comp = (i.companyName || "").toLowerCase();
          const mail = (i.clientEmail || (i as any).email || "").toLowerCase();
          
          const matchesSearch = first.includes(search) || last.includes(search) || comp.includes(search) || mail.includes(search);
          if (!matchesSearch) return false;

          // 2. Status Filter
          const status = String(i.status || i.salesStage || '').toUpperCase();
          if (dbFilter === 'SALES') return i.type === 'SALES' && !['COMPLETED', 'LOST', 'REJECTED'].includes(status);
          if (dbFilter === 'PRODUCTION') return ['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'POST_EVENT'].includes(status);
          if (dbFilter === 'COMPLETED') return status === 'COMPLETED';
          if (dbFilter === 'CONTACT') return i.type === 'CONTACT';
          
          return true; // 'ALL'
        });
    
        const uniqueClientsMap = new Map();
        searched.forEach(client => {
            const c = client as any; 
            const key = `${c.firstName}-${c.lastName}-${c.clientEmail || c.email}`.toLowerCase();
            
            if (!uniqueClientsMap.has(key) || c.type === 'CONTACT') {
                uniqueClientsMap.set(key, client);
            }
        });
        
        const filtered = Array.from(uniqueClientsMap.values()).sort((a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || ''));
    
        return (
          <div className="animate-fade-in-up mt-2">
            <div className="flex flex-col lg:flex-row justify-between items-end mb-8 md:mb-12 gap-6 lg:gap-8">
              <div>
                <h1 className="type-page-title text-white">Client Network</h1>
                <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] mt-4 flex items-center gap-2">
                    <Database size={12} className="text-brand-neonBlue" /> Master Account Directory
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                {/* ⚡ NEW: Status Dropdown Filter */}
                <div className="flex items-center bg-slate-900/80 rounded-xl md:rounded-[22px] border border-slate-700/50 backdrop-blur-xl shadow-2xl overflow-hidden hover:border-brand-neonBlue/50 transition-colors shrink-0">
                    <div className="pl-4 pr-3 bg-slate-950/50 flex items-center h-full border-r border-slate-700/50">
                        <Filter size={14} className="text-brand-neonBlue" />
                    </div>
                    <select 
                        value={dbFilter}
                        onChange={e => setDbFilter(e.target.value)}
                        className="bg-transparent text-[10px] md:text-xs font-black uppercase tracking-widest text-white outline-none cursor-pointer py-3 md:py-4 px-4 appearance-none w-full md:w-auto min-w-[140px]"
                    >
                        <option value="ALL">📋 All Clients</option>
                        <option value="SALES">🟡 Active Quotes</option>
                        <option value="PRODUCTION">🟢 In Production</option>
                        <option value="COMPLETED">✅ Completed Jobs</option>
                        <option value="CONTACT">👤 Contacts Only</option>
                    </select>
                </div>

                <div className="relative flex-1 w-full lg:min-w-[300px]">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18}/>
                  <input 
                    className="w-full bg-slate-900/60 border border-slate-700/50 rounded-xl md:rounded-[22px] py-3 md:py-4 pl-12 pr-6 text-white focus:border-brand-neonBlue outline-none shadow-2xl backdrop-blur-xl transition-all placeholder:text-slate-700 font-medium text-sm" 
                    placeholder="Search network identity..."
                    value={dbSearch}
                    onChange={e => setDbSearch(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={() => {
                    setNewClient({ firstName: '', lastName: '', clientEmail: '', clientPhone: '', companyName: '', companyId: '' });
                    setIsAddingClient(true);
                  }}
                  className="bg-brand-neonPink text-white shadow-neon-pink px-6 py-3 md:px-8 md:py-4 rounded-xl md:rounded-[22px] font-bold transition-transform hover:scale-105 w-full sm:w-auto whitespace-nowrap text-sm shrink-0"
                >
                  <Plus size={18} className="mr-2" /> Initialize Record
                </Button>
              </div>
            </div>
    
            <div className="bg-slate-900/40 rounded-[32px] md:rounded-[48px] border border-white/5 overflow-hidden backdrop-blur-3xl shadow-[0_40px_100px_rgba(0,0,0,0.4)] relative">
              <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead className="bg-slate-950/80 text-slate-500 text-[10px] uppercase font-bold tracking-[0.3em] whitespace-nowrap">
                    <tr>
                      <th className="py-4 px-6 md:px-8">Entity Identity</th>
                      <th className="py-4 px-6 md:px-8">Contact Info</th>
                      <th className="py-4 px-6 md:px-8">Corporate Link</th>
                      <th className="py-4 px-6 md:px-8">Account Status</th>
                      <th className="py-4 px-6 md:px-8 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filtered.map((client: any) => {
                      const cEmail = client.email || client.clientEmail;
                      const cPhone = client.phone || client.clientPhone;

                      return (
                      <tr 
                        key={client.id} 
                        // ⚡ UPGRADED: The entire row is now clickable!
                        onClick={() => handleViewInquiry(client)}
                        className="hover:bg-white/5 text-slate-300 transition-all duration-300 group cursor-pointer"
                      >
                        
                        <td className="py-3 px-6 md:px-8">
                          <div className="flex items-center gap-3 md:gap-4">
                            <div className="type-heading flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/5 bg-slate-800 text-lg text-brand-neonBlue transition-all group-hover:border-brand-neonBlue/30 md:h-12 md:w-12 md:rounded-2xl md:text-xl">
                                {(client.firstName || "U")[0]}
                            </div>
                            <p className="text-base md:text-lg font-bold text-white tracking-tight group-hover:text-brand-neonBlue transition-colors line-clamp-1">
                              {client.firstName} {client.lastName}
                            </p>
                          </div>
                        </td>

                        <td className="py-3 px-6 md:px-8">
                            <div className="space-y-1.5 min-w-[160px]">
                                {cEmail && (
                                    <div className="flex items-center gap-2 text-slate-400 text-xs md:text-sm hover:text-white transition-colors" onClick={e => e.stopPropagation()}>
                                        <Mail size={14} className="text-slate-600 shrink-0" />
                                        <a href={`mailto:${cEmail}`} className="truncate block max-w-[200px]" title={cEmail}>{cEmail}</a>
                                    </div>
                                )}
                                {cPhone && (
                                    <div className="flex items-center gap-2 text-slate-400 text-xs md:text-sm hover:text-white transition-colors" onClick={e => e.stopPropagation()}>
                                        <Phone size={14} className="text-slate-600 shrink-0" />
                                        <a href={`tel:${cPhone}`} >{cPhone}</a>
                                    </div>
                                )}
                                {(!cEmail && !cPhone) && (
                                    <span className="text-[9px] text-slate-600 italic uppercase tracking-widest font-bold">No Contact Data</span>
                                )}
                            </div>
                        </td>

                        <td className="py-3 px-6 md:px-8">
                            <div className="flex items-center gap-3 text-slate-400 font-medium whitespace-nowrap text-xs md:text-sm">
                                <Building2 size={14} className="text-slate-600 shrink-0" />
                                <span className="truncate max-w-[180px] block" title={companies.find(c => c.id === client.company_id || c.id === client.companyId)?.name || client.companyName || 'Private Account'}>
                                  {companies.find(c => c.id === client.company_id || c.id === client.companyId)?.name || client.companyName || 'Private Account'}
                                </span>
                            </div>
                        </td>

                        <td className="py-3 px-6 md:px-8">
                            <span className={`text-[9px] md:text-[10px] font-black px-3 md:px-4 py-1.5 rounded-full border uppercase tracking-[0.15em] whitespace-nowrap ${client.type === 'PRODUCTION' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-slate-800 text-slate-500 border-white/5'}`}>
                                {client.productionStage || client.salesStage || 'Inquiry'}
                            </span>
                        </td>

                        <td className="py-3 px-6 md:px-8 text-right">
                          <div className="flex justify-end items-center gap-4">
                            {/* ⚡ UPGRADED: Stop propagation so editing doesn't open the view modal */}
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingContact({ ...client, email: client.email || client.clientEmail, phone: client.phone || client.clientPhone });
                                }} 
                                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-slate-800 rounded-lg md:rounded-xl text-slate-400 hover:text-brand-neonBlue hover:bg-slate-700 transition-all border border-white/5 shadow-lg" 
                                title="Personnel Profile & Schedule"
                            >
                              <Edit3 size={14}/>
                            </button>
                            <ChevronRight size={20} className="text-slate-600 group-hover:text-white transition-colors" />
                          </div>
                        </td>

                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      };

      const renderPipeline = () => {
          
        // ⚡ BRING THE ACTION TYPES HELPER HERE SO THE BOARD CAN USE IT
        const getActionTypes = (item: any) => {
          const stage = String(item.stage || item.salesStage || item.status || '').toUpperCase();
          const badges: {label: string, color: string}[] = [];

          if (stage === 'INQUIRY') {
              badges.push({ label: 'New Lead', color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' });
              return badges;
          }
          if (stage === 'POST_EVENT') {
              badges.push({ label: 'Awaiting Wrap-Up', color: 'text-indigo-400 border-indigo-500/30 bg-indigo-500/10' });
          }

          // ⚡ FIX: Only show Staffing and Prep warnings if the job is actually Approved/Active!
          const activeProductionStages = ['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT'];
          if (activeProductionStages.includes(stage)) {
              const projectShifts = allShifts.filter(s => s.lead_id === item.id);
              let hasPendingApproval = false;
              let hasMissingCrew = false;
              let hasAwaitingReply = false;
              let hasMissingGear = false; // ⚡ NEW

              for (const shift of projectShifts) {
                  const crew = shift.staff_roster?.crew || [];
                  const required = shift.staffing_source==='PHASES'?(shift.staff_roster?.required_count??0):(shift.staff_roster?.required_count||1);
                  const confirmed = crew.filter((c: any) => c.status === 'CONFIRMED').length;

                  if (crew.some((c: any) => c.status === 'ACCEPTED')) hasPendingApproval = true;
                  if (confirmed < required || (!crew.length && required>0)) hasMissingCrew = true;
                  if (crew.some((c: any) => c.status === 'PENDING')) hasAwaitingReply = true;
                  
                  // ⚡ Check if gear needs confirming
                  if (equipmentConfirmationQueue([item], [shift]).length > 0) {
                      hasMissingGear = true;
                  }
              }

              if (hasMissingGear) badges.push({ label: 'Gear to Confirm', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10 shadow-[0_0_10px_rgba(249,115,22,0.2)]' }); // ⚡ NEW
              if (hasPendingApproval) badges.push({ label: 'To Approve', color: 'text-brand-neonBlue border-brand-neonBlue/30 bg-brand-neonBlue/10' });
              if (hasMissingCrew) badges.push({ label: 'Short Staffed', color: 'text-rose-400 border-rose-500/30 bg-rose-500/10' });
              if (hasAwaitingReply) badges.push({ label: 'Awaiting Reply', color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' });

              const prepTasks = globalTasks.filter(t => t.lead_id === item.id && t.title?.includes('Prep Gear'));
              if (prepTasks.length > 0) {
                  const allPrepped = prepTasks.every(t => t.status === 'COMPLETED');
                  if (allPrepped) {
                      badges.push({ label: 'Gear Ready', color: 'text-green-400 border-green-500/30 bg-green-500/10 shadow-[0_0_10px_rgba(34,197,94,0.2)]' });
                  } else {
                      badges.push({ label: 'Prep Pending', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' });
                  }
              }
          }
          return badges;
        };
    
        // ⚡ NEW: Dynamically add the Graveyard stage to the board if toggled!
        const visibleStages = [...UNIFIED_STAGES];
        if (showPipelineGraveyard) {
            visibleStages.push({ id: 'WITHDRAWN', label: 'Withdrawn', type: 'SALES' });
        }

        return (
          <div className="animate-fade-in-up h-full flex flex-col space-y-8">
            <div className="flex justify-between items-end">
              <div>
                <h1 className="type-page-title text-white">Unified Pipeline</h1>
                <p className="text-slate-500 font-bold uppercase tracking-[0.2em] text-[10px] mt-1">Global Project Logistics</p>
              </div>
              <button 
                  onClick={() => setShowPipelineGraveyard(!showPipelineGraveyard)}
                  className={`px-4 py-2 rounded-xl type-button text-xs transition-colors border ${showPipelineGraveyard ? 'bg-slate-700 text-white border-slate-600' : 'bg-transparent text-slate-500 border-slate-800 hover:text-white hover:border-slate-600'}`}
              >
                  {showPipelineGraveyard ? 'Hide Withdrawn' : 'View Withdrawn'}
              </button>
            </div>
            
            <div className="flex-1 overflow-x-auto pb-10 custom-scrollbar" ref={pipelineScrollContainer}>
              <div className="flex gap-8 min-w-max h-full pt-2">
              {visibleStages.map(stage => {
                const stageItems = crmItems.filter(i => {
                                      const status = String((i as any).salesStage || (i as any).productionStage || (i as any).status || '').toUpperCase();
                                      
                                      // If looking at active board, hide dead items
                                      if (!showPipelineGraveyard && (status === 'WITHDRAWN' || status === 'ARCHIVED')) return false; 
                                      // If looking at graveyard, ONLY show dead items
                                      if (stage.id === 'WITHDRAWN') return ['WITHDRAWN', 'ARCHIVED'].includes(status);
                                      
                                      return status === String(stage.id).toUpperCase();
                                  }).sort((a: any, b: any) => {
                    const dateA = a.eventDate && a.eventDate !== 'TBD' ? new Date(a.eventDate).getTime() : Infinity;
                    const dateB = b.eventDate && b.eventDate !== 'TBD' ? new Date(b.eventDate).getTime() : Infinity;
                    return dateA - dateB;
                });

                const columnTotal = stageItems.reduce((sum, item) => {
                    const leadQuotes = quotes.filter(q => q.linkedCrmItemId === item.id || (q as any).lead_id === item.id);
                    if (stage.id === 'LOST' || stage.id === 'WITHDRAWN') return sum + (Number(item.value) || 0);
                    const hasQuotes = leadQuotes.length > 0;
                    const hasActiveQuotes = leadQuotes.some(q => !['WITHDRAWN', 'REJECTED', 'ARCHIVED'].includes(String(q.status).toUpperCase()));
                    if (hasQuotes && !hasActiveQuotes) return sum;
                    return sum + (Number(item.value) || 0);
                }, 0);

                return (
                    <div key={stage.id} className={`w-[340px] flex flex-col h-full rounded-[40px] border backdrop-blur-xl shadow-inner ${stage.id === 'WITHDRAWN' ? 'bg-slate-900/10 border-dashed border-white/10 opacity-70' : 'bg-slate-900/30 border-white/5'}`}>
                    <div className={`type-label flex items-center justify-between border-b border-white/5 p-7 ${stage.id === 'WITHDRAWN' ? 'text-slate-500' : stage.type === 'SALES' ? 'text-blue-400' : 'text-brand-neonPink'}`}>
                        <div>
                        {stage.label} 
                        {columnTotal > 0 && (
                            <div className="type-numeric text-[11px] mt-1 opacity-70 text-white tracking-normal">
                                ${columnTotal.toLocaleString()}
                            </div>
                        )}
                        </div>
                        <span className="bg-white/5 px-4 py-1.5 rounded-full text-white type-numeric text-[11px] border border-white/5">{stageItems.length}</span>
                    </div>
                    <div className="p-5 space-y-5 overflow-y-auto flex-1 custom-scrollbar min-h-[400px]">
                        {stageItems.map(item => (
                            <div key={item.id} className="bg-slate-800/60 p-6 rounded-[32px] border border-white/5 hover:border-brand-neonBlue/40 transition-all duration-500 cursor-pointer group shadow-2xl relative overflow-hidden" onClick={() => handleViewInquiry(item)}>
                            <div className="mb-4">
                                <span className="font-bold text-white text-lg group-hover:text-brand-neonBlue transition-colors block line-clamp-1" title={item.eventName || 'Unnamed Project'}>
                                {item.eventName || (item as any).project_ref || 'Unnamed Project'}
                                </span>
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block mt-1 line-clamp-1">
                                {item.firstName} {item.lastName} <span className="text-brand-neonPink mx-1">•</span> {item.companyName || 'Private Inquiry'}
                                </span>
                                
                                <div className="flex flex-wrap gap-2 mt-3">
                                <SubmissionTypeBadge submissionType={(item as any).submission_type} compact />
                                {getActionTypes(item).map((badge: any, idx: number) => (
                                    <span key={idx} className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${badge.color}`}>
                                        {badge.label}
                                    </span>
                                ))}
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold uppercase mb-6">
                                <MapPin size={12} className="text-slate-600" />
                                {item.eventVenue || 'Venue TBD'}
                            </div>
        
                            <div className="flex justify-between items-center pt-5 border-t border-white/5">
                                <div className="flex flex-col">
                                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Event Date</span>
                                <span className="text-[11px] text-white font-bold">{item.eventDate ? new Date(item.eventDate).toLocaleDateString('en-NZ', {month:'short', day:'numeric'}) : 'TBD'}</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Value</span>
                                    <span className="text-sm font-bold text-brand-neonBlue font-mono">${(item.value || 0).toLocaleString()}</span>
                                </div>
                            </div>
        
                            <div className="mt-6" onClick={(e) => e.stopPropagation()}>
                                <select 
                                    value={stage.id}
                                    onChange={async (e) => {
                                        const newStage = e.target.value;
                                        try {
                                            const { error } = await supabase.from('event_leads').update({ status: newStage }).eq('id', item.id);
                                            if (error) throw error;
                                            await fetchLeads();
                                        } catch (err: any) {
                                            alert(`Movement Error: ${err.message}`);
                                        }
                                    }}
                                    className={`w-full type-button text-xs px-3 py-2 rounded-xl border transition-all appearance-none cursor-pointer outline-none shadow-inner bg-slate-900/50 hover:bg-slate-800 focus:ring-1 focus:ring-brand-neonBlue text-center ${
                                        stage.id === 'WITHDRAWN' ? 'text-slate-500 border-slate-600' :
                                        item.type === 'SALES' ? 'text-blue-400 border-blue-500/20' : 'text-brand-neonPink border-brand-neonPink/20'
                                    }`}
                                >
                                    <optgroup label="Sales Pipeline">
                                        <option value="INQUIRY">Inquiry</option>
                                        <option value="DISCOVERY">Discovery</option>
                                        <option value="PROPOSAL">Proposal</option>
                                        <option value="LOST">Lost</option>
                                        <option value="WITHDRAWN">Withdrawn</option>
                                    </optgroup>
                                    <optgroup label="Production Pipeline">
                                        <option value="PRE_PRODUCTION">Pre-Production</option>
                                        <option value="STAGING">Staging</option>
                                        <option value="LIVE_EVENT">Live Event</option>
                                        <option value="POST_EVENT">Post-Event</option>
                                        <option value="COMPLETED">Completed</option>
                                    </optgroup>
                                </select>
                            </div>
                            </div>
                        ))}
                    </div>
                    </div>
                );
              })}
              </div>
            </div>
          </div>
        );
      };

      // ⚡ CALENDAR ENGINE: HEATMAP & DRILL-DOWN VIEW ⚡
      const renderCalendar = () => {
        const getEventsForDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dStr = `${year}-${month}-${day}`;
          
          // 1. Find any standalone leads that fall on this day (AND ARE NOT DEAD/LOST/WITHDRAWN)
          const leadsOnDate = crmItems.filter((item: any) => {
            const dateVal = item.eventDate || (item as any).event_date;
            const stage = String(item.status || item.salesStage || item.productionStage || '').toUpperCase();
            const isDead = ['LOST', 'REJECTED', 'WITHDRAWN', 'CANCELED'].includes(stage);
            
            return !isDead && dateVal && typeof dateVal === 'string' && dateVal.startsWith(dStr) && item.type !== 'CONTACT';
          });

          // 2. Find any specific multi-day shifts that fall on this day (AND BELONG TO AN ACTIVE JOB)
          const shiftsOnDate = allShifts.filter((s: any) => {
            if (!s.event_date || !s.event_date.startsWith(dStr)) return false;
            
            // Check if the parent lead is actually alive
            const parentLead = crmItems.find(i => i.id === s.lead_id);
            if (!parentLead) return false;
            
            const stage = String((parentLead as any).status || parentLead.salesStage || parentLead.productionStage || '').toUpperCase();
            const isDead = ['LOST', 'REJECTED', 'WITHDRAWN', 'CANCELED'].includes(stage);
            
            return !isDead;
          });

          // 3. Dress up the shifts so they look like standard events on the calendar
          const mappedShifts = shiftsOnDate.map((shift: any) => {
            const parentLead: any = crmItems.find(i => i.id === shift.lead_id) || {}; 
            
            // ⚡ SMART CHECK: Is this shift falling on the exact same day as the master job?
            const parentDateStr = parentLead.eventDate || parentLead.event_date;
            const isMainDay = parentDateStr && parentDateStr.startsWith(dStr);

            return {
              ...parentLead,
              id: `shift-${shift.id}`, 
              originalLeadId: shift.lead_id, 
              eventName: shift.shift_title || parentLead.eventName || 'Unnamed Shift',
              eventDate: shift.event_date,
              eventVenue: shift.venue_location || parentLead.eventVenue || 'Venue TBD',
              isShift: true, 
              isMainDay: isMainDay // ⚡ Pass this flag to the visual engine!
            };
          });

          // 4. Remove duplicate master leads if a shift already exists for it on this exact day
          const shiftLeadIds = mappedShifts.map(s => s.originalLeadId);
          const finalLeads = leadsOnDate.filter(lead => !shiftLeadIds.includes(lead.id));

          // 5. Combine them all together
          return [...finalLeads, ...mappedShifts];
        };

        const getTasksForDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dStr = `${year}-${month}-${day}`;
            
            return globalTasks.filter((t: any) => {
              const dateVal = t.due_date;
              return dateVal && typeof dateVal === 'string' && dateVal.startsWith(dStr) && t.status !== 'COMPLETED';
            });
        };
    
        const handleAdjustDate = (direction: 'next' | 'prev') => {
            const newDate = new Date(calendarDate);
            const amount = direction === 'next' ? 1 : -1;
    
            if (calendarView === 'month') newDate.setMonth(newDate.getMonth() + amount);
            else if (calendarView === 'week') newDate.setDate(newDate.getDate() + (amount * 7));
            else if (calendarView === 'day') newDate.setDate(newDate.getDate() + amount);
    
            setCalendarDate(newDate);
        };
    
        const renderMonth = () => {
          const daysInMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0).getDate();
          const rawStartDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1).getDay();
          const startDay = rawStartDay === 0 ? 6 : rawStartDay - 1;
          
          const days = [];
          for (let i = 0; i < startDay; i++) days.push(null);
          for (let i = 1; i <= daysInMonth; i++) days.push(i);
       
          return (
              <div className="flex flex-col bg-slate-900/40 border border-white/10 rounded-[24px] md:rounded-[40px] overflow-hidden backdrop-blur-2xl shadow-2xl mt-6 lg:mt-10">
                  <div className="grid grid-cols-7 bg-slate-950/80 border-b border-white/10">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                          <div key={d} className="py-3 md:p-5 text-center text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.2em] md:tracking-[0.3em]">
                              {d.substring(0, 3)}
                          </div>
                      ))}
                  </div>
       
                  <div className="grid grid-cols-7 flex-1 auto-rows-fr">
                      {days.map((day, i) => {
                          if (!day) return <div key={`empty-${i}`} className="bg-slate-950/20 border-r border-b border-white/5 min-h-[80px] md:min-h-[120px]" />;
                          
                          const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
                          const events = getEventsForDate(date);
                          const dayTasks = getTasksForDate(date);
                          const isToday = date.toDateString() === new Date().toDateString();
                          
                          return (
                              <div 
                                  key={day} 
                                  onClick={() => { setCalendarDate(date); setCalendarView('day'); }} 
                                  className={`border-r border-b p-1.5 md:p-3 min-h-[80px] md:min-h-[120px] transition-all duration-300 cursor-pointer group flex flex-col items-center md:items-start ${isToday ? 'bg-brand-neonBlue/5 border-brand-neonBlue/30 hover:bg-brand-neonBlue/10' : 'border-white/5 hover:bg-white/5'}`}
                              >
                                  <span className={`type-numeric mb-1 flex shrink-0 items-center justify-center text-sm md:mb-2 md:text-base ${isToday ? 'w-6 h-6 md:w-8 md:h-8 bg-brand-neonBlue text-slate-950 rounded-full shadow-neon' : 'text-slate-400 group-hover:text-white'}`}>
                                      {day}
                                  </span>
                                  
                                  <div className="w-full flex flex-col gap-1 md:gap-1.5 items-center md:items-start mt-1 flex-1 overflow-y-auto custom-scrollbar pr-1 pb-1">
                                      {/* ⚡ UPGRADED: RENDER ACTUAL EVENT NAMES */}
                                      {events.map((ev: any, idx: number) => {
                                          const isConfirmed = !['INQUIRY', 'DISCOVERY', 'PROPOSAL', 'DRAFT'].includes(String(ev.status).toUpperCase());
                                          const isShift = ev.isShift && !ev.isMainDay;
                                          
                                          let bgColor = "bg-yellow-500/10 border-yellow-500/20 text-yellow-500";
                                          let dotColor = "bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]";
                                          let statusLabel = "Hold";
  
                                          if (isShift) {
                                              bgColor = "bg-brand-neonPink/10 border-brand-neonPink/20 text-brand-neonPink";
                                              dotColor = "bg-brand-neonPink shadow-neon-pink";
                                              statusLabel = "Shift";
                                          } else if (isConfirmed) {
                                              bgColor = "bg-brand-neonBlue/10 border-brand-neonBlue/20 text-brand-neonBlue";
                                              dotColor = "bg-brand-neonBlue shadow-neon";
                                              statusLabel = "Confirmed";
                                          }
  
                                          const eventName = ev.eventName || ev.project_ref || 'Unnamed Event';
                                          const relatedQuote = quotes.find((q: any) => q.linkedCrmItemId === ev.id || q.lead_id === ev.id);
                                          const timeString = relatedQuote?.eventStartTime ? `${relatedQuote.eventStartTime} - ${relatedQuote.eventEndTime || 'Late'}` : 'Time TBD';
                                          const tooltip = `${eventName}\nStatus: ${statusLabel}\nVenue: ${ev.eventVenue || 'TBD'}\nTime: ${timeString}`;
  
                                          return (
                                              <div key={`ev-${ev.id}-${idx}`} title={tooltip} className={`w-full flex items-center gap-1.5 md:gap-2 px-1.5 py-1.5 md:px-2 md:py-2 rounded-md md:rounded-lg border ${bgColor} transition-colors hover:bg-slate-800 overflow-hidden`}>
                                                  <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full shrink-0 ${dotColor}`}></div>
                                                  <span className="text-[10px] md:text-xs font-bold leading-tight truncate">{eventName}</span>
                                              </div>
                                          );
                                      })}
  
                                     {/* ⚡ UPGRADED: RENDER ACTUAL TASK NAMES OR SUMMARY (GREEN THEME) */}
{calendarTaskMode === 'detailed' ? (
    dayTasks.map((t: any, idx: number) => {
        const tooltip = `Task: ${t.title}\nAssignee: ${t.assignee?.full_name || 'Unassigned'}\nStatus: ${t.status}`;
        return (
            <div key={`task-${t.id}-${idx}`} title={tooltip} className="w-full flex items-center gap-1.5 md:gap-2 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-1.5 md:px-2 md:py-2 rounded-md md:rounded-lg text-emerald-400 transition-colors hover:bg-emerald-500/20 overflow-hidden mt-0.5">
                <CheckCircle size={10} className="shrink-0 text-emerald-500" />
                <span className="text-[10px] md:text-xs font-bold leading-tight truncate">{t.title}</span>
            </div>
        );
    })
) : (
    dayTasks.length > 0 && (
        <div title={`${dayTasks.length} tasks due`} className="w-full flex items-center justify-center md:justify-start gap-1.5 md:gap-2 bg-slate-900 border border-emerald-500/30 px-1.5 py-1 md:px-2 md:py-1.5 rounded-md md:rounded-lg text-emerald-500 transition-colors mt-0.5">
            <CheckCircle size={10} className="shrink-0" />
            <span className="type-button text-xs leading-tight truncate">{dayTasks.length} Tasks Due</span>
        </div>
    )
)}
                                </div>
                              </div>
                          );
                      })}
                  </div>
              </div>
          );
        };
      
        const renderWeek = () => {
          const startOfWeek = new Date(calendarDate);
          const day = startOfWeek.getDay();
          const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
          startOfWeek.setDate(diff);
          
          const weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return d;
          });
  
          return (
              <div className="flex flex-col bg-slate-900/40 border border-white/10 rounded-[24px] md:rounded-[40px] overflow-hidden backdrop-blur-2xl shadow-2xl mt-6 lg:mt-10">
                  <div className="grid grid-cols-7 bg-slate-950/80 border-b border-white/10">
                      {weekDays.map(d => (
                          <div key={d.toString()} className="py-3 md:p-5 text-center text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.2em] md:tracking-[0.3em]">
                              {d.toLocaleDateString('en-NZ', { weekday: 'short' })}
                          </div>
                      ))}
                  </div>
                  <div className="grid grid-cols-7">
                      {weekDays.map((date, i) => {
                          const events = getEventsForDate(date);
                          const dayTasks = getTasksForDate(date);
                          const isToday = date.toDateString() === new Date().toDateString();
                          
                          return (
                              <div 
                                  key={i} 
                                  onClick={() => { setCalendarDate(date); setCalendarView('day'); }} 
                                  className={`border-r border-white/5 p-1.5 md:p-4 min-h-[150px] md:min-h-[250px] transition-all duration-300 cursor-pointer group flex flex-col items-center md:items-start ${isToday ? 'bg-brand-neonBlue/5 hover:bg-brand-neonBlue/10' : 'hover:bg-white/5'}`}
                              >
                                  <span className={`type-numeric mb-2 flex shrink-0 items-center justify-center text-sm md:mb-4 md:text-xl ${isToday ? 'w-6 h-6 md:w-10 md:h-10 bg-brand-neonBlue text-slate-950 rounded-full shadow-neon' : 'text-slate-400 group-hover:text-white'}`}>
                                      {date.getDate()}
                                  </span>
                                  
                                  <div className="w-full flex flex-col gap-1 md:gap-1.5 items-center md:items-start mt-1 flex-1 overflow-y-auto custom-scrollbar pr-1 pb-1">
                                      {/* ⚡ UPGRADED: RENDER ACTUAL EVENT NAMES */}
                                      {events.map((ev: any, idx: number) => {
                                          const isConfirmed = !['INQUIRY', 'DISCOVERY', 'PROPOSAL', 'DRAFT'].includes(String(ev.status).toUpperCase());
                                          const isShift = ev.isShift && !ev.isMainDay;
                                          
                                          let bgColor = "bg-yellow-500/10 border-yellow-500/20 text-yellow-500";
                                          let dotColor = "bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]";
                                          let statusLabel = "Hold";
  
                                          if (isShift) {
                                              bgColor = "bg-brand-neonPink/10 border-brand-neonPink/20 text-brand-neonPink";
                                              dotColor = "bg-brand-neonPink shadow-neon-pink";
                                              statusLabel = "Shift";
                                          } else if (isConfirmed) {
                                              bgColor = "bg-brand-neonBlue/10 border-brand-neonBlue/20 text-brand-neonBlue";
                                              dotColor = "bg-brand-neonBlue shadow-neon";
                                              statusLabel = "Confirmed";
                                          }
  
                                          const eventName = ev.eventName || ev.project_ref || 'Unnamed Event';
                                          const relatedQuote = quotes.find((q: any) => q.linkedCrmItemId === ev.id || q.lead_id === ev.id);
                                          const timeString = relatedQuote?.eventStartTime ? `${relatedQuote.eventStartTime} - ${relatedQuote.eventEndTime || 'Late'}` : 'Time TBD';
                                          const tooltip = `${eventName}\nStatus: ${statusLabel}\nVenue: ${ev.eventVenue || 'TBD'}\nTime: ${timeString}`;
  
                                          return (
                                              <div key={`ev-${ev.id}-${idx}`} title={tooltip} className={`w-full flex items-center gap-1.5 md:gap-2 px-1.5 py-1.5 md:px-2 md:py-2 rounded-md md:rounded-lg border ${bgColor} transition-colors hover:bg-slate-800 overflow-hidden`}>
                                                  <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full shrink-0 ${dotColor}`}></div>
                                                  <span className="text-[10px] md:text-xs font-bold leading-tight truncate">{eventName}</span>
                                              </div>
                                          );
                                      })}
  
                                      {/* ⚡ UPGRADED: RENDER ACTUAL TASK NAMES OR SUMMARY (GREEN THEME) */}
{calendarTaskMode === 'detailed' ? (
    dayTasks.map((t: any, idx: number) => {
        const tooltip = `Task: ${t.title}\nAssignee: ${t.assignee?.full_name || 'Unassigned'}\nStatus: ${t.status}`;
        return (
            <div key={`task-${t.id}-${idx}`} title={tooltip} className="w-full flex items-center gap-1.5 md:gap-2 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-1.5 md:px-2 md:py-2 rounded-md md:rounded-lg text-emerald-400 transition-colors hover:bg-emerald-500/20 overflow-hidden mt-0.5">
                <CheckCircle size={10} className="shrink-0 text-emerald-500" />
                <span className="text-[10px] md:text-xs font-bold leading-tight truncate">{t.title}</span>
            </div>
        );
    })
) : (
    dayTasks.length > 0 && (
        <div title={`${dayTasks.length} tasks due`} className="w-full flex items-center justify-center md:justify-start gap-1.5 md:gap-2 bg-slate-900 border border-emerald-500/30 px-1.5 py-1 md:px-2 md:py-1.5 rounded-md md:rounded-lg text-emerald-500 transition-colors mt-0.5">
            <CheckCircle size={10} className="shrink-0" />
            <span className="type-button text-xs leading-tight truncate">{dayTasks.length} Tasks Due</span>
        </div>
    )
)}
                                </div>
                              </div>
                          );
                      })}
                  </div>
              </div>
          );
        };
    
        const renderDay = () => {
          const events = getEventsForDate(calendarDate);
          const dayTasks = getTasksForDate(calendarDate);

          return (
            <div className="max-w-5xl mx-auto space-y-6 lg:space-y-8 mt-6 lg:mt-10 animate-fade-in-up">
              
              {/* Drill-down Header */}
              <div className="bg-slate-900/80 border border-white/10 p-6 lg:p-10 rounded-3xl lg:rounded-[48px] backdrop-blur-3xl shadow-3xl">
                <button onClick={() => setCalendarView('month')} className="mb-6 flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-brand-neonBlue hover:text-white transition-colors bg-brand-neonBlue/10 hover:bg-white/10 px-4 py-2 rounded-full w-fit">
                    <ChevronLeft size={14}/> Back to Month
                </button>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 lg:gap-8">
                    <h4 className="type-heading text-3xl text-white md:text-4xl lg:text-5xl">{calendarDate.toLocaleDateString('en-NZ', { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
                    <div className="bg-slate-950 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl border border-white/5 font-mono text-brand-neonBlue font-bold text-sm lg:text-lg flex gap-4 w-full md:w-auto justify-center shadow-inner">
                        <span>{events.filter((ev: any) => ev.type === 'SALES').length} Bookings</span>
                        <span className="text-brand-neonPink border-l border-white/10 pl-4">{dayTasks.length} Tasks</span>
                    </div>
                </div>
              </div>

              {dayTasks.map((t: any) => {
                // ⚡ SMART SHIFT DATE CALCULATOR
                let displayEventDate = t.lead?.event_date;
                if (t.task_type === 'AUTO_LOGISTICS' && t.due_date) {
                    const d = new Date(t.due_date);
                    if (t.title?.includes('Prep Gear')) {
                        d.setDate(d.getDate() + 1);
                        displayEventDate = d.toISOString();
                    } else if (t.title?.includes('Return & Inspect')) {
                        d.setDate(d.getDate() - 1);
                        displayEventDate = d.toISOString();
                    }
                }

                return (
                <GlassCard key={t.id} className="p-5 lg:p-8 flex items-center justify-between group cursor-pointer border-brand-neonPink/30 hover:shadow-neon-pink transition-all duration-500 rounded-2xl lg:rounded-[32px]" onClick={() => setActiveTab('tasks')}>
                  <div className="flex items-center gap-4 lg:gap-6">
                    <div className="text-brand-neonPink bg-brand-neonPink/10 p-3 lg:p-4 rounded-xl lg:rounded-2xl border border-brand-neonPink/20 shrink-0">
                        <CheckCircle className="w-5 h-5 lg:w-7 lg:h-7" />
                    </div>
                    <div>
                      <h5 className="text-sm md:text-lg lg:text-xl font-bold text-white line-clamp-2 leading-tight mb-1">{t.title}</h5>
                      <div className="flex items-center gap-2">
                          <p className="text-[9px] lg:text-xs font-black uppercase tracking-widest text-brand-neonPink">Scheduled Action Item</p>
                          {displayEventDate && (
                              <span className="text-[9px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded border border-white/5 font-bold uppercase tracking-widest">
                                  For Event: {new Date(displayEventDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                              </span>
                          )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              )})}

{events.filter((ev: any) => ev.type === 'SALES').map((ev: any) => {
                // ⚡ 1. Determine if the job is still pending approval
                const isUnconfirmed = ['INQUIRY', 'DISCOVERY', 'PROPOSAL', 'DRAFT'].includes(String(ev.status).toUpperCase());
                
                return (
                <GlassCard 
                    key={ev.id} 
                    className={`p-5 lg:p-8 flex flex-col md:flex-row md:items-center justify-between group cursor-pointer transition-all duration-700 rounded-2xl lg:rounded-[40px] gap-4 lg:gap-0 ${isUnconfirmed ? 'hover:border-yellow-500/50' : 'hover:border-brand-neonBlue/50'}`} 
                    onClick={() => {
                        if (ev.isShift && ev.originalLeadId) {
                            const realLead = crmItems.find(i => i.id === ev.originalLeadId);
                            if (realLead) handleViewInquiry(realLead);
                        } else {
                            handleViewInquiry(ev);
                        }
                    }}
                >
                  <div className="flex items-start md:items-center gap-4 lg:gap-8">
                    <div className="text-center min-w-[60px] lg:min-w-[100px] shrink-0 pt-2 md:pt-0">
                      <Clock className={`w-5 h-5 lg:w-7 lg:h-7 mx-auto mb-2 lg:mb-3 opacity-30 group-hover:opacity-100 transition-opacity ${isUnconfirmed ? 'text-yellow-500' : 'text-brand-neonBlue'}`} />
                      <p className="text-[9px] lg:text-xs font-bold text-slate-500 uppercase tracking-widest hidden md:block">
                          {isUnconfirmed ? 'Tentative' : 'Confirmed'}
                      </p>
                    </div>
                    
                    {/* ⚡ 2. Color the main vertical bar! */}
                    <div className={`w-1.5 lg:w-2 h-16 lg:h-20 rounded-full shrink-0 ${
                        (ev.isShift && !ev.isMainDay) ? 'bg-brand-neonPink shadow-neon-pink' : 
                        isUnconfirmed ? 'bg-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.4)]' : 
                        'bg-brand-neonBlue shadow-neon'
                    }`} />
                    
                    <div className="min-w-0 pr-2">
                      <div className="flex flex-wrap items-center gap-2 lg:gap-4 mb-1 lg:mb-2">
                      <span className={`text-[8px] lg:text-[10px] bg-white/5 border border-white/10 px-2 lg:px-3 py-0.5 lg:py-1 rounded-full font-black uppercase tracking-widest ${
                            (ev.isShift && !ev.isMainDay) ? 'text-brand-neonPink' : 
                            isUnconfirmed ? 'text-yellow-500' : 'text-slate-400'
                        }`}>
                            {(ev.isShift && !ev.isMainDay) ? '📍 SHIFT' : isUnconfirmed ? '🟡 DRAFT' : `Job #${ev.id.slice(-4)}`}
                        </span>
                        <span className={`${isUnconfirmed ? 'text-yellow-500/70' : 'text-green-400'} font-mono font-bold text-xs lg:text-sm`}>${(ev.value || 0).toLocaleString()}</span>
                      </div>
                      <h5 className={`text-lg md:text-xl lg:text-2xl font-bold text-white transition-all tracking-tight leading-tight line-clamp-2 ${isUnconfirmed ? 'group-hover:text-yellow-500' : 'group-hover:text-brand-neonBlue'}`}>{ev.eventName || 'Untitled Event'}</h5>
                      <p className="text-[10px] md:text-sm lg:text-base text-slate-400 font-medium mt-1 truncate">{ev.firstName} {ev.lastName} <span className="mx-1">•</span> {ev.companyName || 'Private Account'}</p>
                    </div>
                  </div>
                  <ChevronRight size={24} className={`opacity-0 group-hover:opacity-100 transition-all hidden md:block shrink-0 ${isUnconfirmed ? 'text-yellow-500' : 'text-brand-neonBlue'}`} />
                </GlassCard>
              )})}
              
              {events.filter((ev: any) => ev.type === 'SALES').length === 0 && dayTasks.length === 0 && (
                  <div className="text-center py-16 lg:py-24 text-slate-500 font-bold uppercase tracking-widest text-[10px] border-2 border-dashed border-white/5 rounded-3xl lg:rounded-[40px]">
                      No events or tasks scheduled
                  </div>
              )}
            </div>
          );
        };
    
        return (
          <div className="animate-fade-in-up h-full flex flex-col pb-20">
            
            {/* ⚡ CONTROLS HEADER */}
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 xl:gap-10 mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10 w-full xl:w-auto">
                <h1 className="type-page-title shrink-0 text-white">
                    {calendarView === 'day' ? calendarDate.toLocaleDateString('en-NZ', {month:'short', day:'numeric'}) : calendarDate.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}
                </h1>
                
                <div className="flex bg-slate-900/80 rounded-2xl md:rounded-[28px] p-1 md:p-2 border border-white/10 backdrop-blur-xl shadow-2xl w-full md:w-auto">
                  <button onClick={() => handleAdjustDate('prev')} className="p-2 md:p-3 hover:bg-white/10 rounded-xl md:rounded-2xl transition-all text-slate-400 hover:text-white flex-1 md:flex-none flex justify-center"><ChevronLeft className="w-5 h-5 md:w-7 md:h-7"/></button>
                  <button onClick={() => setCalendarDate(new Date())} className="px-4 md:px-8 text-[10px] md:text-xs font-bold text-slate-500 uppercase hover:text-brand-neonBlue tracking-[0.2em] md:tracking-[0.4em] transition-all flex-1 md:flex-none">Today</button>
                  <button onClick={() => handleAdjustDate('next')} className="p-2 md:p-3 hover:bg-white/10 rounded-xl md:rounded-2xl transition-all text-slate-400 hover:text-white flex-1 md:flex-none flex justify-center"><ChevronRight className="w-5 h-5 md:w-7 md:h-7"/></button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
                {/* ⚡ NEW: TASK TOGGLE BUTTON */}
                <button 
                    onClick={() => setCalendarTaskMode(prev => prev === 'summary' ? 'detailed' : 'summary')}
                    className="flex items-center justify-center gap-2 px-4 py-3 md:py-0 md:px-6 bg-slate-900/80 rounded-xl md:rounded-[28px] border border-white/10 type-label text-slate-400 hover:text-white transition-all shadow-2xl"
                >
                    <CheckCircle size={14} className={calendarTaskMode === 'detailed' ? 'text-brand-neonPink' : 'text-slate-500'} />
                    {calendarTaskMode === 'detailed' ? 'Hide Tasks' : 'Show Tasks'}
                </button>

                <div className="flex bg-slate-900/80 rounded-2xl md:rounded-[28px] p-1 md:p-2 border border-white/10 backdrop-blur-xl w-full xl:w-auto">
                  {(['month', 'week', 'day'] as const).map((v) => (
                    <button key={v} onClick={() => setCalendarView(v)} className={`px-4 md:px-8 py-2 md:py-3 rounded-xl md:rounded-2xl type-button text-xs transition-all flex-1 xl:flex-none ${calendarView === v ? 'bg-brand-neonBlue text-slate-950 shadow-neon' : 'text-slate-500 hover:text-white'}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Render Selected View */}
            {calendarView === 'month' && renderMonth()}
            {calendarView === 'week' && renderWeek()}
            {calendarView === 'day' && renderDay()}
          </div>
        );
      };

      const renderProducts = () => {
        return (
          <div className="animate-fade-in-up mt-2">
            <div className="space-y-12">
              
              {/* --- HEADER & CONTROLS --- */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 lg:gap-8">
                <div>
                  <h1 className="type-page-title text-white">Inventory Core</h1>
                  <p className="type-label text-slate-500 mt-2 md:mt-4">Technical Assets & Specifications</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center">
                  <div className="flex bg-slate-900/80 rounded-xl md:rounded-[22px] p-1 md:p-1.5 border border-white/10 backdrop-blur-xl shadow-2xl w-full sm:w-auto">
                     <button 
                        onClick={() => setInventoryViewMode('grid')} 
                        className={`p-2 md:p-3 rounded-lg md:rounded-2xl transition-all flex items-center justify-center flex-1 sm:flex-none ${inventoryViewMode === 'grid' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}
                        title="Card View"
                     >
                        <LayoutGrid size={18} className="md:w-5 md:h-5" />
                     </button>
                     <button 
                        onClick={() => setInventoryViewMode('list')} 
                        className={`p-2 md:p-3 rounded-lg md:rounded-2xl transition-all flex items-center justify-center flex-1 sm:flex-none ${inventoryViewMode === 'list' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}
                        title="List View"
                     >
                        <ListIcon size={18} className="md:w-5 md:h-5" />
                     </button>
                  </div>
    
                  <Button onClick={handleNewProduct} className="bg-brand-neonBlue text-slate-950 font-bold px-6 py-3 md:px-10 md:py-4 rounded-xl md:rounded-[22px] shadow-neon w-full sm:w-auto whitespace-nowrap text-sm">
                    <Plus size={18} className="mr-2"/> New Asset Record
                  </Button>
                </div>
              </div>
    
              {/* --- GRID VIEW (CARDS) --- */}
              {inventoryViewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                  {products.map((product: any) => {
                    let imageArray = [];
                    try {
                      if (!product.image) {
                        imageArray = [];
                      } else if (typeof product.image === 'string') {
                        imageArray = product.image.startsWith('[') ? JSON.parse(product.image) : product.image.split(',');
                      } else if (Array.isArray(product.image)) {
                        imageArray = product.image;
                      }
                    } catch (e) {
                      imageArray = [];
                    }
                    
                    return (
                      <GlassCard key={product.id} className="p-0 overflow-hidden group hover:border-brand-neonBlue/50 transition-all duration-700 shadow-3xl rounded-[40px]">
                        <div className="h-72 bg-slate-800 relative overflow-hidden">
                          <div className="absolute inset-0 transition-transform duration-[3000ms] group-hover:scale-110">
                             <img 
                               src={imageArray[0] || undefined} 
                               className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity duration-1000" 
                               alt={product.name}
                             />
                             <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80" />
                          </div>
    
                          <div className="absolute bottom-8 left-8 right-8 z-10">
                            <div className="flex gap-2 mb-3">
                              {(product.tags || []).slice(0, 3).map((tag: string) => (
                                <span key={tag} className="text-[9px] bg-brand-neonBlue/20 border border-brand-neonBlue/30 px-3 py-1 rounded-full text-brand-neonBlue font-black uppercase tracking-widest backdrop-blur-md">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <h4 className="type-heading text-2xl text-white transition-colors group-hover:text-brand-neonBlue">{product.name}</h4>
                          </div>
                        </div>
    
                        <div className="p-8 bg-slate-900/50 space-y-6">
                          <p className="text-base text-slate-400 leading-relaxed line-clamp-2 font-medium italic opacity-70">
                            "{product.description || 'No technical specifications provided for this asset.'}"
                          </p>
                          <div className="flex items-center justify-between pt-6 border-t border-white/5">
                            <div className="flex flex-col">
                              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-1">Standard Rate</span>
                              <span className="text-white type-numeric text-2xl flex items-center gap-2">
                                  <DollarSign size={18} className="text-green-500" />
                                  {((product as any).base_price || product.price || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex gap-3">
                              <button onClick={() => handleEditProduct(product)} className="p-4 bg-slate-800 rounded-2xl text-slate-400 hover:text-white transition-all shadow-xl hover:bg-slate-700">
                                <Edit3 size={20}/>
                              </button>
                              <button onClick={() => handleDeleteProduct(product.id)} className="p-4 bg-slate-800 rounded-2xl text-red-400 hover:bg-red-500/20 transition-all shadow-xl">
                                <Trash2 size={20}/>
                              </button>
                            </div>
                          </div>
                        </div>
                      </GlassCard>
                    );
                  })}
                </div>
              )}
    
              {/* --- LIST VIEW (TABLE) ⚡ --- */}
              {inventoryViewMode === 'list' && (
                <div className="bg-slate-900/40 rounded-[48px] border border-white/5 overflow-hidden backdrop-blur-3xl shadow-[0_40px_100px_rgba(0,0,0,0.4)] relative">
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                      <thead className="bg-slate-950/80 text-slate-500 text-[10px] uppercase font-bold tracking-[0.3em]">
                        <tr>
                          <th className="p-8">Asset Profile</th>
                          <th className="p-8">Description</th>
                          <th className="p-8 text-right">Standard Rate</th>
                          <th className="p-8 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {products.map((product: any) => {
                          let imageArray = [];
                          try {
                            if (!product.image) imageArray = [];
                            else if (typeof product.image === 'string') imageArray = product.image.startsWith('[') ? JSON.parse(product.image) : product.image.split(',');
                            else if (Array.isArray(product.image)) imageArray = product.image;
                          } catch (e) { imageArray = []; }
    
                          return (
                            <tr key={product.id} className="hover:bg-brand-neonBlue/[0.02] text-slate-300 transition-all duration-500 group">
                              <td className="p-8">
                                <div className="flex items-center gap-6">
                                  <div className="w-16 h-16 bg-slate-800 rounded-[20px] overflow-hidden border border-white/5 group-hover:border-brand-neonBlue/30 transition-all shrink-0">
                                    {imageArray[0] ? (
                                      <img src={imageArray[0]} alt={product.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-brand-neonBlue"><Package size={20} /></div>
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-lg font-bold text-white tracking-tight group-hover:text-brand-neonBlue transition-colors">{product.name}</p>
                                    <div className="flex gap-2 mt-2">
                                      {(product.tags || []).slice(0, 3).map((tag: string) => (
                                        <span key={tag} className="text-[9px] bg-brand-neonBlue/10 border border-brand-neonBlue/20 px-2 py-0.5 rounded text-brand-neonBlue font-bold uppercase tracking-widest">
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-8 max-w-md">
                                <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 italic font-medium">
                                  {product.description || 'No technical specifications provided.'}
                                </p>
                              </td>
                              <td className="p-8 text-right type-numeric text-green-400 text-lg tabular-nums">
                                  ${((product as any).base_price || product.price || 0).toLocaleString()}
                              </td>
                              <td className="p-8">
                                <div className="flex justify-center gap-3 opacity-0 group-hover:opacity-100 transition-all">
                                  <button onClick={() => handleEditProduct(product)} className="p-3 bg-slate-800 rounded-2xl text-slate-400 hover:text-white transition-all shadow-xl hover:bg-slate-700">
                                    <Edit3 size={16}/>
                                  </button>
                                  <button onClick={() => handleDeleteProduct(product.id)} className="p-3 bg-red-500/10 rounded-2xl text-red-500 hover:bg-red-500 hover:text-white transition-all shadow-xl">
                                    <Trash2 size={16}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    {products.length === 0 && (
                        <div className="text-center py-20 text-slate-500 font-medium">
                            No assets currently in inventory.
                        </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      };
    
      const renderStaff = () => {
                // ⚡ BULLETPROOF INITIALS HELPER
              const getInitials = (name: string) => {
                try {
                    if (!name) return 'ID';
                    const cleanName = name.replace(/[^a-zA-Z\s]/g, '').trim();
                    const parts = cleanName.split(/\s+/);
                    return parts.map(p => p[0]).join('').substring(0, 2).toUpperCase() || 'ID';
                } catch (e) {
                    return 'ID';
                }
            };
        
        return (
          <>
            <div className="animate-fade-in-up mt-2">
              {/* Header stacks on mobile */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 md:mb-12 gap-6">
                <div>
                  <h1 className="type-page-title text-white">Team Roster</h1>
                  <p className="type-label text-slate-500 mt-4">Personnel Management & Deployments</p>
                </div>
                <Button onClick={() => handleNewStaff()} className="w-full sm:w-auto bg-brand-neonPink text-white shadow-neon-pink px-8 py-4 rounded-2xl font-bold transition-transform hover:scale-105">
                  <Plus size={20} className="mr-3"/> Add Core Crew Member
                </Button>
              </div>
              
              {/* ⚡ UPGRADED CORE TEAM ROSTER */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {staffList.map((staff: any) => (
                  <GlassCard 
                      key={staff.id} 
                      className="p-5 md:p-8 cursor-pointer group hover:border-brand-neonBlue/40 transition-all duration-500 shadow-xl rounded-[32px] bg-slate-900/40 relative overflow-hidden" 
                      onClick={() => handleEditStaff(staff)}
                  >
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 relative z-10">
                        
                        {/* Avatar Component */}
                        <div className="shrink-0 relative">
                            <div className="w-20 h-20 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-slate-800 border-2 border-white/10 group-hover:border-brand-neonBlue/50 overflow-hidden flex items-center justify-center font-display font-bold text-2xl transition-all shadow-lg">
                                {staff.avatar_url && staff.avatar_url !== 'null' && staff.avatar_url.trim() !== '' ? (
                                    <PrivateImage src={staff.avatar_url} alt={staff.full_name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-brand-neonBlue tracking-widest shadow-[0_0_15px_rgba(0,243,255,0.3)]">
                                        {getInitials(staff.full_name)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Info Component */}
                        <div className="flex-1 min-w-0 pt-1 text-center sm:text-left">
                            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start mb-1 gap-2">
                                <div>
                                    <h4 className="text-white font-bold text-xl md:text-2xl tracking-tight truncate group-hover:text-brand-neonBlue transition-colors">{staff.full_name}</h4>
                                    <p className="type-label text-brand-neonBlue mt-1">{normalizeStaffRole(staff.role)}</p>
                                </div>
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteStaff(staff.id); }} 
                                    className="opacity-100 sm:opacity-0 group-hover:opacity-100 p-2 bg-slate-950/50 rounded-xl text-red-400 hover:bg-red-500 hover:text-white transition-all border border-white/5 sm:border-transparent hover:border-red-500/50"
                                    title="Remove Crew Member"
                                >
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                            
                            <div className="mt-4 sm:mt-5 space-y-2 text-[11px] sm:text-xs md:text-sm text-slate-400 font-medium">
                                <div className="flex items-center justify-center sm:justify-start gap-3 hover:text-white transition-colors"><Mail size={14} className="text-slate-600 shrink-0"/> <span className="truncate">{staff.email}</span></div>
                                <div className="flex items-center justify-center sm:justify-start gap-3 hover:text-white transition-colors"><Phone size={14} className="text-slate-600 shrink-0"/> <span>{staff.phone}</span></div>
                                <div className="flex items-center justify-center sm:justify-start gap-3"><MapPin size={14} className="text-slate-600 shrink-0"/> <span>{staff.base_location || 'Auckland'}</span></div>
                            </div>
                        </div>
                    </div>
                  </GlassCard>
                ))}
              </div>

              {/* ⚡ UPGRADED: FREELANCE & CASUAL POOL ⚡ */}
              <div className="mt-24 pt-12 border-t border-white/10">
                <div className="mb-10 flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                  <div>
                    <h3 className="type-heading text-3xl text-white">Freelance & Casual Pool</h3>
                    <p className="type-label text-slate-500 mt-4">On-Call Assistants & Extra Hands</p>
                  </div>
                  <Button onClick={() => setIsCasualEditorOpen(true)} className="bg-yellow-500 text-slate-950 px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(234,179,8,0.2)]">
                    <Plus size={20} className="mr-2"/> Add Casual Crew
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {casualStaffList.map((casual: any) => {
                    const avgRating = casual.total_ratings > 0 ? (casual.total_stars / casual.total_ratings).toFixed(1) : 'New';
                    
                    return (
                      <GlassCard key={casual.id} className="p-6 relative group hover:border-yellow-500/40 transition-all duration-700 shadow-xl rounded-[24px] bg-slate-900/40 flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3 flex flex-col justify-between">
                              <div>
                                  <div className="flex justify-between items-start">
                                      <h4 className="text-white font-bold text-xl tracking-tight mb-2">{casual.name}</h4>
                                      <button onClick={() => handleDeleteCasual(casual.id)} className="opacity-0 group-hover:opacity-100 p-2 bg-slate-800 rounded-lg text-red-400 hover:bg-red-500/20 transition-all border border-transparent hover:border-red-500/30">
                                          <Trash2 size={14}/>
                                      </button>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mb-4">
                                    <span className="text-[10px] text-yellow-500 uppercase font-black tracking-widest bg-yellow-500/10 px-2 py-1 rounded-md border border-yellow-500/20">
                                      ⭐ {avgRating}
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest bg-slate-800 px-2 py-1 rounded-md">
                                      {casual.jobs_completed || 0} Shifts
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-400 font-medium">
                                      <Phone size={14} className="text-slate-600"/> {casual.phone || 'No phone'}
                                  </div>
                              </div>
                          </div>

                          <div className="md:w-2/3 border-t md:border-t-0 md:border-l border-white/5 pt-4 md:pt-0 md:pl-6 flex flex-col">
                              <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest block mb-2 flex items-center gap-1">
                                <Star size={10}/> Performance History
                              </span>
                              <div className="flex-1 text-xs text-slate-400 italic bg-slate-950/50 p-4 rounded-xl max-h-32 overflow-y-auto custom-scrollbar border border-white/5 whitespace-pre-wrap">
                                {casual.notes || 'No performance reviews logged yet.'}
                              </div>
                          </div>
                      </GlassCard>
                    )
                  })}
                  
                  {casualStaffList.length === 0 && (
                    <div className="col-span-full text-center py-12 border border-dashed border-white/10 rounded-[32px] text-slate-500 font-medium">
                      No casual staff in the database yet. Add them from the Job Dispatcher!
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ⚡ MODALS MOVED OUTSIDE THE ANIMATED DIV */}
            {/* ⚡ UPGRADED STAFF EDITOR MODAL WITH CALENDAR */}
            {isStaffEditorOpen && editingStaff && (
              <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/90 backdrop-blur-3xl overflow-hidden">
                <GlassCard className="w-full max-w-7xl p-6 md:p-10 border-brand-neonPink/30 shadow-neon-pink rounded-t-[32px] md:rounded-[48px] rounded-b-none md:rounded-b-[48px] mt-12 md:my-8 max-h-[92vh] md:max-h-[85vh] flex flex-col animate-fade-in-up">
                  <div className="flex justify-between items-center mb-6 shrink-0">
                    <h2 className="type-heading truncate pr-4 text-2xl text-white md:text-3xl">
                      {editingStaff?.id ? 'Personnel Profile & Schedule' : 'New Crew Member'}
                    </h2>
                    <button onClick={() => setIsStaffEditorOpen(false)} className="text-slate-500 hover:text-white transition-all bg-white/5 p-2 rounded-xl shrink-0"><X size={24}/></button>
                  </div>
                  
                  {/* ⚡ The Scrollable Container */}
                  <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 md:pr-4 pb-6 md:pb-0">
                      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 lg:gap-10">
                          
                          {/* LEFT SIDE: PROFILE FORM */}
                          <div className="xl:col-span-7 space-y-8">
                              
                              {/* Core Identity */}
                              <div className="space-y-6">
                                  <h4 className="type-label text-brand-neonPink border-b border-white/5 pb-2 mb-4 flex items-center gap-2">
                                      <User size={12}/> Core Identity
                                  </h4>
                                  
                                  <div className="space-y-2">
                                    <label className="type-label ml-2 text-slate-500">Full Legal Identity (Matches ID)</label>
                                    <input className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink transition-colors" placeholder="Full Legal Name" value={editingStaff.full_name || editingStaff.name || editingStaff.legal_name || ''} onChange={e => setEditingStaff({...editingStaff, full_name: e.target.value, legal_name: e.target.value})} />
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <label className="type-label ml-2 text-slate-500">Role Assignment</label>
                                      <select className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink cursor-pointer appearance-none transition-colors" value={normalizeStaffRole(editingStaff.role) || 'Event Attendant'} onChange={e => setEditingStaff({...editingStaff, role: e.target.value})}>
                                        {STAFF_ROLE_OPTIONS.map(role => <option key={role} value={role}>{role}</option>)}
                                        {editingStaff.role && !STAFF_ROLE_OPTIONS.includes(normalizeStaffRole(editingStaff.role)) && <option value={editingStaff.role}>{editingStaff.role}</option>}
                                      </select>
                                    </div>
                                    <div className="space-y-2">
                                      <label className="type-label ml-2 text-slate-500">Base Location</label>
                                      <input className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink transition-colors" placeholder="e.g. Auckland" value={editingStaff.base_location || ''} onChange={e => setEditingStaff({...editingStaff, base_location: e.target.value})} />
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Email Identity</label>
                                        <input type="email" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink transition-colors" placeholder="name@example.com" value={editingStaff.email || ''} onChange={e => setEditingStaff({...editingStaff, email: e.target.value})} />
                                      </div>
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Mobile Reference</label>
                                        <input type="tel" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink transition-colors" placeholder="021 555 1234" value={editingStaff.phone || ''} onChange={e => setEditingStaff({...editingStaff, phone: e.target.value})} />
                                      </div>
                                  </div>
                              </div>

                              {editingStaff.id && (
                                <StaffReferralProgramme
                                  staffId={editingStaff.id}
                                  staffName={editingStaff.full_name || editingStaff.name || editingStaff.legal_name || 'Crew member'}
                                />
                              )}

                              {/* Logistics & Travel */}
                              <div className="space-y-6">
                                  <h4 className="type-label text-brand-neonBlue border-b border-white/5 pb-2 mb-4 flex items-center gap-2">
                                      <Truck size={12}/> Travel & Logistics Data
                                  </h4>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Date of Birth</label>
                                        <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors [color-scheme:dark]" value={editingStaff.dob || ''} onChange={e => setEditingStaff({...editingStaff, dob: e.target.value})} />
                                      </div>
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Home Airport</label>
                                        <input type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors uppercase" placeholder="e.g. AKL" maxLength={3} value={editingStaff.home_airport || ''} onChange={e => setEditingStaff({...editingStaff, home_airport: e.target.value})} />
                                      </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Driver's License #</label>
                                        <input type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors uppercase" placeholder="e.g. AB123456" value={editingStaff.license_number || ''} onChange={e => setEditingStaff({...editingStaff, license_number: e.target.value})} />
                                      </div>
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">License Expiry</label>
                                        <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors [color-scheme:dark]" value={editingStaff.license_expiry || ''} onChange={e => setEditingStaff({...editingStaff, license_expiry: e.target.value})} />
                                      </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">T-Shirt Size</label>
                                        <select className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue cursor-pointer appearance-none transition-colors" value={editingStaff.shirt_size || ''} onChange={e => setEditingStaff({...editingStaff, shirt_size: e.target.value})}>
                                            <option value="">Select Size...</option>
                                            <option value="XS">XS</option>
                                            <option value="S">Small</option>
                                            <option value="M">Medium</option>
                                            <option value="L">Large</option>
                                            <option value="XL">XL</option>
                                            <option value="XXL">XXL</option>
                                            <option value="XXXL">XXXL</option>
                                        </select>
                                      </div>
                                      <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Dietary Requirements</label>
                                        <input type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="e.g. Vegan, GF" value={editingStaff.dietary_requirements || ''} onChange={e => setEditingStaff({...editingStaff, dietary_requirements: e.target.value})} />
                                      </div>
                                  </div>

                                  <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-2xl space-y-4">
                                      <h5 className="type-label text-red-400 flex items-center gap-1.5"><AlertCircle size={10}/> Emergency Contact</h5>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                          <input type="text" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-red-500/50 transition-colors" placeholder="Contact Name" value={editingStaff.emergency_contact_name || ''} onChange={e => setEditingStaff({...editingStaff, emergency_contact_name: e.target.value})} />
                                          <input type="tel" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-red-500/50 transition-colors" placeholder="Contact Phone" value={editingStaff.emergency_contact_phone || ''} onChange={e => setEditingStaff({...editingStaff, emergency_contact_phone: e.target.value})} />
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* RIGHT SIDE: LIVE CALENDAR */}
                          <div className="xl:col-span-5 bg-slate-900/50 rounded-[32px] p-5 md:p-6 border border-white/10 flex flex-col h-full min-h-[400px]">
                              <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4">
                                  <button onClick={(e) => { e.preventDefault(); setAdminCalendarMonth(new Date(adminCalendarMonth.getFullYear(), adminCalendarMonth.getMonth() - 1, 1)); }} className="p-2 text-brand-neonBlue hover:bg-brand-neonBlue/10 rounded-xl transition-colors"><ChevronLeft size={20}/></button>
                                  <h3 className="text-white font-bold text-sm tracking-widest uppercase">{adminCalendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                                  <button onClick={(e) => { e.preventDefault(); setAdminCalendarMonth(new Date(adminCalendarMonth.getFullYear(), adminCalendarMonth.getMonth() + 1, 1)); }} className="p-2 text-brand-neonBlue hover:bg-brand-neonBlue/10 rounded-xl transition-colors"><ChevronLeft size={20} className="rotate-180"/></button>
                              </div>

                              {/* ⚡ ALIGNED MONDAY-FIRST CALENDAR HEADERS */}
                              <div className="grid grid-cols-7 gap-1 md:gap-2 text-center mb-2">
                                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                                      <div key={d} className="type-label text-slate-500">{d}</div>
                                  ))}
                              </div>

                              <div className="grid grid-cols-7 gap-1 md:gap-2 flex-1">
                                  {/* ⚡ CORRECTED MONDAY-FIRST PADDING LOGIC */}
                                  {Array.from({ 
                                      length: new Date(adminCalendarMonth.getFullYear(), adminCalendarMonth.getMonth(), 1).getDay() === 0 
                                      ? 6 
                                      : new Date(adminCalendarMonth.getFullYear(), adminCalendarMonth.getMonth(), 1).getDay() - 1 
                                  }).map((_, i) => <div key={`empty-${i}`} />)}
                                  
                                  {/* ⚡ ACTUAL DAYS RENDERER (With all the clicks and tooltips) */}
                                  {Array.from({ length: new Date(adminCalendarMonth.getFullYear(), adminCalendarMonth.getMonth() + 1, 0).getDate() }).map((_, i) => {
                                      const day = i + 1;
                                      const dateStr = `${adminCalendarMonth.getFullYear()}-${String(adminCalendarMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                      
                                      // ⚡ NEW: Identify if this specific box is TODAY
                                      const todayDate = new Date();
                                      const isToday = dateStr === `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
                                      
                                      // 1. Find ALL shifts that touch this day
                                      const shiftsOnThisDay = allShifts.filter(shift => {
                                          if(shift.staffing_source==='PHASES')return phaseStaffOnDay(shift,dateStr,editingStaff.id,['CONFIRMED','ACCEPTED','PENDING']);
                                          if (!shift.event_date || !shift.event_date.startsWith(dateStr)) return false;
                                          return shift.staff_roster?.crew?.some((c: any) => c.id === editingStaff.id);
                                      });
                                      
                                      // 2. Sort them into "Confirmed" vs "Pending"
                                      const bookedShifts = shiftsOnThisDay.filter(s => s.staffing_source==='PHASES'?phaseStaffOnDay(s,dateStr,editingStaff.id,['CONFIRMED','ACCEPTED']):s.staff_roster.crew.find((c:any) => c.id === editingStaff.id && ['CONFIRMED', 'ACCEPTED'].includes(c.status)));
                                      const pendingShifts = shiftsOnThisDay.filter(s => s.staffing_source==='PHASES'?phaseStaffOnDay(s,dateStr,editingStaff.id,['PENDING']):s.staff_roster.crew.find((c:any) => c.id === editingStaff.id && c.status === 'PENDING'));
                                      
                                      const isBooked = bookedShifts.length > 0;
                                      const isPending = pendingShifts.length > 0 && !isBooked; 
                                      const availStatus = adminAvailabilities[dateStr]?.status || 'AVAILABLE';

                                      // ⚡ NEW: Grab tasks specifically assigned to THIS crew member on THIS day
                                      const staffTasksOnDay = globalTasks.filter(t => t.assignee_id === editingStaff.id && t.due_date && t.due_date.startsWith(dateStr) && t.status !== 'COMPLETED');
                                      const pendingTasksCount = staffTasksOnDay.length;
                                      
                                      let badgeClass = 'border-white/10 text-slate-400 bg-slate-900/80 hover:border-brand-neonBlue/30';
                                      let tooltip = 'Available';
                                      
                                      // Helper to get the shift name safely
                                      const getShiftName = (s: any) => {
                                          if (s.shift_title) return s.shift_title;
                                          const parentLead = crmItems.find(i => i.id === s.lead_id);
                                          return parentLead ? (parentLead.eventName || (parentLead as any).project_ref || 'Unnamed Event') : 'Unnamed Event';
                                      };

                                      if (isBooked) {
                                          badgeClass = 'bg-brand-neonBlue text-slate-950 font-bold border-brand-neonBlue shadow-neon ring-2 ring-brand-neonBlue/50 z-10';
                                          try {
                                              const shiftNames = bookedShifts.map(getShiftName).join(' | ');
                                              tooltip = `Booked: ${shiftNames}`;
                                          } catch(e) { tooltip = 'Booked on Shift'; }
                                      } else if (isPending) {
                                          badgeClass = 'bg-brand-neonPink/10 text-brand-neonPink font-bold border-brand-neonPink border-dashed border-2 animate-pulse z-10';
                                          try {
                                              const shiftNames = pendingShifts.map(getShiftName).join(' | ');
                                              tooltip = `Awaiting Reply: ${shiftNames}`;
                                          } catch(e) { tooltip = 'Awaiting Reply'; }
                                      } else if (availStatus === 'AVAILABLE') {
                                          badgeClass = 'bg-green-500/10 text-green-400 border-green-500/30';
                                      } else if (availStatus === 'UNAVAILABLE') {
                                          badgeClass = 'bg-red-500/10 text-red-400 border-red-500/30 opacity-50';
                                          tooltip = 'Unavailable (Blocked)';
                                      } else if (availStatus === 'PARTIAL') {
                                          badgeClass = 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30';
                                          const start = adminAvailabilities[dateStr]?.start_time;
                                          const end = adminAvailabilities[dateStr]?.end_time;
                                          tooltip = `Partial: ${start} - ${end}`;
                                      }

                                      // Add tasks to the mouse hover tooltip
                                      if (pendingTasksCount > 0) {
                                        const taskNames = staffTasksOnDay.map(t => {
                                            // ⚡ SMART SHIFT DATE CALCULATOR
                                            let displayEventDate = t.lead?.event_date;
                                            if (t.task_type === 'AUTO_LOGISTICS' && t.due_date) {
                                                const d = new Date(t.due_date);
                                                if (t.title?.includes('Prep Gear')) d.setDate(d.getDate() + 1);
                                                else if (t.title?.includes('Return & Inspect')) d.setDate(d.getDate() - 1);
                                                displayEventDate = d.toISOString();
                                            }
                                            
                                            const eventDate = displayEventDate ? ` [For Event: ${new Date(displayEventDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}]` : '';
                                            return `${t.title}${eventDate}`;
                                        }).join(', ');
                                        tooltip += ` | Tasks: ${taskNames}`;
                                      }

                                    // Add a striking white outline and bright text for TODAY
                                    if (isToday) {
                                        badgeClass += ' outline outline-2 outline-white outline-offset-2 z-10 !text-white font-black shadow-[0_0_15px_rgba(255,255,255,0.3)]';
                                    }

                                    return (
                                        <div 
                                            key={day} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // If they are booked or pending for a shift, show the alert.
                                                if (isBooked || isPending) {
                                                    alert(tooltip);
                                                    return;
                                                }

                                                // ⚡ NEW: Pop up the specific tasks they need to do on this day WITH EVENT DATE
                                                if (pendingTasksCount > 0) {
                                                  const taskNames = staffTasksOnDay.map(t => {
                                                      // ⚡ SMART SHIFT DATE CALCULATOR
                                                      let displayEventDate = t.lead?.event_date;
                                                      if (t.task_type === 'AUTO_LOGISTICS' && t.due_date) {
                                                          const d = new Date(t.due_date);
                                                          if (t.title?.includes('Prep Gear')) d.setDate(d.getDate() + 1);
                                                          else if (t.title?.includes('Return & Inspect')) d.setDate(d.getDate() - 1);
                                                          displayEventDate = d.toISOString();
                                                      }

                                                      const eventDate = displayEventDate ? ` (For Event: ${new Date(displayEventDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })})` : '';
                                                      return `• ${t.title}${eventDate}`;
                                                  }).join('\n');
                                                  
                                                  alert(`Tasks Scheduled for ${editingStaff.full_name} on ${dateStr}:\n\n${taskNames}\n\n(Click OK to continue to Availability Editor)`);
                                                  return;
                                              }

                                                  // Otherwise, pop open the Admin Override Modal!
                                                  setAdminSelectedDate(dateStr);
                                                  setAdminAvailForm({
                                                      status: availStatus,
                                                      startTime: adminAvailabilities[dateStr]?.start_time || '12:00',
                                                      endTime: adminAvailabilities[dateStr]?.end_time || '23:00'
                                                  });
                                              }}
                                              className={`aspect-square flex items-center justify-center rounded-md md:rounded-lg border text-[10px] md:text-xs transition-all cursor-pointer hover:scale-105 relative ${badgeClass}`} 
                                              title={tooltip}
                                          >
                                              {day}
                                              
                                              {/* ⚡ NEW: RENDER A PINK DOT IF TASKS ARE DUE */}
                                              {pendingTasksCount > 0 && (
                                                  <div className="absolute top-1 right-1 w-2 md:w-2.5 h-2 md:h-2.5 bg-brand-neonPink rounded-full shadow-[0_0_10px_rgba(236,72,153,0.9)] animate-pulse border border-slate-950 z-20"></div>
                                              )}
                                          </div>
                                      );
                                  })}
                              </div>
                              
                              <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-4 pt-4 border-t border-white/5">
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-brand-neonBlue shadow-neon-blue-sm"></div> Booked</span>
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full border border-dashed border-brand-neonPink bg-brand-neonPink/20"></div> Pending</span>
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-brand-neonPink shadow-[0_0_10px_rgba(236,72,153,0.8)] animate-pulse border border-slate-950"></div> Task Due</span>
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div> Free</span>
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div> Partial</span>
                                  <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-red-400"></div> Busy</span>
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* BOTTOM ACTION BUTTONS */}
                  <div className="shrink-0 flex flex-col-reverse sm:flex-row justify-end gap-4 pt-6 mt-4 border-t border-white/5">
                    <Button variant="outline" className="w-full sm:w-auto px-8 py-4 rounded-xl" onClick={() => setIsStaffEditorOpen(false)}>Abort</Button>
                    <Button className="w-full sm:w-auto px-10 py-4 bg-brand-neonPink text-white shadow-neon-pink rounded-xl font-bold border-none" onClick={async () => {
                        if (!editingStaff || !(editingStaff.full_name || editingStaff.name)) {
                            alert("Name is required.");
                            return;
                        }
                        
                        const payload = {
                            full_name: editingStaff.full_name || editingStaff.name,
                            role: editingStaff.role,
                            email: editingStaff.email,
                            phone: editingStaff.phone,
                            base_location: editingStaff.base_location,
                            legal_name: editingStaff.legal_name || editingStaff.full_name,
                            dob: editingStaff.dob || null,
                            license_number: editingStaff.license_number || null,
                            license_expiry: editingStaff.license_expiry || null,
                            shirt_size: editingStaff.shirt_size || null,
                            dietary_requirements: editingStaff.dietary_requirements || null,
                            home_airport: editingStaff.home_airport || null,
                            emergency_contact_name: editingStaff.emergency_contact_name || null,
                            emergency_contact_phone: editingStaff.emergency_contact_phone || null
                        };

                        try {
                            if (editingStaff.id) {
                                const { error } = await supabase.from('staff_members').update(payload).eq('id', editingStaff.id);
                                if (error) throw error;
                            } else {
                                const { error } = await supabase.from('staff_members').insert([payload]);
                                if (error) throw error;
                            }
                            await fetchStaff(); 
                            setIsStaffEditorOpen(false);
                            setEditingStaff(null);
                        } catch (e: any) {
                            alert(`Database Error: ${e.message}`);
                        }
                    }}>
                        Confirm & Save Profile
                    </Button>
                  </div>
                </GlassCard>
              </div>
            )}

{/* ⚡ ADMIN OVERRIDE MODAL (Sits on top of the profile modal) */}
{adminSelectedDate && (
                <div className="fixed inset-0 z-[10000] bg-slate-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setAdminSelectedDate(null)}>
                    <div className="w-full max-w-sm bg-slate-900 border border-brand-neonBlue/50 rounded-t-[32px] sm:rounded-[32px] p-6 animate-slide-up shadow-[0_0_40px_rgba(0,243,255,0.15)]" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4">
                            <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                                <ShieldAlert size={18} className="text-brand-neonBlue" /> Admin Override
                            </h3>
                            <button onClick={() => setAdminSelectedDate(null)} className="p-2 bg-white/5 rounded-full text-slate-400 hover:text-white"><X size={20}/></button>
                        </div>
                        
                        <p className="text-xs text-slate-400 mb-4 font-medium uppercase tracking-widest text-center">
                            {new Date(adminSelectedDate).toLocaleDateString('en-NZ', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </p>

                        <div className="space-y-3 mb-6">
                            <button 
                                onClick={() => setAdminAvailForm({...adminAvailForm, status: 'AVAILABLE'})}
                                className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${adminAvailForm.status === 'AVAILABLE' ? 'bg-green-500/20 text-green-400 border-green-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                            >
                                <CheckCircle size={18}/> Available All Day
                            </button>
                            <button 
                                onClick={() => setAdminAvailForm({...adminAvailForm, status: 'PARTIAL'})}
                                className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${adminAvailForm.status === 'PARTIAL' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                            >
                                <Clock size={18}/> Specific Hours Only
                            </button>
                            <button 
                                onClick={() => setAdminAvailForm({...adminAvailForm, status: 'UNAVAILABLE'})}
                                className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${adminAvailForm.status === 'UNAVAILABLE' ? 'bg-red-500/20 text-red-400 border-red-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                            >
                                <AlertCircle size={18}/> Unavailable (Block Out)
                            </button>
                        </div>

                        {adminAvailForm.status === 'PARTIAL' && (
                            <div className="flex gap-4 mb-6 bg-black/30 p-4 rounded-2xl border border-white/5 animate-fade-in">
                                <div className="flex-1">
                                    <label className="type-label mb-1 block text-slate-500">Free From</label>
                                    <select 
                                        value={adminAvailForm.startTime.slice(0, 5)} 
                                        onChange={e => setAdminAvailForm({...adminAvailForm, startTime: e.target.value})} 
                                        className="w-full bg-slate-950 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-brand-neonBlue cursor-pointer appearance-none"
                                    >
                                        {Array.from({ length: 24 * 4 }).map((_, i) => {
                                            const h = Math.floor(i / 4);
                                            const m = (i % 4) * 15;
                                            const ampm = h >= 12 ? 'PM' : 'AM';
                                            const hour12 = h % 12 || 12;
                                            const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                            const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                            return <option key={`start-${val}`} value={val}>{label}</option>;
                                        })}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="type-label mb-1 block text-slate-500">Free Until</label>
                                    <select 
                                        value={adminAvailForm.endTime.slice(0, 5)} 
                                        onChange={e => setAdminAvailForm({...adminAvailForm, endTime: e.target.value})} 
                                        className="w-full bg-slate-950 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-brand-neonBlue cursor-pointer appearance-none"
                                    >
                                        {Array.from({ length: 24 * 4 }).map((_, i) => {
                                            const h = Math.floor(i / 4);
                                            const m = (i % 4) * 15;
                                            const ampm = h >= 12 ? 'PM' : 'AM';
                                            const hour12 = h % 12 || 12;
                                            const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                            const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                            return <option key={`end-${val}`} value={val}>{label}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>
                        )}

                        <Button onClick={handleSaveAdminAvailability} className="w-full py-5 rounded-2xl bg-brand-neonBlue text-slate-950 font-bold shadow-neon-blue-sm">
                            Force Update Calendar
                        </Button>
                    </div>
                </div>
            )}

            {/* ⚡ NEW CASUAL EDITOR MODAL */}
            {isCasualEditorOpen && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-3xl">
                <GlassCard className="w-full max-w-md p-10 border-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.15)] rounded-[48px]">
                  <div className="flex justify-between items-center mb-10">
                    <h2 className="type-heading text-3xl text-white">
                      New Casual Crew
                    </h2>
                    <button onClick={() => setIsCasualEditorOpen(false)} className="text-slate-500 hover:text-white transition-all"><X size={32}/></button>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="type-label ml-2 text-slate-500">Full Name</label>
                      <input 
                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-yellow-500" 
                        placeholder="e.g. John Doe" 
                        value={newCasualForm.name} 
                        onChange={e => setNewCasualForm({...newCasualForm, name: e.target.value})} 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="type-label ml-2 text-slate-500">Mobile Number</label>
                      <input 
                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-yellow-500" 
                        placeholder="e.g. 021 555 1234" 
                        value={newCasualForm.phone} 
                        onChange={e => setNewCasualForm({...newCasualForm, phone: e.target.value})} 
                    />
                    </div>
                    <div className="flex justify-end gap-4 pt-6">
                      <Button variant="outline" className="px-8 py-4 rounded-2xl" onClick={() => setIsCasualEditorOpen(false)}>Cancel</Button>
                      <Button className="px-8 py-4 bg-yellow-500 text-slate-950 rounded-2xl font-bold" onClick={handleSaveCasual}>Save to Database</Button>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
            
          </>
        );
      };
    
      // ⚡ INTERNAL PROJECTS ENGINE
      const renderProjects = () => {
        const handleSaveProject = async () => {
            if (!newProject.title.trim()) return alert("Project title is required.");
            const payload = {
                title: newProject.title,
                description: newProject.description,
                deadline: newProject.deadline ? new Date(newProject.deadline).toISOString() : null,
                status: newProject.status
            };
            const { error } = await supabase.from('internal_projects').insert([payload]);
            if (error) {
                alert("Error saving project: " + error.message);
            } else {
                setIsAddingProject(false);
                setNewProject({ title: '', description: '', deadline: '', status: 'ACTIVE' });
                fetchProjects();
            }
        };

        return (
            <div className="animate-fade-in-up mt-2">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-4 md:gap-6">
                    <div>
                        <h1 className="type-page-title text-white">Internal Projects</h1>
                        <p className="type-label text-slate-500 mt-2 md:mt-4 flex items-center gap-2">
                            <FolderOpen size={12} className="text-brand-neonBlue"/> Business Operations & Strategy
                        </p>
                    </div>
                    <Button onClick={() => setIsAddingProject(true)} className="w-full md:w-auto bg-brand-neonBlue text-slate-950 font-bold px-6 py-3 md:px-8 md:py-4 rounded-xl md:rounded-2xl shadow-neon transition-transform hover:scale-105 text-sm">
                        <Plus size={18} className="mr-2"/> Start New Project
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                    {internalProjects.map((proj: any) => {
                        const projectTasks = globalTasks.filter(t => t.project_id === proj.id);
                        const completedTasks = projectTasks.filter(t => t.status === 'COMPLETED').length;
                        const progress = projectTasks.length > 0 ? Math.round((completedTasks / projectTasks.length) * 100) : 0;

                        return (
                          <GlassCard key={proj.id} className="p-5 md:p-8 hover:border-brand-neonBlue/20 transition-all duration-500 flex flex-col h-full bg-slate-900/40">
                            <div className="flex justify-between items-start mb-4 md:mb-6">
                              <h4 className="type-heading pr-4 text-xl text-white transition-colors group-hover:text-brand-neonBlue md:text-2xl">{proj.title}</h4>
                              <span className={`type-button text-xs px-3 py-1 rounded-md border shrink-0 ${proj.status === 'COMPLETED' ? 'bg-green-500/10 text-green-400 border-green-500/20' : proj.status === 'ON_HOLD' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' : 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/20'}`}>
                                {proj.status.replace('_', ' ')}
                              </span>
                            </div>
                            
                            <p className="text-sm text-slate-400 mb-8 line-clamp-3 leading-relaxed flex-1">{proj.description || 'No description provided for this project.'}</p>
                            
                            <div className="space-y-3 mb-8">
                              <div className="flex justify-between type-label text-slate-500">
                                <span>Task Progress ({completedTasks}/{projectTasks.length})</span>
                                <span className="text-white">{progress}%</span>
                              </div>
                              <div className="h-2 w-full bg-slate-950 rounded-full overflow-hidden border border-white/5">
                                <div className="h-full bg-gradient-to-r from-brand-neonBlue to-brand-neonPink transition-all duration-1000 relative" style={{ width: `${progress}%` }}>
                                  <div className="absolute top-0 right-0 bottom-0 w-10 bg-white/20 blur-sm"></div>
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-between items-center pt-5 border-t border-white/5 type-label text-slate-500 gap-2">
                              <span className="flex items-center gap-1.5 truncate max-w-[100px] sm:max-w-none"><Calendar size={12} className="text-slate-600"/> {proj.deadline ? new Date(proj.deadline).toLocaleDateString('en-NZ') : 'No Deadline'}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNewTaskData({ title: '', assignee_id: '', lead_id: '', project_id: proj.id, due_date: getLocalDateString(), recurrence: 'none', description: '', is_urgent: false });
                                    setIsAddingGlobalTask(true);
                                  }}
                                  className="px-2.5 py-1.5 bg-brand-neonPink/10 hover:bg-brand-neonPink text-brand-neonPink hover:text-white border border-brand-neonPink/20 rounded-lg font-black transition-colors"
                                  title="Add task directly to this project"
                                >
                                  + Task
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProjectIdFilter(proj.id);
                                    setTaskFilter('ALL');
                                    setTaskAssigneeFilter('ALL');
                                    setActiveTab('tasks');
                                  }}
                                  className="flex items-center gap-1 text-brand-neonBlue bg-brand-neonBlue/10 hover:bg-brand-neonBlue hover:text-slate-950 px-2.5 py-1.5 rounded-lg font-black transition-colors border border-brand-neonBlue/20"
                                >
                                  <CheckCircle size={12}/> View
                                </button>
                              </div>
                            </div>
                          </GlassCard>
                        )
                    })}
                    {internalProjects.length === 0 && (
                        <div className="col-span-full text-center py-24 text-slate-500 font-medium border-2 border-dashed border-white/5 rounded-[40px] bg-slate-900/20">
                            <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />
                            No internal projects active. Start one above!
                        </div>
                    )}
                </div>

                {isAddingProject && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-3xl overflow-y-auto">
                    <GlassCard className="w-full max-w-xl p-6 md:p-10 border-brand-neonBlue/30 shadow-neon rounded-3xl md:rounded-[48px] animate-fade-in-up my-8">
                        <div className="flex justify-between items-center mb-6 md:mb-10">
                            <h3 className="type-heading text-2xl text-white md:text-3xl">Initialize Project</h3>
                                <button onClick={() => setIsAddingProject(false)} className="text-slate-500 hover:text-white transition-all"><X size={32}/></button>
                            </div>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="type-label text-brand-neonBlue ml-2">Project Name *</label>
                                    <input className="w-full bg-slate-900/50 border border-brand-neonBlue/30 rounded-2xl p-5 text-white focus:border-brand-neonBlue outline-none transition-colors" value={newProject.title} onChange={e => setNewProject({...newProject, title: e.target.value})} placeholder="e.g. Q3 Marketing Strategy" autoFocus/>
                                </div>
                                <div className="space-y-2">
                                    <label className="type-label ml-2 text-slate-500">Brief / Objectives</label>
                                    <textarea className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white focus:border-brand-neonBlue outline-none min-h-[120px] transition-colors resize-y" value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} placeholder="What are we trying to achieve?"/>
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Target Deadline</label>
                                        <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white focus:border-brand-neonBlue outline-none [color-scheme:dark] transition-colors" value={newProject.deadline} onChange={e => setNewProject({...newProject, deadline: e.target.value})}/>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="type-label ml-2 text-slate-500">Status</label>
                                        <select className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white focus:border-brand-neonBlue outline-none transition-colors appearance-none cursor-pointer" value={newProject.status} onChange={e => setNewProject({...newProject, status: e.target.value})}>
                                            <option value="ACTIVE">Active / Planning</option>
                                            <option value="ON_HOLD">On Hold</option>
                                            <option value="COMPLETED">Completed</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-4 pt-6 border-t border-white/5">
                                    <Button variant="outline" className="flex-1 py-5 rounded-2xl" onClick={() => setIsAddingProject(false)}>Cancel</Button>
                                    <Button className="flex-1 py-5 bg-brand-neonBlue text-slate-950 shadow-neon rounded-2xl font-bold border-none" onClick={handleSaveProject}>Create Project</Button>
                                </div>
                            </div>
                        </GlassCard>
                    </div>
                )}
            </div>
        )
    };

      // ⚡ TASKS ENGINE: UPGRADED WITH "GROUP BY JOB"
  const renderTasks = () => {
    // ⚡ LOOKUP ENGINE: Finds the match for the project we are isolating
    const currentProjectContext = internalProjects.find(p => p.id === selectedProjectIdFilter);

    const displayTasks = globalTasks.filter(t => {
          // ⚡ COMPONENT GATING: If isolating an internal project, filter out all other tasks instantly
          if (selectedProjectIdFilter !== 'ALL') {
              return t.project_id === selectedProjectIdFilter;
          }
          
          // 1. If "My Tasks" is clicked, override everything else
          if (taskFilter === 'MINE' && currentUser) {
              return t.assignee_id === currentUser.id;
          }
          // 2. If looking for Unassigned tasks
          if (taskAssigneeFilter === 'UNASSIGNED') {
              return !t.assignee_id;
          }
          // 3. If a specific staff member is selected from the dropdown
          if (taskAssigneeFilter !== 'ALL') {
              return t.assignee_id === taskAssigneeFilter;
          }
          // 4. Otherwise, show ALL
          return true;
      });


      const handleArchiveCompleted = async (tasksToSweep: any[]) => {
          if(!window.confirm('Sweep board? This permanently deletes all completed tasks.')) return;
          const completedIds = tasksToSweep.map(t => t.id);
          setGlobalTasks(prev => prev.filter(t => !completedIds.includes(t.id)));
          await supabase.from('tasks').delete().in('id', completedIds);
      };

      // ⚡ THE TASK CARD COMPONENT (Ultra-Slim Mobile)
      const TaskCard = ({ task, team }: { task: any, team: any[] }) => {
        const [isAssigning, setIsAssigning] = useState(false);

        const handleAssign = async (staffId: string) => {
            const { error } = await supabase.from('tasks').update({ assignee_id: staffId }).eq('id', task.id);
            if (!error) setIsAssigning(false);
        };

        const assignee = team.find((s: any) => s.id === task.assignee_id);

        // ⚡ SMART SHIFT DATE CALCULATOR 
        // Reverse-engineers the specific shift date from the task's due date
        let displayEventDate = task.lead?.event_date;
        if (task.task_type === 'AUTO_LOGISTICS' && task.due_date) {
            const d = new Date(task.due_date);
            if (task.title?.includes('Prep Gear')) {
                d.setDate(d.getDate() + 1);
                displayEventDate = d.toISOString();
            } else if (task.title?.includes('Return & Inspect')) {
                d.setDate(d.getDate() - 1);
                displayEventDate = d.toISOString();
            }
        }

        return (
            <GlassCard className="p-3 md:p-5 hover:border-brand-neonBlue/40 transition-all group relative overflow-hidden bg-slate-900/40">
                <div className="flex justify-between items-start mb-2 md:mb-4">
                    <div className="flex items-start md:items-center gap-2 flex-1 mr-2 md:mr-3">
                        {task.recurrence && task.recurrence !== 'none' && (
                            <span title="Recurring Task" className="mt-0.5 md:mt-0"><Repeat size={12} className="w-3 md:w-3.5 h-3 md:h-3.5 text-brand-neonPink shrink-0" /></span>
                        )}
                        <p className="text-xs md:text-sm font-bold text-white leading-snug">{task.title}</p>
                    </div>
                    
                    <div className="flex items-center gap-1 md:gap-2 shrink-0">
                        <div className="relative">
                            {!isAssigning ? (
                                <button onClick={(e) => { e.stopPropagation(); setIsAssigning(true); }} className="flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-white/5 hover:bg-brand-neonPink/20 hover:text-brand-neonPink transition-all" aria-label={assignee ? `Assigned to ${assignee.full_name}. Change assignee` : 'Assign task'}>
                                    {assignee ? (
                                        <div className="w-4 h-4 md:w-6 md:h-6 rounded-full bg-brand-neonBlue flex items-center justify-center text-[8px] md:text-[10px] font-black text-white">
                                            {assignee.full_name.split(' ').map((n: string) => n[0]).join('').substring(0,2).toUpperCase()}
                                        </div>
                                    ) : (
                                        <UserPlus size={12} className="md:w-4 md:h-4" />
                                    )}
                                </button>
                            ) : (
                                <select autoFocus onBlur={() => setIsAssigning(false)} onChange={(e) => handleAssign(e.target.value)} className="absolute right-0 top-0 z-50 min-h-11 w-36 rounded-lg border border-brand-neonPink bg-slate-800 px-2 text-xs text-white outline-none">
                                    <option value="">Assign...</option>
                                    {team.map((member: any) => (
                                        <option key={member.id} value={member.id}>{member.full_name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="flex items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all">
                            <button onClick={(e) => { e.stopPropagation(); openTaskEditor(task); }} className="flex min-h-11 min-w-11 items-center justify-center text-slate-500 hover:text-brand-neonBlue transition-all" title="Edit Task" aria-label={`Edit ${task.title}`}>
                                <Edit3 size={12} className="md:w-[16px] md:h-[16px]" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteGlobalTask(task.id); }} className="flex min-h-11 min-w-11 items-center justify-center text-slate-500 hover:text-rose-500 transition-all" title="Delete Task" aria-label={`Delete ${task.title}`}>
                                <Trash2 size={12} className="md:w-[16px] md:h-[16px]" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="space-y-2 md:space-y-3">
                    {/* Clickable Job Badge */}
                    {task.lead && (
                        <div className="flex flex-wrap gap-2">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const fullLeadData = crmItems.find((item: any) => item.id === task.lead_id);
                                    if (fullLeadData) {
                                        setSelectedInquiry(fullLeadData);
                                        setIsDetailsModalOpen(true);
                                    } else {
                                        alert("Job details could not be found. They may have been deleted.");
                                    }
                                }}
                                className="flex min-h-11 max-w-full items-center gap-1 rounded-lg border border-brand-neonBlue/20 bg-brand-neonBlue/10 px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-brand-neonBlue transition-all hover:border-brand-neonBlue/50 hover:bg-brand-neonBlue/20"
                                title="Open Job Details"
                            >
                                <Briefcase size={10} className="md:w-3.5 md:h-3.5 shrink-0" /> 
                                <span className="truncate max-w-[150px] md:max-w-none">{task.lead.project_ref || task.lead.eventName || 'View Job'}</span>
                                <ArrowUpRight size={10} className="md:w-3.5 md:h-3.5 ml-0.5 opacity-70 shrink-0" />
                            </button>
                        </div>
                    )}
                    <div className="flex min-w-0 flex-col gap-3 border-t border-white/5 pt-2 sm:flex-row sm:items-center sm:justify-between md:pt-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 md:gap-3">
                            <span className="text-[9px] md:text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                                <Calendar size={10} className="md:w-3.5 md:h-3.5" /> Due: {task.due_date ? new Date(task.due_date).toLocaleDateString('en-NZ') : 'No Date'}
                            </span>
                            {displayEventDate && (
                                <span className="text-[9px] md:text-xs font-black uppercase tracking-widest text-brand-neonPink bg-brand-neonPink/5 px-2 py-0.5 rounded border border-brand-neonPink/10">
                                    For Event: {new Date(displayEventDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                                </span>
                            )}
                        </div>
                        
                        {/* ⚡ THE FIX: Properly wired dropdown with double-click protection */}
                        {isCancelledTask(task) ? <span role="status" className="type-button text-xs text-slate-400">Cancelled</span> : <select
                            value={task.status === 'PENDING' ? 'TODO' : task.status}
                            disabled={isProcessingTask === task.id || (taskCompletion.enabled&&oneOffJob(task)&&!taskCompletion.allowed(task.id))}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleUpdateTaskStatus(task, e.target.value);
                            }}
                            aria-label={`Status for ${task.title}`}
                            className={`min-h-11 w-full rounded-lg border px-3 py-2 text-center type-button text-xs transition-all appearance-none cursor-pointer outline-none sm:w-auto md:rounded-xl ${
                                task.status === 'COMPLETED' ? 'bg-green-500/20 text-green-500 border-green-500/50 hover:bg-green-500 hover:text-slate-950' :
                                task.status === 'IN_PROGRESS' ? 'bg-brand-neonBlue/20 text-brand-neonBlue border-brand-neonBlue/50 hover:bg-brand-neonBlue hover:text-slate-950' :
                                'bg-slate-800 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white'
                            } ${isProcessingTask === task.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <option value="TODO">Backlog</option>
                            <option value="IN_PROGRESS">In Production</option>
                            <option value="COMPLETED">Complete</option>
                        </select>}
                    </div>
                </div>
            </GlassCard>
        );
      };

      const initializeTask = () => {
          setNewTaskData({
              title: '',
              assignee_id: '',
              lead_id: '',
              project_id: selectedProjectIdFilter !== 'ALL' ? selectedProjectIdFilter : '',
              due_date: getLocalDateString(),
              recurrence: 'none',
              description: '',
              is_urgent: false
          });
          setIsAddingGlobalTask(true);
      };

      // 2. THE COLUMN RENDERER
      const renderColumns = (tasksList: any[], scope: TaskControlScope, completedArchive: any[]) => {
          const todo = tasksList.filter(t => t.status === 'TODO' || t.status === 'PENDING');
          const inProgress = tasksList.filter(t => t.status === 'IN_PROGRESS');
          const done = tasksList.filter(t => t.status === 'COMPLETED');
          const cancelled = tasksList.filter(isCancelledTask);
          const visibleColumnCount = scope === 'ALL' ? 3 : scope === 'ACTIVE' ? 2 : 1;

          return (
            <div className="space-y-6">
              <div className={`grid grid-cols-1 gap-6 md:gap-8 items-start ${visibleColumnCount === 3 ? 'lg:grid-cols-3' : visibleColumnCount === 2 ? 'lg:grid-cols-2' : ''}`}>
                  {scope !== 'COMPLETED' && (
                  <div className="space-y-4 md:space-y-6">
                      <div className="flex items-center justify-between px-2">
                          <h4 className="type-label text-slate-500 flex items-center gap-2">
                              <Circle size={14} className="text-slate-600"/> Backlog
                          </h4>
                          <span className="type-numeric text-[11px] text-slate-600 bg-white/5 px-2 py-0.5 rounded-full">{todo.length}</span>
                      </div>
                      <div className="space-y-3 md:space-y-4">
                          {todo.map(t => <TaskCard key={t.id} task={t} team={staffList} />)}
                          {todo.length === 0 && <div className="p-8 md:p-10 border border-dashed border-white/5 rounded-[24px] md:rounded-[32px] text-center text-slate-800 italic text-xs">Empty</div>}
                      </div>
                  </div>
                  )}

                  {scope !== 'COMPLETED' && (
                  <div className="space-y-4 md:space-y-6">
                      <div className="flex items-center justify-between px-2">
                          <h4 className="type-label text-brand-neonBlue flex items-center gap-2">
                              <Clock size={14} className="animate-pulse"/> In Production
                          </h4>
                          <span className="type-numeric text-[11px] text-brand-neonBlue bg-brand-neonBlue/10 px-2 py-0.5 rounded-full">{inProgress.length}</span>
                      </div>
                      <div className="space-y-3 md:space-y-4">
                          {inProgress.map(t => <TaskCard key={t.id} task={t} team={staffList} />)}
                          {inProgress.length === 0 && <div className="p-8 md:p-10 border border-dashed border-white/5 rounded-[24px] md:rounded-[32px] text-center text-slate-800 italic text-xs">Empty</div>}
                      </div>
                  </div>
                  )}

                  {scope !== 'ACTIVE' && (
                  <div className="space-y-4 md:space-y-6">
                      <div className="flex items-center justify-between px-2">
                          <h4 className="type-label text-green-500 flex items-center gap-2">
                              <CheckCircle size={14}/> Complete
                          </h4>
                          {completedArchive.length > 0 && (
                              <button onClick={() => handleArchiveCompleted(completedArchive)} className="type-button min-h-11 px-3 py-2 text-xs text-slate-500 transition-colors hover:text-rose-400 bg-white/5 rounded-md">
                                  Sweep Clean
                              </button>
                          )}
                      </div>
                      <div className="space-y-3 md:space-y-4 opacity-60">
                          {done.map(t => <TaskCard key={t.id} task={t} team={staffList} />)}
                      </div>
                  </div>
                  )}
              </div>
              {scope === 'ALL' && cancelled.length > 0 && <section aria-label="Cancelled task history" className="space-y-3">
                <h4 className="type-label text-slate-400">Cancelled — History ({cancelled.length})</h4>
                <p className="text-sm text-slate-400">Retained task history. Cancellation does not mean the physical work was completed.</p>
                {cancelled.map(task=><TaskCard key={task.id} task={task} team={staffList} />)}
              </section>}
            </div>
          );
      };

      // 3. THE MAGIC ROUTER (Grouped vs Flat)
      const groupTasks = (tasks: any[]) => tasks.reduce((acc, task) => {
          let groupName = task.lead?.project_ref || task.lead?.eventName || task.project?.title || 'General Operations';
          
          // ⚡ SMART FIX: Prevent Cross-Pollination for merged clients/shifts!
          // If it's an auto-task, extract the exact Shift/Event name from the task title (e.g. "Eastern Schools")
          if (task.task_type === 'AUTO_LOGISTICS' && task.title.includes(': ')) {
              groupName = task.title.split(': ')[1]; 
          }

          if (!acc[groupName]) acc[groupName] = [];
          acc[groupName].push(task);
          return acc;
      }, {} as Record<string, any[]>);

      const renderTaskCollection = (visibleTasks: any[], scope: TaskControlScope) => {
        if (isGrouped) {
          const grouped = groupTasks(visibleTasks);
          const completeArchiveGroups = groupTasks(displayTasks.filter(task => task.status === 'COMPLETED'));
          return (
                <div className="space-y-4 md:space-y-6">
                    {Object.entries(grouped).map(([groupName, groupTasks]: [string, any]) => {
                        const isExpanded = expandedGroups.includes(groupName);
                        
                        return (
                            <div key={groupName} className="bg-slate-900/40 border border-white/10 rounded-[20px] md:rounded-[32px] overflow-hidden transition-all duration-300 shadow-2xl">
                                <div 
                                    onClick={() => toggleGroup(groupName)}
                                    className="bg-slate-800/80 p-4 md:p-6 flex justify-between items-center cursor-pointer hover:bg-slate-700/80 transition-all border-b border-white/5 gap-3"
                                >
                                    <div className="flex items-center gap-2 md:gap-4 overflow-hidden">
                                        <div className="shrink-0">
                                            {isExpanded ? <ChevronDown size={20} className="md:w-6 md:h-6 text-brand-neonBlue" /> : <ChevronRight size={20} className="md:w-6 md:h-6 text-brand-neonBlue" />}
                                        </div>
                                        <h4 className="type-heading truncate text-base text-white md:text-2xl">{groupName}</h4>
                                    </div>
                                    <span className="bg-brand-neonBlue/10 text-brand-neonBlue text-[9px] md:text-[10px] uppercase tracking-widest font-black px-2 py-1 md:px-3 md:py-1.5 rounded-lg md:rounded-xl shrink-0">{groupTasks.length} Tasks</span>
                                </div>
                                {isExpanded && (
                                    <div className="p-4 md:p-8 bg-slate-950/50 backdrop-blur-sm animate-fade-in">
                                        {renderColumns(groupTasks, scope, completeArchiveGroups[groupName] || [])}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
          );
        }

        return renderColumns(
          visibleTasks,
          scope,
          displayTasks.filter(task => task.status === 'COMPLETED')
        );
      };

      return (
        <TaskControlWorkspace
          tasks={displayTasks}
          staff={staffList}
          isGrouped={isGrouped}
          taskFilter={taskFilter}
          assigneeFilter={taskAssigneeFilter}
          projectName={currentProjectContext?.title}
          onGroupedChange={setIsGrouped}
          onTaskFilterChange={setTaskFilter}
          onAssigneeFilterChange={setTaskAssigneeFilter}
          onClearProjectFilter={() => setSelectedProjectIdFilter('ALL')}
          onCreateTask={initializeTask}
        >
          {renderTaskCollection}
        </TaskControlWorkspace>
      );
  };
 
// ⚡ NEW: MEDIA VAULT ENGINE (With Portals & Strict Screen Locking)
const renderMediaVault = () => <MediaVault loadEvents={loadGroupedVault} marketing={groupedVault} onOpenJob={id => {
  const lead = crmItems.find(item => item.id === id);
  if (lead) handleViewInquiry(lead);
  else alert('Could not locate the original job file.');
}} />;

  const renderSettings = () => <NotificationSettings staff={staffList} />;
      const navItems = [
        { id: 'dashboard', label: 'Overview', icon: LayoutDashboard },
        { id: 'leads', label: 'Inquiries', icon: Mail },
        { id: 'pipeline', label: 'Pipeline', icon: Trello },
        { id: 'projects', label: 'Internal Projects', icon: FolderOpen },
        { id: 'media', label: 'Media Vault', icon: ImageIcon },
        { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
        { id: 'tasks', label: 'Task Control', icon: CheckCircle },
        { id: 'quotes', label: 'Quotes', icon: FileText },
        { id: 'database', label: 'Clients', icon: Database },
        { id: 'companies', label: 'Companies', icon: Building2 }, 
        { id: 'preferred-partners', label: 'Preferred Partners', icon: Handshake },
        { id: 'inventory', label: 'Inventory', icon: Package },
        { id: 'staff', label: 'Staff', icon: Users },
        { id: 'settings', label: 'Settings', icon: Settings },
      ];

      // ⚡ NEW: EDIT TASK MODAL
      const renderEditTaskModal = () => {
          if (!editingTask) return null;

          const handleSaveEdit = async (e: React.FormEvent) => {
              e.preventDefault();
              
              if(taskCompletion.enabled&&oneOffJob(taskEditSnapshot.current?.task)){
                  const original=taskEditSnapshot.current!;
                  if(original.revision===undefined){alert('Task version unavailable; reload first.');return;}
                  if(editingTask.recurrence!==original.task.recurrence){alert('Recurrence changes remain in the existing recurring-task pathway.');return;}
                  const patch:Record<string,unknown>={};
                  for(const key of ['title','description','assignee_id','lead_id','project_id','is_urgent'])if(editingTask[key]!==original.task[key])patch[key]=editingTask[key];
                  // An explicit calendar-date change retains the existing NZ time, seconds and milliseconds.
                  if(editingTask.due_date!==original.task.due_date){try{patch.due_date=preserveTaskTimeOnDateChange(original.task.due_date,editingTask.due_date);}catch(e:any){alert(e.message);return;}}
                  try{await taskCompletion.change(original.task,editingTask.status,patch,original.revision);setEditingTask(null);}
                  catch(e:any){alert(taskCompletion.save(editingTask.id).message||e.message);}
                  return;
              }
              // 1. Save all the new details to the database
              const { error } = await supabase
                  .from('tasks')
                  .update({
                      title: editingTask.title,
                      due_date: editingTask.due_date,
                      assignee_id: editingTask.assignee_id,
                      description: editingTask.description,
                      recurrence: editingTask.recurrence,
                      status: editingTask.status, // ⚡ NOW SAVES THE STATUS
                      lead_id: editingTask.lead_id || null, // ⚡ SAVES LEAD LINK
                      project_id: editingTask.project_id || null, // ⚡ SAVES PROJECT LINK
                      is_urgent: editingTask.is_urgent || false // ⚡ NEW: SAVES URGENT STATUS
                  })
                  .eq('id', editingTask.id);

              if (!error) {
                  // ⚡ 2. AUTO-CLONE ENGINE FOR MODAL SAVES
                  // If they changed it to COMPLETED right here in the modal, and it repeats, clone it!
                  const validRecurrences = ['daily', 'weekly', 'fortnightly', 'monthly', 'bimonthly', 'quarterly', 'yearly'];
                  if (editingTask.status === 'COMPLETED' && validRecurrences.includes(editingTask.recurrence)) {
                      const oldDate = new Date(editingTask.due_date || new Date());
                      let nextDate = new Date(oldDate);
                      
                      if (editingTask.recurrence === 'daily') nextDate.setDate(nextDate.getDate() + 1);
                      if (editingTask.recurrence === 'weekly') nextDate.setDate(nextDate.getDate() + 7);
                      if (editingTask.recurrence === 'fortnightly') nextDate.setDate(nextDate.getDate() + 14);
                      if (editingTask.recurrence === 'monthly') nextDate.setMonth(nextDate.getMonth() + 1);
                      if (editingTask.recurrence === 'bimonthly') nextDate.setMonth(nextDate.getMonth() + 2);
                      if (editingTask.recurrence === 'quarterly') nextDate.setMonth(nextDate.getMonth() + 3);
                      if (editingTask.recurrence === 'yearly') nextDate.setFullYear(nextDate.getFullYear() + 1);

                      const cloneTask = {
                          title: editingTask.title,
                          assignee_id: editingTask.assignee_id,
                          lead_id: editingTask.lead_id,
                          project_id: editingTask.project_id,
                          task_type: editingTask.task_type || 'MANUAL',
                          status: 'TODO',
                          due_date: nextDate.toISOString(),
                          recurrence: editingTask.recurrence
                      };
                      await supabase.from('tasks').insert([cloneTask]);
                  }

                  setEditingTask(null);
                  fetchGlobalTasks(); 
              } else {
                  alert("Error saving task: " + error.message);
              }
          };

          // ⚡ Determine Context Name for Header
          let contextLabel = 'Global Task (Unassigned)';
          let contextIcon = <CheckCircle size={14} className="text-slate-500" />;
          if (editingTask.project_id) {
              const proj = internalProjects.find(p => p.id === editingTask.project_id);
              contextLabel = proj ? `Project: ${proj.title}` : 'Internal Project';
              contextIcon = <FolderOpen size={14} className="text-purple-400" />;
          } else if (editingTask.lead_id) {
              const lead = crmItems.find(i => i.id === editingTask.lead_id);
              contextLabel = lead ? `Event: ${lead.eventName}` : 'Client Event';
              contextIcon = <Briefcase size={14} className="text-brand-neonBlue" />;
          }

          return (
              <TaskDetailsModalShell
                  tone="blue"
                  zIndexClassName="z-[120]"
                  onSubmit={handleSaveEdit}
                  header={(
                      <div className="flex items-start justify-between gap-4">
                          <div>
                              <h3 className="type-heading flex items-center gap-3 text-xl text-white md:text-2xl">
                                  <Edit3 className="text-brand-neonBlue" /> Edit Task
                              </h3>
                              {/* ⚡ THE NEW CONTEXT HEADER */}
                              <p className="type-label text-slate-400 mt-2 flex items-center gap-2">
                                  {contextIcon} {contextLabel}
                              </p>
                          </div>
                          <button type="button" aria-label="Close task details" onClick={() => setEditingTask(null)} className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-white/5 hover:text-white">
                              <X size={24} />
                          </button>
                      </div>
                  )}
                  footer={(
                      <div className="flex flex-col gap-3 sm:flex-row">
                          <Button type="button" variant="outline" className="min-h-11 w-full sm:flex-1" onClick={() => setEditingTask(null)}>
                              Cancel
                          </Button>
                          <button type="submit" className="min-h-11 w-full rounded-xl border-none bg-brand-neonPink px-6 py-3 font-black uppercase tracking-widest text-white shadow-neon-pink transition-transform hover:scale-[1.02] sm:flex-1">
                              Save Changes
                          </button>
                      </div>
                  )}
              >
                      <div className="space-y-4 md:space-y-6">
                          <div>
                              <label className="type-label text-slate-500 ml-1 mb-2 flex items-center h-4">Task Title</label>
                              <input 
                                  type="text" 
                                  required
                                  value={editingTask.title}
                                  onChange={e => setEditingTask({...editingTask, title: e.target.value})}
                                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white text-sm outline-none focus:border-brand-neonBlue transition-all"
                              />
                          </div>

                          {/* ⚡ 4-COLUMN GRID */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div>
                                  <label className="type-label text-slate-500 ml-1 mb-2 flex items-center h-4">Assign To</label>
                                  <select 
                                      value={editingTask.assignee_id || ''}
                                      onChange={e => setEditingTask({...editingTask, assignee_id: e.target.value})}
                                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white text-sm outline-none focus:border-brand-neonBlue transition-all appearance-none cursor-pointer"
                                  >
                                      <option value="">Unassigned</option>
                                      {staffList.map((member: any) => (
                                          <option key={member.id} value={member.id}>{member.full_name}</option>
                                      ))}
                                  </select>
                              </div>
                              <div>
                                  <label className="type-label text-slate-500 ml-1 mb-2 flex items-center h-4">Due Date</label>
                                  <input 
                                      type="date" 
                                      value={taskCompletion.enabled&&oneOffJob(taskEditSnapshot.current?.task)?taskDateNZ(editingTask.due_date):editingTask.due_date ? editingTask.due_date.split('T')[0] : ''}
                                      onChange={e => setEditingTask({...editingTask, due_date: e.target.value})}
                                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white text-sm outline-none focus:border-brand-neonBlue transition-all [color-scheme:dark]"
                                  />
                              </div>
                              <div>
                                  <label className="type-label text-slate-500 ml-1 mb-2 flex items-center h-4 gap-1">
                                      <Repeat size={10} className="text-brand-neonBlue" /> Repeat
                                  </label>
                                  <select 
                                      value={editingTask.recurrence || 'none'}
                                      onChange={e => setEditingTask({...editingTask, recurrence: e.target.value})}
                                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white text-sm outline-none focus:border-brand-neonBlue transition-all appearance-none cursor-pointer"
                                  >
                                      <option value="none">One-Off</option>
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="fortnightly">Two-Weekly</option>
                                      <option value="monthly">Monthly</option>
                                      <option value="bimonthly">Two-Monthly (GST)</option>
                                      <option value="quarterly">Quarterly</option>
                                      <option value="yearly">Yearly</option>
                                  </select>
                              </div>
                              
                              <div>
                                  <label className="type-label text-slate-500 ml-1 mb-2 flex items-center h-4 gap-1">
                                      <CheckCircle size={10} className="text-green-500" /> Status
                                  </label>
                                  <select 
                                      value={editingTask.status === 'PENDING' ? 'TODO' : (editingTask.status || 'TODO')}
                                      onChange={e => setEditingTask({...editingTask, status: e.target.value})}
                                      className={`w-full bg-slate-900/50 border rounded-xl px-4 h-[56px] text-white text-sm outline-none transition-all appearance-none cursor-pointer ${
                                          editingTask.status === 'COMPLETED' ? 'border-green-500/50 text-green-400' :
                                          editingTask.status === 'IN_PROGRESS' ? 'border-brand-neonBlue/50 text-brand-neonBlue' :
                                          'border-white/10 focus:border-brand-neonBlue'
                                      }`}
                                  >
                                      <option value="TODO">Backlog</option>
                                      <option value="IN_PROGRESS">In Production</option>
                                      <option value="COMPLETED">Complete</option>
                                      {isCancelledTask(editingTask) && <option value={editingTask.status}>Cancelled</option>}
                                  </select>
                              </div>
                          </div>

                          {/* ⚡ NEW: LINKING DROPDOWNS */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                              <div>
                                  <div className="flex justify-between items-end mb-2">
                                      <label className="type-label text-brand-neonBlue ml-1 flex items-center h-4">
                                          Link to Client Job
                                      </label>
                                      {editingTask.lead_id && (
                                          <button 
                                              type="button"
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  const linkedLead = crmItems.find((i: any) => i.id === editingTask.lead_id);
                                                  if (linkedLead) {
                                                      const data = linkedLead as any;
                                                      const displayNotes = `PROJECT: ${data.eventName || 'N/A'}\nVENUE: ${data.eventVenue || 'TBD'}\nDATE: ${data.eventDate || 'TBD'}\n🛠️ PRODUCTS: ${data.interestedProducts || 'None'}\n\nREQS: ${data.notes || ''}`;
                                                      handleViewInquiry({ ...data, notes: displayNotes });
                                                  }
                                              }}
                                              className="type-button text-xs text-slate-950 bg-brand-neonBlue hover:bg-white px-2 py-1.5 rounded-md transition-colors flex items-center gap-1 shadow-neon-blue-sm"
                                          >
                                              <Briefcase size={10} /> Open Job File
                                          </button>
                                      )}
                                  </div>
                                  <select 
                                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-brand-neonBlue appearance-none cursor-pointer"
                                      value={editingTask.lead_id || ''}
                                      onChange={e => setEditingTask({...editingTask, lead_id: e.target.value, project_id: e.target.value ? null : editingTask.project_id})}
                                  >
                                      <option value="">-- No Client Job --</option>
                                      {crmItems.filter((i: any) => i.type === 'SALES').map((i: any) => (
                                          <option key={i.id} value={i.id}>{i.eventName} ({i.firstName})</option>
                                      ))}
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest ml-1 mb-2 flex items-center h-4">
                                      OR Internal Project
                                  </label>
                                  <select 
                                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-purple-400 appearance-none cursor-pointer"
                                      value={editingTask.project_id || ''}
                                      onChange={e => setEditingTask({...editingTask, project_id: e.target.value, lead_id: e.target.value ? null : editingTask.lead_id})}
                                  >
                                      <option value="">-- No Internal Project --</option>
                                      {internalProjects.map((p: any) => (
                                          <option key={p.id} value={p.id}>{p.title}</option>
                                      ))}
                                  </select>
                              </div>
                          </div>

                          {/* TASK DESCRIPTION & CHECKLIST EDITOR */}
                          <div className="pt-2">
                              <label className="type-label text-slate-500 ml-1 mb-2 flex items-center gap-2">
                                  <FileText size={12} className="text-brand-neonBlue" /> Instructions & Packing List
                              </label>
                              <textarea 
                                  className="w-full bg-slate-900/50 border border-white/10 rounded-xl p-4 text-white text-sm outline-none focus:border-brand-neonBlue transition-all min-h-[200px] leading-relaxed resize-y"
                                  placeholder="Type instructions here..."
                                  value={editingTask.description || ''}
                                  onChange={e => setEditingTask({...editingTask, description: e.target.value})}
                              />
                              <div className="bg-brand-neonBlue/10 border border-brand-neonBlue/20 rounded-lg p-3 mt-3">
                                  <p className="text-[9px] text-brand-neonBlue font-black uppercase tracking-widest leading-relaxed">
                                      💡 Pro Tip: Start a new line with a bullet point (<span className="text-white bg-slate-800 px-1 py-0.5 rounded"> • </span>) and it will automatically turn into an interactive checkbox on the Crew Portal!
                                  </p>
                              </div>
                          </div>

                          {/* ⚡ NEW: URGENT CHECKBOX */}
                            <div className="pt-4 mt-4 flex items-center justify-between">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <input 
                                        type="checkbox" 
                                        checked={newTaskData?.is_urgent || editingTask?.is_urgent} 
                                        onChange={e => {
                                            if (editingTask) setEditingTask({...editingTask, is_urgent: e.target.checked});
                                            else setNewTaskData({...newTaskData, is_urgent: e.target.checked});
                                        }} 
                                        className="w-5 h-5 rounded border-white/20 bg-slate-900/50 cursor-pointer"
                                    />
                                    <span className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors flex items-center gap-2">
                                        <AlertCircle size={16} className="text-red-500" /> 
                                        CRITICAL: Mark as Urgent (Triggers Screen Lock)
                                    </span>
                                </label>
                            </div>

                      </div>
              </TaskDetailsModalShell>
          );
      };

    
  return (
    // ⚡ 1. REMOVED pt-20 SO IT DOESN'T PUSH THE APP DOWN 80px!
    <div className="min-h-screen bg-slate-950 text-slate-200">
      
      {/* ⚡ 2. NEW SLEEK FLOATING MOBILE MENU PILL */}
      <div className="md:hidden fixed top-4 left-4 z-40">
         <button onClick={() => setIsMobileMenuOpen(true)} className="type-button flex min-h-11 items-center gap-2 rounded-full border border-white/10 bg-slate-900/90 px-5 py-3 text-xs text-white shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-2xl transition-all hover:text-brand-neonBlue">
           <Menu size={16} className="text-brand-neonBlue" /> Admin Menu
         </button>
      </div>

      {isMobileMenuOpen && (
         <div className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* ⚡ 3. SIDEBAR MOVED FROM top-20 TO top-0 */}
      <aside className={`fixed top-0 bottom-0 left-0 w-80 bg-slate-900/95 md:bg-slate-900/60 backdrop-blur-3xl border-r border-white/10 z-50 flex flex-col transition-transform duration-300 ease-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="p-6 pt-10 md:p-10 md:pt-12 flex-1 overflow-y-auto custom-scrollbar">
          
          <div className="flex justify-between items-center md:hidden mb-8 pb-6 border-b border-white/10">
             <span className="type-heading text-xl text-brand-neonBlue">Navigation</span>
             <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-white/5 rounded-xl text-slate-400 hover:text-white transition-colors"><X size={20}/></button>
          </div>

          <nav className="space-y-2">
            {navItems.map(tab => (
              <button key={tab.id} onClick={() => requestEditorNavigation(() => { setActiveTab(tab.id); setViewState('DASHBOARD'); setIsMobileMenuOpen(false); })} className={`type-button flex min-h-11 w-full items-center justify-between rounded-2xl px-6 py-4 text-xs transition-all ${activeTab === tab.id ? 'bg-brand-neonBlue text-slate-950 shadow-neon' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}>
                <div className="flex items-center gap-4">
                    <tab.icon size={18} /> {tab.label}
                </div>
                {tab.id === 'tasks' && globalTasks.filter(t => t.status === 'PENDING' || t.status === 'TODO').filter(t => new Date(t.due_date) <= new Date()).length > 0 && (
                    <span className={`text-[10px] font-black px-2 py-1 rounded-md ${activeTab === 'tasks' ? 'bg-slate-900 text-brand-neonBlue' : 'bg-brand-neonBlue text-slate-900'} shadow-sm`}>
                        {globalTasks.filter(t => (t.status === 'PENDING' || t.status === 'TODO') && new Date(t.due_date) <= new Date()).length} Due
                    </span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6 md:p-10 border-t border-white/5">
            <Button variant="outline" className="w-full text-rose-500 hover:bg-rose-500/10 border-rose-500/20" onClick={() => requestEditorNavigation(() => { void logout().catch(() => alert('Sign out could not be completed. Please retry.')); })}>Log Out</Button>
        </div>
      </aside>
    
          <main className="md:ml-80 p-4 pt-24 md:p-10 lg:p-20 transition-all duration-300">
            <div className="max-w-[96%] mx-auto">
              {viewState !== 'CREATE_QUOTE' && verifiedAuthId && <QuoteDraftRecovery
                key={`${activeTab}:${viewState}`}
                scope={{ adminId: verifiedAuthId, context: `${window.location.origin}|${process.env.REACT_APP_SUPABASE_URL || 'unconfigured'}` }}
                quotes={quotes}
                onOpen={quote => requestEditorNavigation(() => { setActiveQuote(quote); setActiveTab('quotes'); setViewState('CREATE_QUOTE'); })}
              />}
              
              {/* ⚡ NEW GLOBAL TASK WARNING BANNER ON DASHBOARD ⚡ */}
              {activeTab === 'dashboard' && (
                <div className="animate-fade-in">
                  <h1 className="type-page-title mb-8 text-white md:mb-12">Overview</h1>
                  
                  <DashboardOverview 
                    currentUser={currentUser} 
                    tasks={globalTasks}
                    crmItems={crmItems.filter(item => item.type === 'SALES')} 
                    quotes={quotes} 
                    staffList={staffList} 
                    companies={companies}
                    allShifts={allShifts} 
                    onViewInquiry={handleViewInquiry} 
                    onQuoteAction={handleQuoteAction} 
                    onChangeTab={setActiveTab} 
                    isLoading={isLoading} 
                    onAddInquiry={() => setIsAddingInquiry(true)} 
                    onAddTask={() => setIsAddingGlobalTask(true)} 
                    onEditTask={(task) => {  
                        openTaskEditor(task); 
                    }}
                    onCompleteTask={(task) => handleUpdateTaskStatus(task, 'COMPLETED')}
                    onSetLeadFilter={setLeadFilter} 
                  />
                </div>
              )}
              
              {activeTab === 'calendar' && renderCalendar()}
              {activeTab === 'tasks' && renderTasks()}
              {activeTab === 'leads' && renderLeadsList()}
              {activeTab === 'pipeline' && renderPipeline()}
              {activeTab === 'projects' && renderProjects()}
              {activeTab === 'media' && renderMediaVault()}   
              {activeTab === 'quotes' && (
                <div className="h-full">
                  {viewState === 'DASHBOARD' && (
                    <div className="animate-fade-in">
                      <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                        <div>
                          <h1 className="type-page-title text-white">Proposals Hub</h1>
                          <p className="type-label text-slate-500 mt-4 flex items-center gap-2">
                            <FileText size={12} className="text-brand-neonBlue" /> Draft & Sent Quote Inventory
                          </p>
                        </div>
                        <Button 
                          className="bg-brand-neonBlue text-slate-950 font-bold shadow-neon px-10 py-4 rounded-2xl transition-transform hover:scale-105" 
                          onClick={() => { setActiveQuote(null); setViewState('CREATE_QUOTE'); }}
                        >
                          <Plus size={20} className="mr-3"/> Initialize Quote
                        </Button>
                      </div>
                      {renderQuotesList()}
                    </div>
                  )}
                  {viewState === 'CREATE_QUOTE' && (
                    <QuoteBuilder 
                      key={activeQuote?.id || 'new'}
                      initialQuote={activeQuote} 
                      serverQuote={activeQuote?.id ? quotes.find(q => q.id === activeQuote.id) : null}
                      isPersistedQuote={!!activeQuote?.id && quotes.some(q => q.id === activeQuote.id)}
                      checkLatestQuote={async quoteId => {
                        const { data, error } = await supabase.from('quotes').select('*').eq('id', quoteId).maybeSingle();
                        if (error) throw new Error('Saved Quote check failed');
                        return data ? formatQuoteFromDB(data) : null;
                      }}
                      draftScope={verifiedAuthId ? { adminId: verifiedAuthId, context: `${window.location.origin}|${process.env.REACT_APP_SUPABASE_URL || 'unconfigured'}` } : undefined}
                      linkedLead={activeQuote?.linkedCrmItemId ? crmItems.find(i => i.id === activeQuote.linkedCrmItemId) : null} 
                      onSave={handleSaveQuote} 
                      onCancel={() => setViewState('DASHBOARD')} 
                      availableProducts={products} 
                      companies={companies} 
                      onUpdateStatus={handleUpdateQuoteStatus} 
                    />
                  )}
                  {viewState === 'VIEW_QUOTE' && activeQuote && (
                    (() => {
                      const linkedLead = crmItems.find(i => i.id === activeQuote.linkedCrmItemId || i.id === (activeQuote as any).lead_id);
                     // ⚡ NEW: MANUAL INQUIRY MODAL WITH SERVICE PILLS
    
                      return (
                        <QuotePreview 
                          quote={{
                            ...activeQuote,
                            client: {
                              ...activeQuote.client,
                              company: activeQuote.client?.company || activeQuote.companyName || linkedLead?.companyName || 'Private Account',
                              firstName: activeQuote.client?.firstName || linkedLead?.firstName || '',
                              lastName: activeQuote.client?.lastName || linkedLead?.lastName || '',
                              email: activeQuote.client?.email || linkedLead?.clientEmail || '',
                              phone: activeQuote.client?.phone || linkedLead?.clientPhone || ''
                            }
                          }} 
                          onStatusChange={handleStatusChange} 
                          onUpdate={handlePreviewUpdate} 
                          onBack={() => setViewState('DASHBOARD')} 
                          onEdit={handleEditQuote} 
                          availableProducts={products}
                          onDuplicateQuote={handleDuplicateQuote} 
                        />
                      );
                    })()
                  )}
                </div>
              )}
              {activeTab === 'database' && renderDatabase()}
              {activeTab === 'companies' && <Companies companies={companies} crmItems={crmItems} quotes={quotes} onRefresh={fetchCompanies} onOpenQuote={(q: Quote) => { setActiveQuote(q); setViewState('VIEW_QUOTE'); setActiveTab('quotes'); }} onViewInquiry={handleViewInquiry} onQuoteAction={handleQuoteAction} />}
              {activeTab === 'preferred-partners' && <PreferredPartnerAdmin companies={companies} />}
              {activeTab === 'inventory' && renderProducts()}
              {activeTab === 'staff' && renderStaff()}
              {activeTab === 'settings' && renderSettings()}
            </div>
            </main>

            {isDetailsModalOpen && selectedInquiry && (
              <InquiryDetailsModal 
                equipmentFocusShiftId={allShifts.some(s=>s.id===equipmentFocusShiftId && s.lead_id===selectedInquiry.id) ? equipmentFocusShiftId : undefined}
                isOpen={isDetailsModalOpen} 
                onClose={() => { setIsDetailsModalOpen(false); setEquipmentFocusShiftId(undefined); }} 
                data={{
                  ...selectedInquiry,
                  notes: selectedInquiry.notes 
                }} 
                allQuotes={quotes} 
                completionService={taskCompletion}
                managementTasks={globalTasks}
                managementTasksLoading={managementTasksLoading}
                managementTasksError={managementTasksError}
                onOpenManagementTask={() => { setActiveTab('tasks'); setIsDetailsModalOpen(false); }}
                allCompanies={companies} 
                crmItems={crmItems} 
                onLinkToCompany={async (leadId: string, companyId: string) => {
                    await handleLinkLeadToCompany(leadId, companyId);
                    await fetchLeads(); 
                 }}
                onViewQuote={(q: any) => { setActiveQuote(q); setViewState('VIEW_QUOTE'); setActiveTab('quotes'); setIsDetailsModalOpen(false); }} 
                onQuote={handleQuoteAction} 
                onDuplicateQuote={handleDuplicateQuote}
                onApproveQuote={handleSaveQuote} 
              />
            )}

<ProductModal isOpen={isProductEditorOpen} onClose={() => setIsProductEditorOpen(false)} product={editingProduct} onSaveSuccess={handleProductSaved} />

{isAddingClient && (
  <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-3xl">
    <GlassCard className="w-full max-w-2xl p-12 rounded-[48px] border-brand-neonPink/30 shadow-neon-pink animate-fade-in-up">
      <div className="flex justify-between items-center mb-10">
        <h3 className="type-heading flex items-center gap-4 text-3xl text-white">
          <UserPlus className="text-brand-neonPink" /> New Identity
        </h3>
        <button onClick={() => { setIsAddingClient(false); setNewClient({ firstName: '', lastName: '', clientEmail: '', clientPhone: '', companyName: '', companyId: '' }); }} className="text-slate-500 hover:text-white transition-all"><X size={32}/></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="space-y-2">
          <label className="type-label ml-2 text-slate-500">First Name</label>
          <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink" value={newClient.firstName} onChange={e => setNewClient({...newClient, firstName: e.target.value})} placeholder="e.g. Tom" />
        </div>
        <div className="space-y-2">
          <label className="type-label ml-2 text-slate-500">Last Name</label>
          <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink" value={newClient.lastName} onChange={e => setNewClient({...newClient, lastName: e.target.value})} placeholder="e.g. Elliott" />
        </div>
        <div className="space-y-2">
          <label className="type-label ml-2 text-slate-500">Email Identity</label>
          <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink" value={newClient.clientEmail} onChange={e => setNewClient({...newClient, clientEmail: e.target.value})} placeholder="name@company.com" />
        </div>
        <div className="space-y-2">
          <label className="type-label ml-2 text-slate-500">Mobile Reference</label>
          <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink" value={newClient.clientPhone} onChange={e => setNewClient({...newClient, clientPhone: e.target.value})} placeholder="021 000 0000" />
        </div>

        <div className="md:col-span-2 space-y-2">
          <label className="type-label ml-2 text-slate-500">Corporate Association</label>
          <select 
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink cursor-pointer appearance-none"
              value={(newClient as any).companyId}
              onChange={(e) => setNewClient({...newClient, companyId: e.target.value} as any)}
          >
              <option value="">Private Individual / None</option>
              <optgroup label="Existing Database">
                  {companies.map(comp => (
                      <option key={comp.id} value={comp.id}>{comp.name}</option>
                  ))}
              </optgroup>
              <option value="NEW" className="text-brand-neonPink font-bold">+ Register New Company...</option>
          </select>
        </div>

        {(newClient as any).companyId === 'NEW' && (
            <div className="md:col-span-2 space-y-2 animate-fade-in">
              <label className="type-label text-brand-neonPink ml-2">New Company Name</label>
              <input className="w-full bg-slate-950 border border-brand-neonPink/30 rounded-2xl p-5 text-white outline-none focus:border-brand-neonPink" value={newClient.companyName} onChange={e => setNewClient({...newClient, companyName: e.target.value})} placeholder="Enter legal company name..." autoFocus />
            </div>
        )}
      </div>

      <div className="flex gap-4">
        <Button variant="outline" className="flex-1 py-5 rounded-2xl" onClick={() => setIsAddingClient(false)}>Discard</Button>
        <Button className="flex-1 py-5 bg-brand-neonPink text-white shadow-neon-pink rounded-2xl font-bold" onClick={handleSaveManualClient}>Confirm Record</Button>
      </div>
    </GlassCard>
  </div>
)}

{taskCompletion.enabled&&<div className="fixed bottom-4 left-4 z-[100000] max-w-sm max-h-48 overflow-auto bg-slate-950 text-white rounded-xl"><JobTaskFeedback service={taskCompletion} tasks={globalTasks}/></div>}
{/* Urgent Admin reminder: intentionally non-blocking so Settings remains reachable. */}
          {activeAdminAlarm && createPortal(
              <div role="alert" aria-label="Critical task reminder" className="fixed bottom-4 right-4 z-[99999] w-[calc(100%-2rem)] max-w-md pointer-events-none">
                  <div className="pointer-events-auto bg-red-950/95 backdrop-blur-2xl border-2 border-red-500 shadow-[0_0_60px_rgba(239,68,68,0.35)] rounded-3xl p-6 text-center animate-fade-in-up">
                      <div className="w-16 h-16 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner border border-red-500/50 animate-pulse">
                          <AlertCircle size={32} />
                      </div>
                      <h2 className="text-3xl md:text-4xl font-display font-black text-white tracking-tighter mb-4">
                          CRITICAL ACTION DUE
                      </h2>
                      <p className="text-xl text-red-400 font-bold mb-2">
                          {activeAdminAlarm.title}
                      </p>
                      <p className="text-slate-400 text-sm mb-6 leading-relaxed font-medium">
                          {activeAdminAlarm.description || "This task is scheduled for right now. Please action it or snooze it when appropriate; CRM navigation remains available."}
                      </p>

                      <div className="flex flex-col gap-4">
                          <Button 
                              onClick={async () => {
                                  if(taskCompletion.enabled&&oneOffJob(activeAdminAlarm)){
                                      if(await handleUpdateTaskStatus(activeAdminAlarm,'COMPLETED'))setActiveAdminAlarm(null);
                                      return;
                                  }
                                  setGlobalTasks(prev => prev.map(t => t.id === activeAdminAlarm.id ? { ...t, status: 'COMPLETED' } : t));
                                  await supabase.from('tasks').update({ status: 'COMPLETED' }).eq('id', activeAdminAlarm.id);
                                  setActiveAdminAlarm(null);
                              }}
                              className="w-full py-5 bg-green-500 text-slate-950 rounded-2xl font-black uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(34,197,94,0.4)] hover:scale-105 border-none"
                          >
                              <CheckCircle size={18} className="mr-2"/> I Have Completed This
                          </Button>
                          
                          <Button 
                              onClick={async () => {
                                  const snoozedTime = new Date();
                                  snoozedTime.setMinutes(snoozedTime.getMinutes() + 15);
                                  setGlobalTasks(prev => prev.map(t => t.id === activeAdminAlarm.id ? { ...t, due_date: snoozedTime.toISOString() } : t));
                                  await supabase.from('tasks').update({ due_date: snoozedTime.toISOString() }).eq('id', activeAdminAlarm.id);
                                  setActiveAdminAlarm(null);
                              }}
                              className="w-full py-5 bg-slate-900 hover:bg-slate-800 text-slate-300 border border-white/10 rounded-2xl font-bold uppercase tracking-widest text-xs"
                          >
                              <Clock size={16} className="mr-2 text-yellow-500"/> Snooze for 15 Minutes
                          </Button>
                      </div>
                  </div>
              </div>,
              document.body 
          )}
{/* ⚡ GLOBAL TASK INITIALIZER MODAL */}
{isAddingGlobalTask && (
            <TaskDetailsModalShell
                header={(
                    <div className="flex items-center justify-between gap-4">
                        <h3 className="type-heading flex items-center gap-3 text-xl text-white md:text-2xl">
                            <CheckCircle className="text-brand-neonPink" /> New Task Definition
                        </h3>
                        <button type="button" aria-label="Close create task" onClick={() => setIsAddingGlobalTask(false)} className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 transition-all hover:bg-white/5 hover:text-white"><X size={24}/></button>
                    </div>
                )}
                footer={(
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Button type="button" variant="outline" className="min-h-11 w-full sm:flex-1" onClick={() => setIsAddingGlobalTask(false)}>Discard</Button>
                        <Button type="button" className="min-h-11 w-full bg-brand-neonPink text-[10px] font-bold uppercase tracking-widest text-white shadow-neon-pink sm:flex-1" onClick={handleSaveGlobalTask}>Deploy Task</Button>
                    </div>
                )}
            >
                    <div className="space-y-4 md:space-y-6">
                        {/* TITLE */}
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-2 flex items-center h-4">Task Title</label>
                            <input 
                                className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white focus:border-brand-neonPink outline-none transition-all" 
                                placeholder="What needs to be done?" 
                                value={newTaskData.title}
                                onChange={e => setNewTaskData({...newTaskData, title: e.target.value})}
                                autoFocus
                            />
                        </div>

                        {/* ASSIGNEE, DUE DATE, REPEAT */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-2 flex items-center h-4">Assign To</label>
                                <select 
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-brand-neonPink appearance-none cursor-pointer transition-all"
                                    value={newTaskData.assignee_id || ''}
                                    onChange={e => setNewTaskData({...newTaskData, assignee_id: e.target.value})}
                                >
                                    <option value="">-- Unassigned --</option>
                                    {staffList.map((s: any) => <option key={s.id} value={s.id}>{s.full_name || s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-2 flex items-center h-4">Due Date</label>
                                <input 
                                    type="date" 
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-brand-neonPink [color-scheme:dark] transition-all" 
                                    value={newTaskData.due_date ? newTaskData.due_date.split('T')[0] : ''}
                                    onChange={e => setNewTaskData({...newTaskData, due_date: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 mb-2 flex items-center h-4 gap-1">
                                    <Repeat size={10} className="text-brand-neonPink" /> Repeat
                                </label>
                                <select 
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-brand-neonPink appearance-none cursor-pointer transition-all"
                                    value={newTaskData.recurrence || 'none'}
                                    onChange={e => setNewTaskData({...newTaskData, recurrence: e.target.value})}
                                >
                                    <option value="none">One-Off</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="fortnightly">Two-Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="bimonthly">Two-Monthly (GST)</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </div>
                        </div>

                        {/* LINKS */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="flex justify-between items-end mb-2">
                                    <label className="type-label text-brand-neonBlue ml-1 flex items-center h-4">
                                        Link to Client Job
                                    </label>
                                    {newTaskData.lead_id && (
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const linkedLead = crmItems.find((i: any) => i.id === newTaskData.lead_id);
                                                if (linkedLead) {
                                                    const data = linkedLead as any;
                                                    const displayNotes = `PROJECT: ${data.eventName || 'N/A'}\nVENUE: ${data.eventVenue || 'TBD'}\nDATE: ${data.eventDate || 'TBD'}\n🛠️ PRODUCTS: ${data.interestedProducts || 'None'}\n\nREQS: ${data.notes || ''}`;
                                                    handleViewInquiry({ ...data, notes: displayNotes });
                                                }
                                            }}
                                            className="type-button text-xs text-slate-950 bg-brand-neonBlue hover:bg-white px-2 py-1.5 rounded-md transition-colors flex items-center gap-1 shadow-neon-blue-sm"
                                        >
                                            <Briefcase size={10} /> Open Job File
                                        </button>
                                    )}
                                </div>
                                <select 
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-brand-neonBlue appearance-none cursor-pointer transition-all"
                                    value={newTaskData.lead_id || ''}
                                    onChange={e => setNewTaskData({...newTaskData, lead_id: e.target.value, project_id: e.target.value ? '' : newTaskData.project_id})}
                                >
                                    <option value="">-- No Client Job --</option>
                                    {crmItems.filter((i: any) => i.type === 'SALES').map((i: any) => (
                                        <option key={i.id} value={i.id}>{i.eventName} ({i.firstName})</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest ml-1 mb-2 flex items-center h-4">
                                    OR Internal Project
                                </label>
                                <select 
                                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 h-[56px] text-white outline-none focus:border-purple-400 appearance-none cursor-pointer transition-all"
                                    value={newTaskData.project_id || ''}
                                    onChange={e => setNewTaskData({...newTaskData, project_id: e.target.value, lead_id: e.target.value ? '' : newTaskData.lead_id})}
                                >
                                    <option value="">-- No Internal Project --</option>
                                    {internalProjects.map((p: any) => (
                                        <option key={p.id} value={p.id}>{p.title}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* DESCRIPTION */}
                        <div className="pt-2">
                            <label className="type-label text-slate-500 ml-1 mb-2 flex items-center gap-2">
                                <FileText size={12} className="text-brand-neonPink" /> Instructions & Packing List
                            </label>
                            <textarea 
                                className="w-full bg-slate-900/50 border border-white/10 rounded-xl p-4 text-white text-sm outline-none focus:border-brand-neonPink transition-all min-h-[160px] leading-relaxed resize-y"
                                placeholder="Type instructions here..."
                                value={newTaskData.description || ''}
                                onChange={e => setNewTaskData({...newTaskData, description: e.target.value})}
                            />
                            <div className="bg-brand-neonPink/10 border border-brand-neonPink/20 rounded-lg p-3 mt-3">
                                <p className="text-[9px] text-brand-neonPink font-black uppercase tracking-widest leading-relaxed">
                                    💡 Pro Tip: Start a new line with a bullet point (<span className="text-white bg-slate-800 px-1 py-0.5 rounded"> • </span>) and it will automatically turn into an interactive checkbox on the Crew Portal!
                                </p>
                            </div>
                        </div>

                        {/* ⚡ NEW: URGENT CHECKBOX */}
                            <div className="pt-4 mt-4 flex items-center justify-between">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <input 
                                        type="checkbox" 
                                        checked={newTaskData?.is_urgent || editingTask?.is_urgent} 
                                        onChange={e => {
                                            if (editingTask) setEditingTask({...editingTask, is_urgent: e.target.checked});
                                            else setNewTaskData({...newTaskData, is_urgent: e.target.checked});
                                        }} 
                                        className="w-5 h-5 rounded border-white/20 bg-slate-900/50 cursor-pointer"
                                    />
                                    <span className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors flex items-center gap-2">
                                        <AlertCircle size={16} className="text-red-500" /> 
                                        CRITICAL: Mark as Urgent (Triggers Screen Lock)
                                    </span>
                                </label>
                            </div>

                    </div>
            </TaskDetailsModalShell>
        )}

          {/* ⚡ EDIT TASK MODAL */}
          {renderEditTaskModal()}

          {renderManualInquiryModal()} 
        </div>
      );
    };

    export default LicenseeLogin;
